import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AlertCircle, Check, X, Play, Pause, Square, Download, Upload, Eye, EyeOff, Plus, Trash2, Settings, Activity, FileText, Clock, Search, Wifi, WifiOff, Ban, LogOut, User, Lock, Moon, Sun, Menu, ChevronDown, ChevronRight, Key, RefreshCw, Copy, Shield, Columns, Image, DollarSign, Languages, Bell, KeyRound, HelpCircle, RotateCcw, Globe } from 'lucide-react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '';

const currencySymbol = (code) => {
  const symbols = {
    'USD': '$', 'EUR': '€', 'GBP': '£', 'BRL': 'R$',
    'AED': 'د.إ', 'SAR': '﷼', 'AUD': 'A$', 'CAD': 'C$',
    'CHF': 'CHF', 'CNY': '¥', 'CZK': 'Kč', 'DKK': 'kr',
    'EGP': 'E£', 'HKD': 'HK$', 'HUF': 'Ft', 'IDR': 'Rp',
    'INR': '₹', 'JPY': '¥', 'KRW': '₩', 'KWD': 'د.ك',
    'MXN': 'MX$', 'MYR': 'RM', 'NGN': '₦', 'NOK': 'kr',
    'NZD': 'NZ$', 'OMR': 'ر.ع', 'PHP': '₱', 'PLN': 'zł',
    'QAR': 'ر.ق', 'RON': 'lei', 'RUB': '₽', 'SEK': 'kr',
    'SGD': 'S$', 'THB': '฿', 'TRY': '₺', 'TWD': 'NT$',
    'ZAR': 'R', 'BHD': 'BD', 'KES': 'KSh', 'GHS': 'GH₵',
    'TZS': 'TSh', 'UGX': 'USh', 'PKR': '₨', 'VND': '₫',
    'COP': 'COL$', 'CLP': 'CLP$', 'ARS': 'AR$', 'PEN': 'S/',
    'KZT': '₸', 'IQD': 'ع.د', 'CRC': '₡', 'UYU': '$U',
    'JOD': 'JD', 'LKR': 'Rs', 'BGN': 'лв',
  };
  return symbols[code] || code || '€';
};

// `curated ?? vendor`, the same choice the backend's publishCurated() makes.
// `!= null`, never truthiness: an empty curated list is a real value.
const curatedOr = (curated, vendor) => (curated != null && curated !== '' ? curated : (vendor || null));

// Transform product data from backend format (the raw `products` row). At
// module scope so every writer of a product — the initial load, the inline
// editors, the Edit window, the bulk actions — builds the SAME shape.
const toProduct = (p) => ({
    id: p.id,
    sku: p.sku,
    erpSku: p.erp_sku || null,
    vendorSku: p.vendor_sku,
    ean: p.ean,
    name: p.product_name,
    internalDescription: p.internal_description,
    type: p.product_type,
    faceValue: p.face_value_minor,
    currencyCode: p.currency_code,
    currency: p.currency_numeric,
    discountPercent: parseFloat(p.discount_percent) || 0,
    active: p.active,
    apiEnabled: p.api_enabled || false,
    isVariableAmount: p.is_variable_amount || false,
    minAmountMinor: p.min_amount_minor,
    maxAmountMinor: p.max_amount_minor,
    vendor: p.vendor || 'epay',
    bambooProductId: p.bamboo_product_id,
    costPriceMinor: p.cost_price_minor,
    // #58 — Bamboo only. What the vendor quoted, in the currency it BILLS in;
    // costPriceMinor is that converted into currencyCode at vendorCostFxRate.
    vendorCostMinor: p.vendor_cost_minor,
    vendorCostCurrency: p.vendor_cost_currency || null,
    vendorCostFxRate: p.vendor_cost_fx_rate != null ? Number(p.vendor_cost_fx_rate) : null,
    vendorProductGroup: p.vendor_product_group,
    wockBulkPrices: p.wock_bulk_prices,
    stockQuantity: p.stock_quantity,
    // The five descriptive fields below, and brandName/category, are what Key Gen
    // PUBLISHES: our curated value where there is one, else the vendor's (F26).
    // `vendorValues` and `curated` keep the two layers apart for the editors.
    brandName: curatedOr(p.curated_brand, typeof p.brand_name === 'string' ? p.brand_name.trim() : p.brand_name),
    category: curatedOr(p.curated_category, p.category),
    // Vendor-supplied metadata. WOCK fills platform/language/languages/regions;
    // region comes from WOCK (free-text label) OR Ubiqfy (ISO-3 country code) —
    // mixed formats in one column, deliberately. Everything is nullable.
    platform: curatedOr(p.curated_platform, p.platform),
    region: curatedOr(p.curated_region, p.region),
    language: curatedOr(p.curated_language, p.language),
    languages: curatedOr(p.curated_languages, p.languages),
    regions: curatedOr(p.curated_regions, p.regions),
    vendorValues: {
      name: p.product_name || null,
      brand: (typeof p.brand_name === 'string' ? p.brand_name.trim() : p.brand_name) || null,
      category: p.category || null,
      platform: p.platform || null,
      region: p.region || null,
      language: p.language || null,
      languages: p.languages || null,
      regions: p.regions || null,
    },
    curated: {
      name: p.internal_description || null,
      brand: p.curated_brand || null,
      category: p.curated_category || null,
      platform: p.curated_platform || null,
      region: p.curated_region || null,
      language: p.curated_language || null,
      // NULL = no curated value; [] IS one (countries: worldwide). Never `||`.
      languages: p.curated_languages ?? null,
      regions: p.curated_regions ?? null,
    },
    enrichmentMeta: p.enrichment_meta || null,
    // The fields whose AI proposal still waits on the Enrichment tab (sparse from the
    // API; a single-row refresh carries none, and a full reload restores it).
    waiting: Array.isArray(p.waiting) ? p.waiting : [],
    // F32: the picture the product is published with (resolved by the server —
    // a person's, the brand's, the supplier's or the game database's) and the
    // brand library's entry for its brand. A single-row refresh carries both.
    image: p.image || null,
    brandImage: p.brandImage || null,
    createdAt: p.created_at,
    updatedAt: p.updated_at
  });

// ISO 3166-1 alpha-2, generated from the tz database's iso3166.tab. Names come
// from the browser (Intl.DisplayNames), so only the codes are kept here.
const ISO_COUNTRY_CODES = 'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'.split(' ');

// Dark Mode Hook
const useDarkMode = () => {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.theme === 'dark' || 
        (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    }
  }, [isDark]);

  return [isDark, setIsDark];
};

// Dark Mode Toggle Button
const DarkModeToggle = ({ isDark, setIsDark }) => (
  <button
    onClick={() => setIsDark(!isDark)}
    className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
  >
    {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
  </button>
);

// Configure axios to use auth token
const setupAxiosAuth = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Login Component
const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('credentials');
  const [sessionToken, setSessionToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      if (response.data.otpRequired) {
        setSessionToken(response.data.sessionToken);
        setMaskedEmail(response.data.email);
        setStep('otp');
        setOtpCode('');
        setResendCooldown(60);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-otp`, { sessionToken, otpCode });
      const { token, user } = response.data;
      localStorage.setItem('authToken', token);
      localStorage.setItem('authUser', JSON.stringify(user));
      setupAxiosAuth(token);
      onLogin(user, token);
    } catch (err) {
      const reason = err.response?.data?.reason;
      if (reason === 'expired_or_invalid' || reason === 'max_attempts') {
        setError(err.response?.data?.error || 'Code expired. Please log in again.');
        resetToCredentials();
      } else {
        const remaining = err.response?.data?.attemptsRemaining;
        setError(err.response?.data?.error + (remaining != null ? ` (${remaining} attempts remaining)` : ''));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/resend-otp`, { sessionToken });
      setSessionToken(response.data.sessionToken);
      setMaskedEmail(response.data.email);
      setOtpCode('');
      setResendCooldown(60);
    } catch (err) {
      const reason = err.response?.data?.reason;
      if (reason === 'expired_or_invalid') {
        setError('Session expired. Please log in again.');
        resetToCredentials();
      } else {
        setError(err.response?.data?.error || 'Failed to resend code');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetToCredentials = () => {
    setStep('credentials');
    setSessionToken('');
    setMaskedEmail('');
    setOtpCode('');
    setResendCooldown(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-8">
        {step === 'credentials' ? (
          <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#ffd71f] rounded-full mb-4">
                <Lock className="w-8 h-8 text-[#080300]" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Key Gen</h1>
              <p className="text-gray-500 dark:text-gray-400 mt-2">Sign in to your account</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleCredentialsSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full mb-4">
                <Shield className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Verify Your Identity</h1>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                We sent a verification code to<br />
                <span className="font-medium text-gray-700 dark:text-gray-300">{maskedEmail}</span>
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleOTPSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  autoFocus
                  className="w-full px-4 py-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent text-center text-2xl tracking-[0.5em] font-mono placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="000000"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || otpCode.length !== 6}
                className="w-full py-3 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </button>
            </form>

            <div className="mt-4 text-center space-y-2">
              <button
                onClick={handleResend}
                disabled={resendCooldown > 0 || isLoading}
                className="text-sm text-amber-600 dark:text-amber-400 hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
              >
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
              </button>
              <br />
              <button
                onClick={resetToCredentials}
                className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
              >
                Back to login
              </button>
            </div>
          </>
        )}

        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-6">
          Protected internal application
        </p>
      </div>
    </div>
  );
};

const App = () => {
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [view, setView] = useState('dashboard');
  const [queues, setQueues] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionsPagination, setTransactionsPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [products, setProducts] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processorStatus, setProcessorStatus] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const socketRef = useRef(null);
  const [isDark, setIsDark] = useDarkMode();

  // Check for existing auth on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('authUser');
    
    if (token && savedUser) {
      setupAxiosAuth(token);
      setAuthToken(token);
      setUser(JSON.parse(savedUser));
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleLogin = (userData, token) => {
    setUser(userData);
    setAuthToken(token);
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setupAxiosAuth(null);
    setUser(null);
    setAuthToken(null);
    setQueues([]);
    setTransactions([]);
    setProducts([]);
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
  }, []);

  // Auto-logout on expired/invalid JWT
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          const url = error.config?.url || '';
          if (!url.includes('/api/auth/login') && !url.includes('/api/auth/verify-otp') && !url.includes('/api/auth/resend-otp')) {
            handleLogout();
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [handleLogout]);

  const transformProduct = useCallback(toProduct, []);

  // Transform queue data from backend format
  const transformQueue = useCallback((q) => ({
    id: q.id,
    poNumber: q.po_number,
    piNumber: q.pi_number,
    environment: q.environment,
    status: q.status,
    notes: q.notes,
    createdAt: q.created_at,
    items: q.items ? q.items.map(item => ({
      id: item.id,
      productId: item.productId || item.product_id,
      sku: item.sku || item.product?.sku,
      ratePerMinute: item.ratePerMinute || item.rate_per_minute,
      targetQuantity: item.targetQuantity || item.target_quantity,
      generatedCount: item.generatedCount || item.generated_count || 0,
      successCount: item.successCount || item.success_count || 0,
      failedCount: item.failedCount || item.failed_count || 0,
      vendor: item.vendor,
      wockAutoDeliver: item.wockAutoDeliver
    })) : [],
    wockOrders: (q.wock_orders || []).map(wo => ({
      id: wo.id,
      wockOrderId: wo.wock_order_id,
      queueItemId: wo.queue_item_id,
      quantity: wo.quantity,
      status: wo.status,
      autoDeliver: wo.auto_deliver,
      keysReceived: wo.keys_received,
      keysFailed: wo.keys_failed,
      errorMessage: wo.error_message,
      createdAt: wo.created_at,
      completedAt: wo.completed_at
    }))
  }), []);

  // Transform transaction data from backend format
  const transformTransaction = useCallback((tx) => ({
    id: tx.id,
    queueId: tx.queue_id || tx.queueId,
    queueItemId: tx.queue_item_id || tx.queueItemId,
    sku: tx.sku,
    vendorSku: tx.vendor_sku,
    vendor: tx.vendor,
    ean: tx.ean,
    txid: tx.txid,
    hostTxid: tx.host_txid || tx.hostTxid,
    pin: tx.pin,
    pinMimeType: tx.pin_mime_type || tx.pinMimeType || null,
    serial: tx.serial,
    validTo: tx.valid_to || tx.validTo,
    status: tx.status,
    resultCode: tx.result_code || tx.resultCode,
    resultText: tx.result_text || tx.resultText,
    timestamp: tx.timestamp || new Date(tx.created_at).toLocaleString(),
    amount: tx.actual_amount_minor || tx.actualAmountMinor || tx.amount,
    actualAmountMinor: tx.actual_amount_minor || tx.actualAmountMinor,
    isVariableAmount: tx.is_variable_amount || tx.isVariableAmount || false,
    currency: tx.currency === 978 ? 'EUR' : tx.currency,
    currencyCode: tx.currency_code || tx.currencyCode || (tx.currency === 840 ? 'USD' : tx.currency === 986 ? 'BRL' : tx.currency === 826 ? 'GBP' : 'EUR'),
    creatorInitials: tx.creator_initials || tx.creatorInitials || '-',
    piNumber: tx.pi_number || tx.piNumber || null
  }), []);

  // Initial data load via HTTP (fallback and initial load). One request for every queue
  // WITH its items (`?withItems=1`): the old one-request-per-queue loop was 169 requests,
  // run twice per page load, and spent the API's rate budget on its own (2026-10-09).
  // A second load within 10 s (the socket's "initial data" after the mount's load) is skipped.
  const lastLoadRef = useRef(0);
  const loadDataViaHTTP = useCallback(async (showLoading = false) => {
    const now = Date.now();
    if (now - lastLoadRef.current < 10000) return;
    lastLoadRef.current = now;
    if (showLoading) setIsLoading(true);
    
    try {
      // Load queues, items included
      const queuesResponse = await axios.get(`${API_URL}/api/queues?withItems=1`);
      setQueues(queuesResponse.data.map(transformQueue));

      // Load transactions (paginated)
      const txResponse = await axios.get(`${API_URL}/api/transactions?page=1&limit=50`);
      setTransactions(txResponse.data.data.map(transformTransaction));
      setTransactionsPagination(txResponse.data.pagination);

      // Load products
      try {
        const productsResponse = await axios.get(`${API_URL}/api/products?includeInactive=true`);
        setProducts(productsResponse.data.map(transformProduct));
      } catch (err) {
        console.error('Failed to load products:', err);
      }

      // Load processor status
      try {
        const statusResponse = await axios.get(`${API_URL}/api/processor/status`);
        setProcessorStatus(statusResponse.data);
      } catch (err) {
        console.error('Failed to load processor status:', err);
      }

    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [transformQueue, transformTransaction, transformProduct]);

  // Fetch transactions with server-side pagination and filtering
  const fetchTransactions = useCallback(async (page = 1, filters = {}) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: (filters.limit || 50).toString()
      });

      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status.toUpperCase());
      }
      if (filters.sku && filters.sku !== 'all') {
        params.append('sku', filters.sku);
      }
      if (filters.vendor && filters.vendor !== 'all') {
        params.append('vendor', filters.vendor);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.poNumber) {
        params.append('poNumber', filters.poNumber);
      }
      if (filters.piNumber) {
        params.append('piNumber', filters.piNumber);
      }
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo);
      }

      const response = await axios.get(`${API_URL}/api/transactions?${params}`);
      setTransactions(response.data.data.map(transformTransaction));
      setTransactionsPagination(response.data.pagination);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      throw error;
    }
  }, [transformTransaction]);

  // Setup Socket.IO connection (only when authenticated)
  useEffect(() => {
    // Don't connect if not authenticated
    if (!user) return;

    // Create socket connection
    const socket = io(API_URL, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });
    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      console.log('🔌 Connected to WebSocket');
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('🔌 Disconnected from WebSocket');
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      setIsConnected(false);
    });

    // Initial data from server
    socket.on('initial:data', (data) => {
      console.log('📦 Received initial data');
      if (data.queues) {
        // Need to load full queue details
        loadDataViaHTTP(false);
      }
      if (data.transactions && data.transactionsPagination) {
        setTransactions(data.transactions.map(transformTransaction));
        setTransactionsPagination(data.transactionsPagination);
      }
      if (data.processorStatus) {
        setProcessorStatus(data.processorStatus);
      }
      setIsLoading(false);
    });

    // Real-time queue status changes
    socket.on('queue:statusChanged', (data) => {
      console.log('📢 Queue status changed:', data);
      setQueues(prev => prev.map(q => 
        q.id === data.queueId 
          ? { ...q, status: data.status }
          : q
      ));
    });

    // Real-time queue progress updates
    socket.on('queue:progress', (data) => {
      console.log('📊 Queue progress:', data);
      setQueues(prev => prev.map(q => {
        if (q.id === data.queueId) {
          return {
            ...q,
            items: q.items.map(item => 
              item.id === data.itemId
                ? { 
                    ...item, 
                    successCount: data.successCount,
                    failedCount: data.failedCount
                  }
                : item
            )
          };
        }
        return q;
      }));
    });

    // WOCK order ready for manual delivery — refresh queue to get wock_orders
    socket.on('wock:orderReady', (data) => {
      console.log('📋 WOCK order ready:', data);
      // Find which queue contains this queue item and refresh it from server
      setQueues(prev => {
        const targetQueue = prev.find(q => q.items.some(i => i.id === data.queueItemId));
        if (targetQueue) {
          axios.get(`${API_URL}/api/queues/${targetQueue.id}`).then(res => {
            setQueues(p => p.map(pq => pq.id === targetQueue.id ? transformQueue(res.data) : pq));
          }).catch(err => console.error('Failed to refresh queue after wock:orderReady:', err));
        }
        return prev;
      });
    });

    // Real-time transaction created
    socket.on('transaction:created', (data) => {
      console.log('💳 Transaction created:', data);
      const newTx = transformTransaction(data.transaction);
      // Prepend new transaction and keep array bounded
      // The actual page size is managed by server-side pagination on next fetch
      setTransactions(prev => [newTx, ...prev].slice(0, Math.max(prev.length, 50)));
      // Increment the total count
      setTransactionsPagination(prev => ({
        ...prev,
        total: prev.total + 1,
        totalPages: Math.ceil((prev.total + 1) / prev.limit)
      }));
    });

    // Real-time products update. A single-product event (`created` / `updated` /
    // `deactivated` / `deleted`) carries the whole row, so it is patched in place.
    // Re-downloading the catalogue here (28k rows, ~49 MB, ~3 s) on EVERY inline
    // edit froze the grid for the length of the download and the repaint — F26 S8,
    // found by the owner mid-review. Only a vendor sync (`vendor` + `count`) and
    // the bulk actions, which name no rows, still reload everything.
    socket.on('products:updated', async (data) => {
      console.log('📦 Products updated', data);
      const row = data && data.product;
      if (row && row.id != null) {
        if (data.action === 'deleted') {
          setProducts(prev => prev.filter(p => p.id !== row.id));
        } else {
          const fresh = transformProduct(row);
          setProducts(prev => prev.some(p => p.id === fresh.id)
            ? prev.map(p => p.id === fresh.id ? fresh : p)
            : [fresh, ...prev]);
        }
        return;
      }
      try {
        const productsResponse = await axios.get(`${API_URL}/api/products?includeInactive=true`);
        setProducts(productsResponse.data.map(transformProduct));
      } catch (err) {
        console.error('Failed to reload products:', err);
      }
    });

    // Initial HTTP load as backup
    loadDataViaHTTP(true);

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, [user, loadDataViaHTTP, transformTransaction, transformProduct]);

  const getDashboardMetrics = () => {
    const runningQueues = queues.filter(q => q.status === 'RUNNING').length;
    // Use server-side counts from queue items instead of paginated transactions
    let totalSuccess = 0;
    let totalFailed = 0;
    queues.forEach(q => {
      q.items.forEach(item => {
        totalSuccess += item.successCount || 0;
        totalFailed += item.failedCount || 0;
      });
    });
    const totalTransactions = totalSuccess + totalFailed;
    const successRate = totalTransactions > 0
      ? ((totalSuccess / totalTransactions) * 100).toFixed(1)
      : 0;

    return { runningQueues, totalTransactions, successRate, failedTransactions: totalFailed };
  };

  const metrics = getDashboardMetrics();

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#ffd71f] dark:border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  const navItems = ['dashboard', 'queues', 'transactions', 'products', 'customers', 'config'];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button 
              onClick={() => setView('dashboard')}
              className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity"
            >
              <KeyRound className="w-7 h-7 sm:w-8 sm:h-8 text-[#ffd71f]" />
              <div className="text-left">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">Key Gen</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Code Generation System</p>
              </div>
            </button>

            {/* Desktop Navigation — from `lg`, not `md`: at 768px the six items
                plus the controls were 63px wider than the screen (measured
                2026-10-05), so a tablet gets the menu button instead. */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-4">
              {/* WebSocket Status */}
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                isConnected 
                  ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' 
                  : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
              }`}>
                {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {isConnected ? 'Live' : 'Offline'}
              </div>
              
              <nav className="flex gap-1">
                {navItems.map(v => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-3 xl:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      view === v 
                        ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300' 
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </nav>

              {/* Dark mode toggle */}
              <DarkModeToggle isDark={isDark} setIsDark={setIsDark} />

              {/* User menu */}
              <div className="flex items-center gap-2 pl-2 border-l border-gray-200 dark:border-gray-600">
                <div className="hidden xl:block text-right">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{user.firstName || user.email}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{user.role}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                  title="Sign out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center gap-2 lg:hidden">
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                isConnected 
                  ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' 
                  : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
              }`}>
                {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              </div>
              <DarkModeToggle isDark={isDark} setIsDark={setIsDark} />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Mobile Navigation Dropdown */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <nav className="grid grid-cols-2 gap-2">
                {navItems.map(v => (
                  <button
                    key={v}
                    onClick={() => { setView(v); setMobileMenuOpen(false); }}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors text-left ${
                      view === v 
                        ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300' 
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </nav>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{user.firstName || user.email}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{user.role}</div>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg font-medium"
                >
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        {view === 'dashboard' && <Dashboard metrics={metrics} queues={queues} transactions={transactions} products={products} setView={setView} setSelectedQueue={setSelectedQueue} />}
        {view === 'queues' && <Queues queues={queues} setQueues={setQueues} setView={setView} setSelectedQueue={setSelectedQueue} transactions={transactions} setTransactions={setTransactions} products={products} />}
        {view === 'products' && <Products products={products} setProducts={setProducts} />}
        {view === 'transactions' && <Transactions transactions={transactions} setTransactions={setTransactions} queues={queues} pagination={transactionsPagination} fetchTransactions={fetchTransactions} products={products} />}
        {view === 'customers' && <Customers />}
        {view === 'config' && <Configuration currentUser={user} />}
      </main>
    </div>
  );
};

const Dashboard = ({ metrics, queues, transactions, products, setView, setSelectedQueue }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [timeframe, setTimeframe] = useState('7d');
  const [reportTransactions, setReportTransactions] = useState([]);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [bambooBalances, setBambooBalances] = useState([]);
  const [ubiqfyBalances, setUbiqfyBalances] = useState([]);
  const [wockPartnerInfo, setWockPartnerInfo] = useState(null);

  // Fetch Bamboo balance
  useEffect(() => {
    const loadBambooBalance = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/bamboo/accounts`);
        const accounts = response.data.accounts || [];
        setBambooBalances(accounts.map(a => ({
          currency: a.currency,
          display: `${a.currency} ${a.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        })));
      } catch {}
    };
    loadBambooBalance();
  }, []);

  // Fetch Ubiqfy balances
  useEffect(() => {
    const loadUbiqfyBalance = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/ubiqfy/accounts`);
        const balances = response.data.balances || [];
        setUbiqfyBalances(balances.filter(b => b.balance !== null).map(b => ({
          currency: b.currency,
          display: `${b.currency} ${typeof b.balance === 'number' ? b.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : b.balance}`
        })));
      } catch {}
    };
    loadUbiqfyBalance();
  }, []);

  // Fetch WOCK partner info (balance/credit)
  useEffect(() => {
    const loadWockBalance = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/wock/accounts`);
        setWockPartnerInfo(response.data.partnerInfo || null);
      } catch {}
    };
    loadWockBalance();
  }, []);

  const timeframeOptions = [
    { value: '6h', label: '6 Hours', hours: 6 },
    { value: '12h', label: '12 Hours', hours: 12 },
    { value: '24h', label: '24 Hours', hours: 24 },
    { value: '5d', label: '5 Days', hours: 24 * 5 },
    { value: '7d', label: '7 Days', hours: 24 * 7 },
    { value: '15d', label: '15 Days', hours: 24 * 15 },
    { value: '30d', label: '30 Days', hours: 24 * 30 },
    { value: '90d', label: '90 Days', hours: 24 * 90 },
    { value: '180d', label: '180 Days', hours: 24 * 180 },
    { value: '365d', label: '365 Days', hours: 24 * 365 },
  ];

  // Get current timeframe hours
  const currentTimeframeHours = timeframeOptions.find(t => t.value === timeframe)?.hours || 24 * 7;
  const cutoffDate = new Date(Date.now() - currentTimeframeHours * 60 * 60 * 1000);

  // Fetch all transactions for the selected timeframe (for reports/charts)
  useEffect(() => {
    if (activeTab !== 'reports') return;
    const loadReportData = async () => {
      setIsLoadingReport(true);
      try {
        const response = await axios.get(`${API_URL}/api/transactions?paginate=false&status=SUCCESS&dateFrom=${cutoffDate.toISOString()}`);
        const txData = Array.isArray(response.data) ? response.data : response.data.data || [];
        setReportTransactions(txData.map(tx => ({
          sku: tx.sku,
          status: tx.status,
          amount: tx.actual_amount_minor || tx.amount,
          timestamp: tx.created_at,
        })));
      } catch (error) {
        console.error('Failed to load report data:', error);
      } finally {
        setIsLoadingReport(false);
      }
    };
    loadReportData();
  }, [activeTab, timeframe]);

  // Use fetched report data for charts
  const filteredTransactions = reportTransactions;

  // Create product lookup map
  const productMap = {};
  products.forEach(p => {
    if (p.sku) productMap[p.sku] = p;
    if (p.vendorSku) productMap[p.vendorSku] = p;
  });

  // ===== BAR CHART DATA: Codes per SKU =====
  const codesPerSku = {};
  filteredTransactions.forEach(tx => {
    const sku = tx.sku || 'Unknown';
    codesPerSku[sku] = (codesPerSku[sku] || 0) + 1;
  });
  
  const barChartData = Object.entries(codesPerSku)
    .map(([sku, count]) => ({ sku: sku.length > 15 ? sku.substring(0, 15) + '...' : sku, fullSku: sku, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // ===== PIE CHART DATA: Potential Revenue per SKU =====
  const revenuePerSku = {};
  filteredTransactions.forEach(tx => {
    const sku = tx.sku || 'Unknown';
    const product = productMap[sku];
    if (product) {
      const faceValue = (product.face_value_minor || product.faceValue || 0) / 100;
      const discount = (product.discount_percent || product.discountPercent || 0) / 100;
      const margin = faceValue * discount;
      revenuePerSku[sku] = (revenuePerSku[sku] || 0) + margin;
    }
  });

  const pieChartData = Object.entries(revenuePerSku)
    .map(([sku, revenue]) => ({ 
      name: sku.length > 12 ? sku.substring(0, 12) + '...' : sku, 
      fullName: sku,
      value: parseFloat(revenue.toFixed(2))
    }))
    .filter(item => item.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  const totalPotentialRevenue = Object.values(revenuePerSku).reduce((sum, val) => sum + val, 0);

  // ===== TOP 5 SELLERS TABLE =====
  const topSellers = Object.entries(codesPerSku)
    .map(([sku, count]) => {
      const product = productMap[sku];
      return {
        sku,
        count,
        productName: product?.product_name || product?.productName || sku,
        faceValue: product ? (product.face_value_minor || product.faceValue || 0) / 100 : 0
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // ===== AVERAGE MARGIN CALCULATION =====
  let totalDenomination = 0;
  let totalCost = 0;
  
  filteredTransactions.forEach(tx => {
    const product = productMap[tx.sku];
    if (product) {
      const faceValue = (product.face_value_minor || product.faceValue || 0) / 100;
      const discount = (product.discount_percent || product.discountPercent || 0) / 100;
      const cost = faceValue * (1 - discount);
      
      totalDenomination += faceValue;
      totalCost += cost;
    }
  });

  const averageMargin = totalDenomination > 0 
    ? ((totalDenomination - totalCost) / totalDenomination * 100).toFixed(2)
    : 0;

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

  const recentTransactions = transactions.slice(0, 10);

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('summary')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'summary'
                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Reports
          </button>
        </nav>
      </div>

      {/* SUMMARY TAB */}
      {activeTab === 'summary' && (
        <div className="space-y-4">
          {/* Metrics Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
            <MetricCard
              title="Running Queues"
              value={metrics.runningQueues}
              icon={Play}
              color="#ffd71f"
            />
            <MetricCard
              title="Total Transactions"
              value={metrics.totalTransactions}
              icon={Activity}
              color="purple"
            />
            <MetricCard
              title="Success Rate"
              value={`${metrics.successRate}%`}
              icon={Check}
              color="green"
            />
            <MetricCard
              title="Failed"
              value={metrics.failedTransactions}
              icon={AlertCircle}
              color="red"
            />
            {bambooBalances.map((b, i) => (
              <MetricCard
                key={`bamboo-${i}`}
                title={`Bamboo ${b.currency}`}
                value={b.display}
                icon={Shield}
                color="green"
              />
            ))}
            {ubiqfyBalances.map((b, i) => (
              <MetricCard
                key={`ubiqfy-${i}`}
                title={`Ubiqfy ${b.currency}`}
                value={b.display}
                icon={Shield}
                color="orange"
              />
            ))}
            {wockPartnerInfo && (
              <MetricCard
                title="WOCK Credit"
                value={`€${(wockPartnerInfo.available || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={Shield}
                color="pink"
              />
            )}
          </div>

          {/* Active Queues */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-3 sm:px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Active Queues</h2>
              <button 
                onClick={() => setView('queues')}
                className="text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium"
              >
                View All →
              </button>
            </div>
            <div className="p-3 sm:p-4">
              {queues.filter(q => q.status === 'RUNNING').length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">No active queues</p>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {queues.filter(q => q.status === 'RUNNING').map(queue => (
                    <div key={queue.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white truncate">{queue.poNumber}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{queue.items.length} SKUs • {queue.environment}</div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedQueue(queue);
                          setView('queues');
                        }}
                        className="px-3 py-2 text-sm bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/70 w-full sm:w-auto"
                      >
                        View Details
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-3 sm:px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Recent Transactions</h2>
              <button 
                onClick={() => setView('transactions')}
                className="text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium"
              >
                View All →
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Time</th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">SKU</th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden sm:table-cell">TXID</th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {recentTransactions.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        No transactions yet
                      </td>
                    </tr>
                  ) : (
                    recentTransactions.map(tx => (
                      <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm text-gray-900 dark:text-gray-200">{tx.timestamp}</td>
                        <td className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm text-gray-900 dark:text-gray-200 max-w-[120px] truncate">{tx.sku}</td>
                        <td className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm font-mono text-gray-600 dark:text-gray-400 hidden sm:table-cell">{tx.txid}</td>
                        <td className="px-3 sm:px-4 py-1.5 sm:py-2">
                          <StatusBadge status={tx.status} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* REPORTS TAB */}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          {/* Timeframe Selector */}
          <div className="flex items-center justify-end gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Timeframe:</span>
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              {timeframeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {isLoadingReport ? (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading report data...
            </div>
          ) : (
          <>
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar Chart - Codes per SKU */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">Codes Generated per SKU</h3>
              {barChartData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No transactions in selected timeframe
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barChartData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                      <XAxis 
                        dataKey="sku" 
                        tick={{ fill: '#9CA3AF', fontSize: 10 }} 
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                      />
                      <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'var(--tooltip-bg, #ffffff)', 
                          border: '1px solid var(--tooltip-border, #e5e7eb)', 
                          borderRadius: '8px',
                          color: 'var(--tooltip-text, #111827)'
                        }}
                        labelStyle={{ color: 'var(--tooltip-text, #111827)', fontWeight: 600 }}
                        itemStyle={{ color: '#3B82F6' }}
                        formatter={(value, name, props) => [value, 'Codes']}
                        labelFormatter={(label, payload) => payload[0]?.payload?.fullSku || label}
                        wrapperClassName="chart-tooltip"
                      />
                      <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Pie Chart - Potential Revenue */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Potential Revenue by SKU</h3>
                <span className="text-lg font-bold text-green-600 dark:text-green-400">€{totalPotentialRevenue.toFixed(2)}</span>
              </div>
              {pieChartData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No revenue data available
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'var(--tooltip-bg, #ffffff)', 
                          border: '1px solid var(--tooltip-border, #e5e7eb)', 
                          borderRadius: '8px',
                          color: 'var(--tooltip-text, #111827)'
                        }}
                        labelStyle={{ color: 'var(--tooltip-text, #111827)', fontWeight: 600 }}
                        itemStyle={{ color: '#10B981' }}
                        formatter={(value) => [`€${value.toFixed(2)}`, 'Revenue']}
                        labelFormatter={(label, payload) => payload[0]?.payload?.fullName || label}
                        wrapperClassName="chart-tooltip"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Row - Top Sellers & Average Margin */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top 5 Sellers */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">Top 5 Sellers</h3>
              {topSellers.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">No sales data available</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Rank</th>
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">SKU</th>
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden sm:table-cell">Product</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Codes</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden sm:table-cell">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSellers.map((seller, index) => (
                        <tr key={seller.sku} className="border-b border-gray-100 dark:border-gray-700/50">
                          <td className="py-3 px-2">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                              index === 0 ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400' :
                              index === 1 ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300' :
                              index === 2 ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}>
                              {index + 1}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-sm font-mono text-gray-900 dark:text-white truncate max-w-[120px]" title={seller.sku}>
                            {seller.sku}
                          </td>
                          <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400 hidden sm:table-cell truncate max-w-[150px]" title={seller.productName}>
                            {seller.productName}
                          </td>
                          <td className="py-3 px-2 text-sm font-semibold text-gray-900 dark:text-white text-right">
                            {seller.count}
                          </td>
                          <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400 text-right hidden sm:table-cell">
                            €{(seller.count * seller.faceValue).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Average Margin Box */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 dark:from-green-600 dark:to-emerald-700 rounded-lg border border-green-400 dark:border-green-700 p-6 flex flex-col justify-center items-center text-white">
              <div className="text-sm font-medium opacity-90 mb-2">Average Margin</div>
              <div className="text-4xl sm:text-5xl font-bold mb-2">{averageMargin}%</div>
              <div className="text-xs opacity-75 text-center">
                Based on {filteredTransactions.length} transactions
              </div>
              <div className="mt-4 text-sm opacity-90 w-full">
                <div className="flex justify-between gap-4">
                  <span>Revenue:</span>
                  <span className="font-semibold">€{totalDenomination.toFixed(2)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>Cost:</span>
                  <span className="font-semibold">€{totalCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between gap-4 border-t border-white/30 mt-1 pt-1">
                  <span>Profit:</span>
                  <span className="font-semibold">€{(totalDenomination - totalCost).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      )}
    </div>
  );
};

const Queues = ({ queues, setQueues, setView, setSelectedQueue, transactions, setTransactions, products }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 50;

  // Filter queues
  const filteredQueues = queues.filter(q => {
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    const matchesSearch = searchTerm === '' || 
      q.poNumber.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Sort by newest first
  const sortedQueues = [...filteredQueues].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Paginate
  const totalPages = Math.ceil(sortedQueues.length / itemsPerPage);
  const paginatedQueues = sortedQueues.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filters change
  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Queue Management</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg text-sm sm:text-base w-full sm:w-auto justify-center"
        >
          <Plus className="w-5 h-5" />
          <span>Create Queue</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search PO Number..."
              value={searchTerm}
              onChange={(e) => handleFilterChange(setSearchTerm)(e.target.value)}
              className={`w-full pl-10 pr-3 py-2 border ${searchTerm ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => handleFilterChange(setStatusFilter)(e.target.value)}
            className={`px-3 py-2 border ${statusFilter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
          >
            <option value="all">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="RUNNING">Running</option>
            <option value="PAUSED">Paused</option>
            <option value="STOPPED">Stopped</option>
            <option value="COMPLETED">Completed</option>
          </select>
          {(searchTerm || statusFilter !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setStatusFilter('all'); setCurrentPage(1); }}
              className="flex items-center gap-1 px-2 py-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Clear all filters"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </div>

      {queues.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8 text-center">
          <Activity className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No queues yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">Create your first code generation queue to get started</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg"
          >
            Create Queue
          </button>
        </div>
      ) : filteredQueues.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">No queues match your filters</p>
        </div>
      ) : (
        <>
          <div className="grid gap-2">
            {paginatedQueues.map(queue => (
              <QueueCard 
                key={queue.id} 
                queue={queue} 
                setQueues={setQueues}
                setSelectedQueue={setSelectedQueue}
                transactions={transactions}
                setTransactions={setTransactions}
                products={products}
              />
            ))}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={sortedQueues.length}
            itemsPerPage={itemsPerPage}
          />
        </>
      )}

      {showCreateModal && (
        <CreateQueueModal 
          onClose={() => setShowCreateModal(false)}
          onSubmit={(newQueue) => {
            setQueues([newQueue, ...queues]);
            setShowCreateModal(false);
          }}
          products={products}
        />
      )}
    </div>
  );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, onItemsPerPageChange, pageSizeOptions }) => {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const [jumpToPage, setJumpToPage] = useState('');

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const handleJumpToPage = (e) => {
    e.preventDefault();
    const pageNum = parseInt(jumpToPage, 10);
    if (pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum);
      setJumpToPage('');
    }
  };

  if (totalPages <= 1 && !onItemsPerPageChange) return null;

  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-gray-600 dark:text-gray-400">
            Showing {startItem}-{endItem} of {totalItems.toLocaleString()}
          </span>
          {onItemsPerPageChange && pageSizeOptions && (
            <div className="flex items-center gap-2">
              <label className="text-gray-500 dark:text-gray-400">Per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => onItemsPerPageChange(parseInt(e.target.value, 10))}
                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
              title="First page"
            >
              &laquo;
            </button>
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
            >
              Prev
            </button>
            {getPageNumbers().map((page, idx) => (
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 dark:text-gray-500">...</span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`px-3 py-1 rounded ${
                    currentPage === page
                      ? 'bg-[#ffd71f] text-[#080300]'
                      : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {page}
                </button>
              )
            ))}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
            >
              Next
            </button>
            <button
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
              title="Last page"
            >
              &raquo;
            </button>
          </div>
        )}
      </div>
      {totalPages > 5 && (
        <div className="flex items-center justify-center sm:justify-end gap-2">
          <form onSubmit={handleJumpToPage} className="flex items-center gap-2">
            <label className="text-gray-500 dark:text-gray-400">Go to page:</label>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={jumpToPage}
              onChange={(e) => setJumpToPage(e.target.value)}
              placeholder={`1-${totalPages}`}
              className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <button
              type="submit"
              disabled={!jumpToPage || parseInt(jumpToPage, 10) < 1 || parseInt(jumpToPage, 10) > totalPages}
              className="px-3 py-1 rounded bg-[#ffd71f] hover:bg-amber-400 text-[#080300] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Go
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

const QueueCard = ({ queue, setQueues, setSelectedQueue, transactions, setTransactions, products = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [wockActionLoading, setWockActionLoading] = useState(null); // 'deliver-{id}' or 'cancel-{id}'

  const refreshQueue = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/queues/${queue.id}`);
      const q = res.data;
      setQueues(prev => prev.map(pq => pq.id === queue.id ? {
        ...pq,
        status: q.status,
        items: q.items ? q.items.map(item => ({
          id: item.id,
          productId: item.productId,
          sku: item.sku || item.product?.sku,
          ratePerMinute: item.ratePerMinute,
          targetQuantity: item.targetQuantity,
          generatedCount: item.generatedCount || 0,
          successCount: item.successCount || 0,
          failedCount: item.failedCount || 0,
          vendor: item.vendor,
          wockAutoDeliver: item.wockAutoDeliver
        })) : pq.items,
        wockOrders: (q.wock_orders || []).map(wo => ({
          id: wo.id,
          wockOrderId: wo.wock_order_id,
          queueItemId: wo.queue_item_id,
          quantity: wo.quantity,
          status: wo.status,
          autoDeliver: wo.auto_deliver,
          keysReceived: wo.keys_received,
          keysFailed: wo.keys_failed,
          errorMessage: wo.error_message,
          createdAt: wo.created_at,
          completedAt: wo.completed_at
        }))
      } : pq));
    } catch (err) {
      console.error('Failed to refresh queue:', err);
    }
  };

  const handleWockDeliver = async (wockOrderDbId) => {
    if (!window.confirm('Download and import keys for this order?')) return;
    setWockActionLoading(`deliver-${wockOrderDbId}`);
    try {
      await axios.post(`${API_URL}/api/wock/orders/${wockOrderDbId}/deliver`);
      await refreshQueue();
    } catch (err) {
      alert('Deliver failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setWockActionLoading(null);
    }
  };

  const handleWockCancel = async (wockOrderDbId) => {
    if (!window.confirm('Cancel ALL WOCK orders for this queue? The queue will be stopped and counts reset to 0.')) return;
    setWockActionLoading(`cancel-${wockOrderDbId}`);
    try {
      await axios.post(`${API_URL}/api/wock/orders/${wockOrderDbId}/cancel`);
      await refreshQueue();
    } catch (err) {
      alert('Cancel failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setWockActionLoading(null);
    }
  };

  // Use server-side counts from queue items (not paginated transactions)
  const successCount = queue.items.reduce((sum, item) => sum + (item.successCount || 0), 0);
  const failedCount = queue.items.reduce((sum, item) => sum + (item.failedCount || 0), 0);

  // WOCK manual delivery summary
  const wockOrders = queue.wockOrders || [];
  const hasWockManual = queue.items.some(i => i.vendor === 'wock' && i.wockAutoDeliver === false);
  const wockReadyCount = wockOrders.filter(wo => wo.status === 'READY').reduce((sum, wo) => sum + wo.quantity, 0);
  const wockDeliveredCount = wockOrders.filter(wo => wo.status === 'COMPLETED').reduce((sum, wo) => sum + (wo.keysReceived || 0), 0);
  const wockPendingCount = wockOrders.filter(wo => wo.status === 'CREATED' || wo.status === 'PROCESSING').reduce((sum, wo) => sum + wo.quantity, 0);
  const wockOrderedTotal = wockOrders.filter(wo => wo.status !== 'CANCELLED' && wo.status !== 'FAILED').reduce((sum, wo) => sum + wo.quantity, 0);

  const handleToggleGeneration = async () => {
    setIsUpdating(true);
    try {
      if (queue.status === 'RUNNING') {
        // Pause the queue
        await axios.post(`${API_URL}/api/queues/${queue.id}/pause`);
        setQueues(prev => prev.map(q => 
          q.id === queue.id ? { ...q, status: 'PAUSED' } : q
        ));
      } else {
        // Start/Resume the queue
        await axios.post(`${API_URL}/api/queues/${queue.id}/start`);
        setQueues(prev => prev.map(q => 
          q.id === queue.id ? { ...q, status: 'RUNNING' } : q
        ));
      }
    } catch (error) {
      console.error('Failed to update queue status:', error);
      alert('Failed to update queue status: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStop = async () => {
    setIsUpdating(true);
    try {
      await axios.post(`${API_URL}/api/queues/${queue.id}/stop`);
      setQueues(prev => prev.map(q => 
        q.id === queue.id ? { ...q, status: 'STOPPED' } : q
      ));
    } catch (error) {
      console.error('Failed to stop queue:', error);
      alert('Failed to stop queue: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    const totalGenerated = queue.items.reduce((sum, item) => sum + (item.successCount || 0) + (item.failedCount || 0), 0);
    const confirmMessage = totalGenerated > 0
      ? `Are you sure you want to delete queue "${queue.poNumber}"?\n\nThis will also delete ${totalGenerated} associated transaction(s). This action cannot be undone.`
      : `Are you sure you want to delete queue "${queue.poNumber}"?\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    
    try {
      // Try to delete from backend API
      try {
        await axios.delete(`${API_URL}/api/queues/${queue.id}`);
      } catch (apiError) {
        // If API fails (e.g., queue only exists locally), continue with local deletion
        console.log('API delete failed, removing locally:', apiError.message);
      }

      // Remove associated transactions from local state
      setTransactions(prev => prev.filter(t => t.queueId !== queue.id));
      
      // Remove queue from local state
      setQueues(prev => prev.filter(q => q.id !== queue.id));
      
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete queue: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Calculate total progress
  const totalTarget = queue.items.reduce((sum, item) => sum + (item.targetQuantity || 0), 0);
  const totalProgress = totalTarget > 0 ? ((successCount / totalTarget) * 100).toFixed(0) : 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="p-3 sm:p-4">
        {/* Compact single-row layout */}
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Left: Queue info */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate text-sm sm:text-base">{queue.poNumber}</h3>
                {queue.piNumber && <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">PI: {queue.piNumber}</span>}
                <StatusBadge status={queue.status} />
                <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">{queue.environment}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline">• {queue.items.length} SKUs</span>
              </div>
            </div>
          </div>

          {/* Center: Stats (compact) */}
          <div className="hidden md:flex items-center gap-2">
            {hasWockManual && successCount === 0 && failedCount === 0 && wockOrderedTotal > 0 ? (
              <>
                <div className="flex items-center gap-1 px-2 py-1 bg-pink-50 dark:bg-pink-900/30 rounded text-sm">
                  <span className="text-pink-500 dark:text-pink-400">📦</span>
                  <span className="font-semibold text-pink-700 dark:text-pink-400">{wockOrderedTotal} ordered</span>
                </div>
                {wockReadyCount > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/30 rounded text-sm">
                    <span className="font-semibold text-green-700 dark:text-green-400">{wockReadyCount} ready</span>
                  </div>
                )}
                {wockPendingCount > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 dark:bg-amber-900/30 rounded text-sm">
                    <span className="font-semibold text-amber-700 dark:text-amber-400">{wockPendingCount} pending</span>
                  </div>
                )}
                {wockDeliveredCount > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/30 rounded text-sm">
                    <span className="text-green-600 dark:text-green-400">✓</span>
                    <span className="font-semibold text-green-700 dark:text-green-400">{wockDeliveredCount}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/30 rounded text-sm">
                  <span className="text-green-600 dark:text-green-400">✓</span>
                  <span className="font-semibold text-green-700 dark:text-green-400">{successCount}</span>
                </div>
                {failedCount > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-red-50 dark:bg-red-900/30 rounded text-sm">
                    <span className="text-red-600 dark:text-red-400">✗</span>
                    <span className="font-semibold text-red-700 dark:text-red-400">{failedCount}</span>
                  </div>
                )}
                {totalTarget > 0 && totalTarget - successCount > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Pending:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{totalTarget - successCount}</span>
                  </div>
                )}
                {totalTarget > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 dark:bg-amber-900/30 rounded text-sm">
                    <span className="font-semibold text-amber-700 dark:text-amber-400">{totalProgress}%</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1">
            {queue.status !== 'STOPPED' && queue.status !== 'COMPLETED' && (
              <>
                <button
                  onClick={handleToggleGeneration}
                  disabled={isUpdating}
                  className={`p-1.5 rounded ${
                    queue.status === 'RUNNING' 
                      ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-900/60' 
                      : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/60'
                  } disabled:opacity-50`}
                  title={queue.status === 'RUNNING' ? 'Pause' : 'Start'}
                >
                  {isUpdating ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : queue.status === 'RUNNING' ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={handleStop}
                  disabled={isUpdating}
                  className="p-1.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/60 disabled:opacity-50"
                  title="Stop"
                >
                  <Square className="w-4 h-4" />
                </button>
              </>
            )}
            <button
              onClick={handleDelete}
              disabled={isDeleting || queue.status === 'RUNNING'}
              className="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete Queue"
            >
              {isDeleting ? (
                <div className="w-4 h-4 border-2 border-red-600 dark:border-red-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="px-2 py-1 text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded"
            >
              {expanded ? 'Hide' : 'Details'}
            </button>
          </div>
        </div>

        {/* Mobile stats row */}
        <div className="flex md:hidden items-center gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex-wrap">
          {hasWockManual && successCount === 0 && failedCount === 0 && wockOrderedTotal > 0 ? (
            <>
              <div className="flex items-center gap-1 px-2 py-1 bg-pink-50 dark:bg-pink-900/30 rounded text-xs">
                <span className="font-semibold text-pink-700 dark:text-pink-400">{wockOrderedTotal} ordered</span>
              </div>
              {wockReadyCount > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/30 rounded text-xs">
                  <span className="font-semibold text-green-700 dark:text-green-400">{wockReadyCount} ready</span>
                </div>
              )}
              {wockPendingCount > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 dark:bg-amber-900/30 rounded text-xs">
                  <span className="font-semibold text-amber-700 dark:text-amber-400">{wockPendingCount} pending</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/30 rounded text-xs">
                <span className="text-green-600 dark:text-green-400">✓</span>
                <span className="font-semibold text-green-700 dark:text-green-400">{successCount}</span>
              </div>
              {failedCount > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-red-50 dark:bg-red-900/30 rounded text-xs">
                  <span className="text-red-600 dark:text-red-400">✗</span>
                  <span className="font-semibold text-red-700 dark:text-red-400">{failedCount}</span>
                </div>
              )}
              {totalTarget > 0 && totalTarget - successCount > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Pending:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{totalTarget - successCount}</span>
                </div>
              )}
              {totalTarget > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 dark:bg-amber-900/30 rounded text-xs">
                  <span className="font-semibold text-amber-700 dark:text-amber-400">{totalProgress}%</span>
                </div>
              )}
            </>
          )}
        </div>

        {expanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2 text-sm">SKU Configuration</h4>
            <div className="space-y-2">
              {queue.items.map((item, idx) => {
                const product = products.find(p => p.id === item.productId);
                const isWockManualItem = item.vendor === 'wock' && item.wockAutoDeliver === false;
                const itemWockOrders = isWockManualItem ? (queue.wockOrders || []).filter(wo => wo.queueItemId === item.id) : [];
                const itemOrdered = isWockManualItem ? itemWockOrders.filter(wo => wo.status !== 'CANCELLED' && wo.status !== 'FAILED').reduce((s, wo) => s + wo.quantity, 0) : 0;
                const itemDelivered = isWockManualItem ? itemWockOrders.filter(wo => wo.status === 'COMPLETED').reduce((s, wo) => s + (wo.keysReceived || 0), 0) : 0;
                const itemSuccess = item.successCount || 0;
                const itemFailed = item.failedCount || 0;
                const itemGenerated = itemSuccess + itemFailed;
                const displayCount = isWockManualItem && itemGenerated === 0 ? itemOrdered : itemSuccess;
                const progress = item.targetQuantity ? ((displayCount / item.targetQuantity) * 100).toFixed(0) : 0;
                return (
                  <div key={idx} className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 dark:text-white text-sm truncate">{item.sku || product?.sku || 'Unknown'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {product?.faceValue ? `${currencySymbol(product?.currencyCode)}${(product.faceValue / 100).toFixed(2)}` : ''} • {item.vendor === 'bamboo' || item.vendor === 'wock' ? (isWockManualItem ? 'Manual' : 'Batch') : `${item.ratePerMinute}/min`}
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        {isWockManualItem && itemGenerated === 0 && itemOrdered > 0 ? (
                          <>
                            <span className="font-medium text-pink-600 dark:text-pink-400">{itemOrdered}</span>
                            <span className="text-gray-500 dark:text-gray-400">/{item.targetQuantity || '∞'}</span>
                            <span className="text-xs text-pink-500 dark:text-pink-400 ml-1">ordered</span>
                          </>
                        ) : (
                          <>
                            <span className="font-medium text-green-600 dark:text-green-400">{itemSuccess}</span>
                            <span className="text-gray-500 dark:text-gray-400">/{item.targetQuantity || '∞'}</span>
                            {itemFailed > 0 && <span className="text-xs text-red-500 dark:text-red-400 ml-1">({itemFailed} failed)</span>}
                          </>
                        )}
                      </div>
                    </div>
                    {item.targetQuantity && (
                      <div className="mt-1.5">
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all duration-300 ${isWockManualItem && itemGenerated === 0 ? 'bg-pink-500' : 'bg-green-500 dark:bg-green-400'}`}
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {/* WOCK order status for manual delivery items */}
                    {isWockManualItem && itemWockOrders.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {itemWockOrders.map(wo => (
                          <div key={wo.id} className="flex flex-wrap items-center gap-1.5 sm:gap-2 px-2 py-1.5 bg-pink-50 dark:bg-pink-900/20 rounded text-xs">
                            <div className="flex items-center gap-1.5 min-w-0">
                              {wo.status === 'CREATED' || wo.status === 'PROCESSING' ? (
                                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                  <div className="w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                  {wo.status === 'CREATED' ? 'Pending' : 'Processing'}
                                </span>
                              ) : wo.status === 'READY' ? (
                                <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 rounded font-medium">
                                  Ready
                                </span>
                              ) : wo.status === 'COMPLETED' ? (
                                <span className="text-green-600 dark:text-green-400">
                                  Delivered ({wo.keysReceived || 0})
                                </span>
                              ) : wo.status === 'FAILED' ? (
                                <span className="text-red-600 dark:text-red-400 truncate" title={wo.errorMessage}>
                                  Failed
                                </span>
                              ) : wo.status === 'CANCELLED' ? (
                                <span className="text-gray-500 dark:text-gray-400">Cancelled</span>
                              ) : null}
                              <span className="text-gray-400 dark:text-gray-500">×{wo.quantity}</span>
                            </div>
                            {(wo.status === 'CREATED' || wo.status === 'PROCESSING' || wo.status === 'READY') && (
                              <div className="flex items-center gap-1 ml-auto">
                                {wo.status === 'READY' && (
                                  <button
                                    onClick={() => handleWockDeliver(wo.id)}
                                    disabled={!!wockActionLoading}
                                    className="px-2 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-xs font-medium"
                                  >
                                    {wockActionLoading === `deliver-${wo.id}` ? '...' : 'Deliver'}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleWockCancel(wo.id)}
                                  disabled={!!wockActionLoading}
                                  className="px-2 py-0.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-xs font-medium"
                                >
                                  {wockActionLoading === `cancel-${wo.id}` ? '...' : 'Cancel'}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Transactions = ({ transactions, setTransactions, queues, pagination, fetchTransactions, products }) => {
  const [filter, setFilter] = useState('all');
  const [revealedPins, setRevealedPins] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [skuFilter, setSkuFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [poFilter, setPoFilter] = useState('');
  const [debouncedPoFilter, setDebouncedPoFilter] = useState('');
  const [piFilter, setPiFilter] = useState('');
  const [debouncedPiFilter, setDebouncedPiFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [cancellingIds, setCancellingIds] = useState(new Set());
  const [isCancellingAll, setIsCancellingAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const pageSizeOptions = [50, 100, 250, 500];
  const [expandedSubKeys, setExpandedSubKeys] = useState(new Set());

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce PO filter
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPoFilter(poFilter), 300);
    return () => clearTimeout(timer);
  }, [poFilter]);

  // Debounce PI filter
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPiFilter(piFilter), 300);
    return () => clearTimeout(timer);
  }, [piFilter]);

  // Default column visibility
  const defaultColumns = {
    timestamp: true,
    user: true,
    poNumber: true,
    piNumber: true,
    sku: true,
    vendor: true,
    tid: true,
    txid: true,
    hostTxid: false,
    pin: true,
    serial: true,
    amount: true,
    status: true,
    actions: true
  };

  const [visibleColumns, setVisibleColumns] = useState(defaultColumns);
  const [columnsLoaded, setColumnsLoaded] = useState(false);

  // Load column preferences on mount
  useEffect(() => {
    const loadColumnPrefs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/preferences/transactionColumns`);
        if (response.data.value) {
          setVisibleColumns({ ...defaultColumns, ...response.data.value });
        }
      } catch (error) {
        console.log('No column preferences found, using defaults');
      }
      setColumnsLoaded(true);
    };
    loadColumnPrefs();
  }, []);

  // Save column preferences when changed
  const toggleColumn = async (column) => {
    const newColumns = { ...visibleColumns, [column]: !visibleColumns[column] };
    setVisibleColumns(newColumns);
    try {
      await axios.put(`${API_URL}/api/preferences/transactionColumns`, { value: newColumns });
    } catch (error) {
      console.error('Failed to save column preference:', error);
    }
  };

  // Column definitions
  const columnDefs = [
    { key: 'timestamp', label: 'Timestamp' },
    { key: 'user', label: 'User' },
    { key: 'poNumber', label: 'PO Number' },
    { key: 'piNumber', label: 'PI Number' },
    { key: 'sku', label: 'SKU' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'status', label: 'Status' },
    { key: 'tid', label: 'TID' },
    { key: 'txid', label: 'TXID' },
    { key: 'hostTxid', label: 'Vendor TXID' },
    { key: 'pin', label: 'PIN/Code' },
    { key: 'serial', label: 'Serial' },
    { key: 'amount', label: 'Amount' },
    { key: 'actions', label: 'Actions' }
  ];

  // Create a map of queueId to PO Number
  const queuePoMap = {};
  queues.forEach(q => {
    queuePoMap[q.id] = q.poNumber;
  });

  // Get unique PO numbers
  const uniquePoNumbers = [...new Set(queues.map(q => q.poNumber))];

  // Calculate date filter range for server-side filtering
  const getDateFilters = () => {
    const now = new Date();
    switch (dateFilter) {
      case '7days':
        return { dateFrom: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString() };
      case '30days':
        return { dateFrom: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString() };
      case '90days':
        return { dateFrom: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString() };
      case 'custom':
        return {
          dateFrom: customStartDate ? new Date(customStartDate + 'T00:00:00').toISOString() : undefined,
          dateTo: customEndDate ? new Date(customEndDate + 'T23:59:59').toISOString() : undefined
        };
      default:
        return {};
    }
  };

  // Fetch transactions when filters or page changes (server-side pagination)
  useEffect(() => {
    const loadTransactions = async () => {
      setIsLoadingTransactions(true);
      try {
        const dateFilters = getDateFilters();
        await fetchTransactions(currentPage, {
          status: filter,
          sku: skuFilter,
          vendor: vendorFilter,
          search: debouncedSearch,
          poNumber: debouncedPoFilter,
          piNumber: debouncedPiFilter,
          limit: itemsPerPage,
          ...dateFilters
        });
      } catch (error) {
        console.error('Failed to load transactions:', error);
      } finally {
        setIsLoadingTransactions(false);
      }
    };
    loadTransactions();
  }, [currentPage, itemsPerPage, filter, skuFilter, vendorFilter, debouncedSearch, debouncedPoFilter, debouncedPiFilter, dateFilter, customStartDate, customEndDate, fetchTransactions]);

  // Server-side pagination - transactions are already filtered and paginated
  // Group sub-key transactions (TXID contains -S\d+) under their parent.
  // ONLY WOCK HAS SUB-KEYS (#107) — Bamboo's txid ends in a vendor-supplied
  // `card.id`, so ungated, a card id shaped `S<digits>` would nest a real
  // transaction under a parent that does not exist and drop it from this table.
  const { parentTransactions, subKeyMap } = useMemo(() => {
    const parents = [];
    const subs = new Map(); // rootTxid -> [subKeyTxs]
    for (const tx of transactions) {
      if (tx.vendor === 'wock' && /-S\d+/.test(tx.txid)) {
        const rootTxid = tx.txid.replace(/-S\d+/g, '');
        if (!subs.has(rootTxid)) subs.set(rootTxid, []);
        subs.get(rootTxid).push(tx);
      } else {
        parents.push(tx);
      }
    }
    return { parentTransactions: parents, subKeyMap: subs };
  }, [transactions]);
  const paginatedTransactions = parentTransactions;
  const filteredTransactions = transactions; // Alias for export functions (exports current page)
  const totalPages = pagination?.totalPages || 0;

  // Reset to page 1 when filters change
  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  // Handle page size change
  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  // Get unique SKUs from products (more reliable than from current page of transactions)
  const uniqueSkus = products ? [...new Set(products.map(p => p.sku || p.vendorSku).filter(Boolean))] : [];

  // Get cancellable transactions from current page (only SUCCESS status can be cancelled)
  const cancellableTransactions = transactions.filter(t => t.status === 'SUCCESS');

  const togglePinReveal = (id) => {
    setRevealedPins(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleCancelTransaction = async (tx) => {
    if (tx.status !== 'SUCCESS') {
      alert('Can only cancel successful transactions');
      return;
    }

    if (!window.confirm(`Are you sure you want to cancel this transaction?\n\nTXID: ${tx.txid}\nPIN: ${tx.pin}\nAmount: ${currencySymbol(tx.currencyCode)}${(tx.amount / 100).toFixed(2)}`)) {
      return;
    }

    setCancellingIds(prev => new Set([...prev, tx.id]));

    try {
      const response = await axios.post(`${API_URL}/api/cancel`, {
        txref: tx.txid,
        ean: tx.ean,
        amount: tx.amount
      });

      if (response.data.success) {
        // Update transaction status to CANCELLED
        setTransactions(prev => prev.map(t => 
          t.id === tx.id 
            ? { ...t, status: 'CANCELLED', cancelTxid: response.data.txid }
            : t
        ));
        alert('Transaction cancelled successfully!');
      } else {
        alert(`Cancel failed: ${response.data.response?.resultText || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Cancel error:', error);
      alert('Cancel failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setCancellingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(tx.id);
        return newSet;
      });
    }
  };

  const handleCancelAll = async () => {
    if (cancellableTransactions.length === 0) {
      alert('No successful transactions to cancel');
      return;
    }

    const totalValue = (cancellableTransactions.reduce((sum, t) => sum + t.amount, 0) / 100).toFixed(2);

    if (!window.confirm(`Are you sure you want to cancel ALL ${cancellableTransactions.length} successful transaction(s)?\n\nTotal value: ${currencySymbol(cancellableTransactions[0]?.currencyCode)}${totalValue}\n\nThis action cannot be undone.`)) {
      return;
    }

    setIsCancellingAll(true);
    let cancelledCount = 0;
    let failedCount = 0;

    for (const tx of cancellableTransactions) {
      try {
        setCancellingIds(prev => new Set([...prev, tx.id]));
        
        const response = await axios.post(`${API_URL}/api/cancel`, {
          txref: tx.txid,
          ean: tx.ean,
          amount: tx.amount
        });

        if (response.data.success) {
          setTransactions(prev => prev.map(t => 
            t.id === tx.id 
              ? { ...t, status: 'CANCELLED', cancelTxid: response.data.txid, cancelledAt: new Date().toLocaleString() }
              : t
          ));
          cancelledCount++;
        } else {
          failedCount++;
        }

        // Small delay between cancellations
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Cancel error for', tx.txid, error);
        failedCount++;
      } finally {
        setCancellingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(tx.id);
          return newSet;
        });
      }
    }

    setIsCancellingAll(false);
    alert(`Cancel complete!\n\nCancelled: ${cancelledCount}\nFailed: ${failedCount}`);
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({ format });

      if (filter && filter !== 'all') {
        params.append('status', filter.toUpperCase());
      }
      if (skuFilter && skuFilter !== 'all') {
        params.append('sku', skuFilter);
      }
      if (vendorFilter && vendorFilter !== 'all') {
        params.append('vendor', vendorFilter);
      }
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      if (debouncedPoFilter) {
        params.append('poNumber', debouncedPoFilter);
      }
      if (debouncedPiFilter) {
        params.append('piNumber', debouncedPiFilter);
      }
      const dateFilters = getDateFilters();
      if (dateFilters.dateFrom) {
        params.append('dateFrom', dateFilters.dateFrom);
      }
      if (dateFilters.dateTo) {
        params.append('dateTo', dateFilters.dateTo);
      }

      const response = await axios.get(`${API_URL}/api/transactions/export?${params}`, {
        responseType: 'blob'
      });

      const ext = format === 'xlsx' ? 'xlsx' : 'csv';
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions_${Date.now()}.${ext}`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Transaction History</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button 
            onClick={handleCancelAll}
            disabled={isCancellingAll || cancellableTransactions.length === 0}
            className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 text-sm sm:text-base bg-red-600 dark:bg-red-500 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4" />
            <span className="hidden sm:inline">{isCancellingAll ? 'Cancelling...' : `Cancel All (${cancellableTransactions.length})`}</span>
            <span className="sm:hidden">{isCancellingAll ? '...' : `Cancel (${cancellableTransactions.length})`}</span>
          </button>
          <button
            onClick={() => handleExport('csv')}
            disabled={isExporting || transactions.length === 0}
            className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 text-sm sm:text-base bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            <span>{isExporting ? '...' : 'CSV'}</span>
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={isExporting || transactions.length === 0}
            className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 text-sm sm:text-base bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            <span>{isExporting ? '...' : 'Excel'}</span>
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search TXID, PIN, or Serial..."
              value={searchTerm}
              onChange={(e) => handleFilterChange(setSearchTerm)(e.target.value)}
              className={`w-full pl-10 pr-3 py-2 border ${searchTerm ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
            />
          </div>
          <div className="w-56">
            <FilterableProductSelect
              products={products}
              value={skuFilter}
              onChange={handleFilterChange(setSkuFilter)}
              placeholder="All Products"
            />
          </div>
          <select
            value={vendorFilter}
            onChange={(e) => handleFilterChange(setVendorFilter)(e.target.value)}
            className={`px-3 py-2 border ${vendorFilter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          >
            <option value="all">All Vendors</option>
            <option value="epay">ePay</option>
            <option value="bamboo">Bamboo</option>
            <option value="ubiqfy">Ubiqfy</option>
            <option value="wock">WOCK</option>
          </select>
          <select
            value={filter}
            onChange={(e) => handleFilterChange(setFilter)(e.target.value)}
            className={`px-3 py-2 border ${filter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          >
            <option value="all">All Statuses</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <input
            type="text"
            placeholder="PO Number..."
            value={poFilter}
            onChange={(e) => handleFilterChange(setPoFilter)(e.target.value)}
            className={`w-36 px-3 py-2 border ${poFilter ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          />
          <input
            type="text"
            placeholder="PI Number..."
            value={piFilter}
            onChange={(e) => handleFilterChange(setPiFilter)(e.target.value)}
            className={`w-36 px-3 py-2 border ${piFilter ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          />
          <select
            value={dateFilter}
            onChange={(e) => handleFilterChange(setDateFilter)(e.target.value)}
            className={`px-3 py-2 border ${dateFilter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          >
            <option value="all">All Time</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="custom">Custom Range</option>
          </select>
          {(searchTerm || skuFilter !== 'all' || vendorFilter !== 'all' || filter !== 'all' || poFilter || piFilter || dateFilter !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setSkuFilter('all'); setVendorFilter('all'); setFilter('all'); setPoFilter(''); setPiFilter(''); setDateFilter('all'); setCustomStartDate(''); setCustomEndDate(''); setCurrentPage(1); }}
              className="flex items-center gap-1 px-2 py-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Clear all filters"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        
        {/* Custom Date Range Picker */}
        {dateFilter === 'custom' && (
          <div className="flex flex-col sm:flex-row gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => {
                  setCustomStartDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => {
                  setCustomEndDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
            {(customStartDate || customEndDate) && (
              <button
                onClick={() => {
                  setCustomStartDate('');
                  setCustomEndDate('');
                  setCurrentPage(1);
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
              >
                Clear Dates
              </button>
            )}
          </div>
        )}
      </div>

      {/* Column Selector & Table Container */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Column Selector Button */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex justify-end">
          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <Settings className="w-4 h-4" />
              Columns
            </button>
            {showColumnSelector && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                <div className="p-2">
                  {columnDefs.map(col => (
                    <label key={col.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                {visibleColumns.timestamp && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Timestamp</th>}
                {visibleColumns.user && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>}
                {visibleColumns.poNumber && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">PO Number</th>}
                {visibleColumns.piNumber && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">PI Number</th>}
                {visibleColumns.sku && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">SKU</th>}
                {visibleColumns.vendor && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vendor</th>}
                {visibleColumns.status && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>}
                {visibleColumns.tid && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">TID</th>}
                {visibleColumns.txid && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">TXID</th>}
                {visibleColumns.hostTxid && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vendor TXID</th>}
                {visibleColumns.pin && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">PIN/Code</th>}
                {visibleColumns.serial && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Serial</th>}
                {visibleColumns.amount && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>}
                {visibleColumns.actions && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoadingTransactions ? (
                <tr>
                  <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    <div className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading transactions...
                    </div>
                  </td>
                </tr>
              ) : paginatedTransactions.length === 0 ? (
                <tr>
                  <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No transactions found
                  </td>
                </tr>
              ) : (
                paginatedTransactions.map(tx => {
                  const subKeys = subKeyMap.get(tx.txid) || [];
                  const hasSubKeys = subKeys.length > 0;
                  const isExpanded = expandedSubKeys.has(tx.txid);
                  return (
                  <React.Fragment key={tx.id}>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    {visibleColumns.timestamp && (
                      <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        <div className="flex items-center">
                          {hasSubKeys && (
                            <button
                              onClick={() => setExpandedSubKeys(prev => {
                                const next = new Set(prev);
                                if (next.has(tx.txid)) next.delete(tx.txid);
                                else next.add(tx.txid);
                                return next;
                              })}
                              className="mr-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                              title={`${subKeys.length} DLC sub-code${subKeys.length > 1 ? 's' : ''}`}
                            >
                              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          {tx.timestamp}
                        </div>
                      </td>
                    )}
                    {visibleColumns.user && (
                      <td className="px-3 py-1.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          tx.creatorInitials === 'SC' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400' :
                          tx.creatorInitials === 'JG' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' :
                          tx.creatorInitials === 'KAB' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}>
                          {tx.creatorInitials || tx.creator_initials || '-'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.poNumber && (
                      <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-white font-medium">{queuePoMap[tx.queueId] || tx.po_number || '-'}</td>
                    )}
                    {visibleColumns.piNumber && (
                      <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-white">{tx.piNumber || '-'}</td>
                    )}
                    {visibleColumns.sku && (
                      <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-white">{tx.sku}</td>
                    )}
                    {visibleColumns.vendor && (
                      <td className="px-3 py-1.5">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          tx.vendor === 'bamboo'
                            ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                            : tx.vendor === 'ubiqfy'
                              ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400'
                              : tx.vendor === 'wock'
                                ? 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-400'
                                : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                        }`}>
                          {tx.vendor === 'bamboo' ? 'Bamboo' : tx.vendor === 'ubiqfy' ? 'Ubiqfy' : tx.vendor === 'wock' ? 'WOCK' : 'ePay'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-3 py-1.5">
                        <StatusBadge status={tx.status} />
                      </td>
                    )}
                    {visibleColumns.tid && (
                      <td className="px-3 py-1.5 text-sm font-mono text-gray-600 dark:text-gray-400">{tx.terminalTid || tx.terminal_tid || '-'}</td>
                    )}
                    {visibleColumns.txid && (
                      <td className="px-3 py-1.5 text-sm font-mono text-gray-600 dark:text-gray-400 max-w-[180px]">
                        <span
                          className="truncate block cursor-pointer hover:text-amber-600 dark:hover:text-amber-400"
                          title="Click to copy"
                          onClick={() => { navigator.clipboard.writeText(tx.txid); }}
                        >{tx.txid}</span>
                      </td>
                    )}
                    {visibleColumns.hostTxid && (
                      <td className="px-3 py-1.5 text-sm font-mono text-gray-600 dark:text-gray-400">
                        {tx.hostTxid ? (
                          <span
                            className="cursor-pointer hover:text-amber-600 dark:hover:text-amber-400"
                            title="Click to copy"
                            onClick={() => { navigator.clipboard.writeText(tx.hostTxid); }}
                          >{tx.hostTxid}</span>
                        ) : '-'}
                      </td>
                    )}
                    {visibleColumns.pin && (
                      <td className="px-3 py-1.5 text-sm">
                        {tx.pin && tx.pinMimeType && tx.pinMimeType.startsWith('image/') ? (
                          <div className="flex items-center space-x-2">
                            {revealedPins.has(tx.id) ? (
                              <img src={`data:${tx.pinMimeType};base64,${tx.pin}`} alt="Key" className="max-h-16 max-w-[200px] rounded border border-gray-200 dark:border-gray-600" />
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400">
                                <Image className="w-3 h-3 mr-1" />Image key
                              </span>
                            )}
                            <button
                              onClick={() => togglePinReveal(tx.id)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              {revealedPins.has(tx.id) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="font-mono text-gray-900 dark:text-white">
                              {tx.pin ? (
                                revealedPins.has(tx.id)
                                  ? tx.pin
                                  : tx.pin.length > 4
                                    ? `${'*'.repeat(tx.pin.length - 4)}${tx.pin.slice(-4)}`
                                    : '****'
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </span>
                            {tx.pin && (
                              <button
                                onClick={() => togglePinReveal(tx.id)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                              >
                                {revealedPins.has(tx.id) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                    {visibleColumns.serial && (
                      <td className="px-3 py-1.5 text-sm font-mono text-gray-600 dark:text-gray-400">{tx.serial || '-'}</td>
                    )}
                    {visibleColumns.amount && (
                      <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-white">{currencySymbol(tx.currencyCode)}{(tx.amount / 100).toFixed(2)}</td>
                    )}
                    {visibleColumns.actions && (
                      <td className="px-3 py-1.5">
                        <div className="flex items-center space-x-2">
                          {tx.status === 'SUCCESS' && tx.vendor !== 'bamboo' && tx.vendor !== 'wock' && (
                            <button
                              onClick={() => handleCancelTransaction(tx)}
                              disabled={cancellingIds.has(tx.id)}
                              className="flex items-center space-x-1 px-2 py-1 text-xs bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/60 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {cancellingIds.has(tx.id) ? (
                                <div className="w-3 h-3 border-2 border-red-700 dark:border-red-400 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <X className="w-3 h-3" />
                              )}
                              <span>{cancellingIds.has(tx.id) ? '...' : 'Cancel'}</span>
                            </button>
                          )}
                          {tx.status === 'CANCELLED' && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">Cancelled</span>
                          )}
                          {tx.status === 'FAILED' && (
                            <span className="text-xs text-gray-500 dark:text-gray-400" title={tx.resultText}>{tx.resultText || 'Failed'}</span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  {/* Sub-key (DLC) rows */}
                  {isExpanded && subKeys.map(sk => (
                    <tr key={sk.id} className="bg-purple-50/50 dark:bg-purple-900/10">
                      {visibleColumns.timestamp && (
                        <td className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 pl-8">{sk.timestamp}</td>
                      )}
                      {visibleColumns.user && <td className="px-3 py-1"></td>}
                      {visibleColumns.poNumber && <td className="px-3 py-1"></td>}
                      {visibleColumns.piNumber && <td className="px-3 py-1"></td>}
                      {visibleColumns.sku && (
                        <td className="px-3 py-1">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400">DLC</span>
                        </td>
                      )}
                      {visibleColumns.vendor && <td className="px-3 py-1"></td>}
                      {visibleColumns.status && (
                        <td className="px-3 py-1"><StatusBadge status={sk.status} /></td>
                      )}
                      {visibleColumns.tid && <td className="px-3 py-1"></td>}
                      {visibleColumns.txid && (
                        <td className="px-3 py-1 text-xs font-mono text-gray-500 dark:text-gray-400 max-w-[180px]">
                          <span className="truncate block cursor-pointer hover:text-amber-600 dark:hover:text-amber-400" title="Click to copy" onClick={() => { navigator.clipboard.writeText(sk.txid); }}>{sk.txid}</span>
                        </td>
                      )}
                      {visibleColumns.hostTxid && <td className="px-3 py-1"></td>}
                      {visibleColumns.pin && (
                        <td className="px-3 py-1 text-sm">
                          {sk.pin && sk.pinMimeType && sk.pinMimeType.startsWith('image/') ? (
                            <div className="flex items-center space-x-2">
                              {revealedPins.has(sk.id) ? (
                                <img src={`data:${sk.pinMimeType};base64,${sk.pin}`} alt="DLC Key" className="max-h-16 max-w-[200px] rounded border border-gray-200 dark:border-gray-600" />
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400">
                                  <Image className="w-3 h-3 mr-1" />Image
                                </span>
                              )}
                              <button onClick={() => togglePinReveal(sk.id)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                {revealedPins.has(sk.id) ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="font-mono text-gray-700 dark:text-gray-300 text-xs">
                                {sk.pin ? (
                                  revealedPins.has(sk.id) ? sk.pin : sk.pin.length > 4 ? `${'*'.repeat(sk.pin.length - 4)}${sk.pin.slice(-4)}` : '****'
                                ) : <span className="text-gray-400">-</span>}
                              </span>
                              {sk.pin && (
                                <button onClick={() => togglePinReveal(sk.id)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                  {revealedPins.has(sk.id) ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                      {visibleColumns.serial && <td className="px-3 py-1"></td>}
                      {visibleColumns.amount && <td className="px-3 py-1 text-xs text-gray-400">—</td>}
                      {visibleColumns.actions && <td className="px-3 py-1"></td>}
                    </tr>
                  ))}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={pagination?.total || 0}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        pageSizeOptions={pageSizeOptions}
      />
    </div>
  );
};

// ============================================================================
// PRODUCTS MANAGEMENT COMPONENT
// ============================================================================

const downloadTemplate = async (format) => {
  try {
    const response = await axios.get(`${API_URL}/api/products/template/${format}`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `product_template.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    alert('Failed to download template: ' + (error.response?.data?.error || error.message));
  }
};

const PRODUCT_COLUMNS = [
  { key: 'picture', label: 'Picture', defaultVisible: true },
  { key: 'sku', label: 'Customer SKU', defaultVisible: true },
  { key: 'erpSku', label: 'ERP SKU', defaultVisible: false },
  { key: 'vendorSku', label: 'Vendor ID', defaultVisible: true },
  { key: 'vendor', label: 'Vendor', defaultVisible: true },
  { key: 'ean', label: 'EAN', defaultVisible: true },
  { key: 'internalDescription', label: 'Internal Description', defaultVisible: true },
  { key: 'description', label: 'Vendor Description', defaultVisible: true },
  { key: 'denomination', label: 'Denomination', defaultVisible: true },
  { key: 'stock', label: 'Stock', defaultVisible: true },
  { key: 'brandName', label: 'Brand', defaultVisible: false },
  { key: 'category', label: 'Category', defaultVisible: false },
  // Vendor metadata — off by default, like Brand and Category. Only WOCK and
  // Ubiqfy supply any of it, so leaving them on would show two mostly-empty
  // columns to everyone working on ePay products.
  { key: 'platform', label: 'Platform', defaultVisible: false },
  { key: 'region', label: 'Region', defaultVisible: false },
  { key: 'language', label: 'Language', defaultVisible: false },
  { key: 'languages', label: 'Languages', defaultVisible: false },
  { key: 'regions', label: 'Countries', defaultVisible: false },
  { key: 'cost', label: 'Our Cost', defaultVisible: true },
  { key: 'discount', label: 'Discount', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'api', label: 'API', defaultVisible: true },
  { key: 'actions', label: 'Actions', defaultVisible: true },
];

const COLUMNS_STORAGE_KEY = 'productColumnsVisibility';

/**
 * Render a vendor code array (languages, regions) in one table cell.
 *
 * A WOCK product can carry up to 251 region codes. Printing them all makes a
 * single row taller than the viewport, so the cell shows the first three and
 * the count, with the complete list in the native tooltip.
 */
const codeListCell = (codes) => {
  if (!Array.isArray(codes) || codes.length === 0) return '—';
  const head = codes.slice(0, 3).join(', ');
  return (
    <span title={codes.join(', ')} className="whitespace-nowrap">
      {head}{codes.length > 3 ? ` +${codes.length - 3}` : ''}
    </span>
  );
};

/**
 * Inline click-to-edit text cell.
 *
 * Behavior:
 *   - Click (or focus + Enter/Space) to enter edit mode.
 *   - Enter or blur commits; Escape cancels and restores the original value.
 *   - `onSave(newValue)` receives the trimmed string, or `null` when cleared.
 *     Whitespace-only input is treated as null.
 *   - If the value is unchanged, commit is a no-op (no request fired).
 *   - On save error, the input stays open with the user's draft preserved so
 *     they can correct it or press Esc to abort. Error surfaced via alert().
 *
 * Accessibility:
 *   - Trigger is a real <button>, reachable via Tab and activatable via
 *     Enter/Space. Carries an aria-label describing the current value.
 *   - Editing input has aria-label + aria-busy while saving.
 *
 * Concurrency:
 *   - Re-entry into commit() is blocked with a ref rather than state because
 *     onBlur can fire before a preceding React state update flushes, which
 *     would otherwise let a double-submit slip through.
 */
const EditableTextCell = ({
  value,
  onSave,
  placeholder = '—',
  ariaLabel = 'Editable field',
  emptyClassName = 'text-gray-400 dark:text-gray-500',
  inputClassName = '',
  listId = null, // id of a <datalist> of suggestions
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const committingRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = () => {
    setDraft(value ?? '');
    setEditing(true);
  };

  const commit = async () => {
    if (committingRef.current) return;
    const trimmed = draft.trim();
    const original = value ?? '';
    if (trimmed === original) {
      setEditing(false);
      return;
    }
    committingRef.current = true;
    setSaving(true);
    try {
      await onSave(trimmed === '' ? null : trimmed);
      setEditing(false);
    } catch (error) {
      alert('Failed to save: ' + (error.response?.data?.error || error.message));
      // Preserve user's draft so they can correct and retry. Refocus the
      // input after the error message dismisses so typing can resume.
      requestAnimationFrame(() => inputRef.current?.focus());
    } finally {
      setSaving(false);
      committingRef.current = false;
    }
  };

  const cancel = () => {
    setDraft(value ?? '');
    setEditing(false);
  };

  if (editing) {
    return (
      <span className="relative inline-block w-full">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          list={listId || undefined}
          disabled={saving}
          aria-label={ariaLabel}
          aria-busy={saving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          onClick={(e) => e.stopPropagation()}
          className={`w-full min-w-[8rem] px-2 py-0.5 pr-6 border border-amber-400 dark:border-amber-500 rounded bg-white dark:bg-gray-700 text-sm outline-none focus:ring-1 focus:ring-amber-400 disabled:opacity-60 ${inputClassName}`}
        />
        {saving && (
          <RefreshCw
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-amber-500 pointer-events-none"
            aria-hidden="true"
          />
        )}
      </span>
    );
  }

  const triggerLabel = value
    ? `${ariaLabel}: ${value}. Click to edit.`
    : `${ariaLabel}: empty. Click to set.`;

  return (
    <button
      type="button"
      onClick={startEdit}
      aria-label={triggerLabel}
      className="text-left cursor-text inline-block min-w-[3rem] px-1 -mx-1 rounded bg-transparent hover:bg-amber-50 dark:hover:bg-amber-900/20 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400 font-[inherit] text-[length:inherit]"
    >
      {value
        ? value
        : <span className={`italic ${emptyClassName}`}>{placeholder}</span>}
    </button>
  );
};

// ============================================================================
// THE CURATED LAYER, staff side (F26 S4). Each descriptive field of a product
// has two layers: the VENDOR's value (rewritten by every sync) and OUR curated
// value (never touched by a sync). Key Gen publishes curated ?? vendor. The
// rules live in backend/src/enrichment/curated.js; this is only the screen.
//   key   = the field's name in `enrichment_meta` and in the bulk route
//   body  = the PUT /api/products/:id property that writes the curated value
// ============================================================================
const CURATED_FIELDS = [
  { key: 'name', label: 'Name', body: 'internalDescription', kind: 'text', column: 'internalDescription' },
  { key: 'brand', label: 'Brand', body: 'curatedBrand', kind: 'text', column: 'brandName' },
  { key: 'category', label: 'Category', body: 'curatedCategory', kind: 'text', column: 'category' },
  { key: 'platform', label: 'Platform', body: 'curatedPlatform', kind: 'text', column: 'platform' },
  { key: 'region', label: 'Region', body: 'curatedRegion', kind: 'text', column: 'region' },
  { key: 'language', label: 'Language', body: 'curatedLanguage', kind: 'text', column: 'language' },
  { key: 'languages', label: 'Languages', body: 'curatedLanguages', kind: 'languages', column: 'languages' },
  { key: 'regions', label: 'Countries', body: 'curatedRegions', kind: 'regions', column: 'regions' },
];
const CURATED_FIELD = Object.fromEntries(CURATED_FIELDS.map(f => [f.key, f]));

// Where the value on screen came from. One of:
//   vendor    the supplier's own value, nobody has touched the field
//   ai        written by the AI, not yet read by a person
//   ai_ok     written by the AI and marked as reviewed
//   manual    typed by a person
//   kept      a person put the field BACK to the supplier's value, on purpose —
//             the AI will not fill it again until that is lifted
const provenanceOf = (product, key) => {
  const curated = product.curated ? product.curated[key] : null;
  const entry = product.enrichmentMeta ? product.enrichmentMeta[key] : null;
  if (curated != null && curated !== '') {
    if (entry && entry.source === 'ai') return entry.reviewed_at ? 'ai_ok' : 'ai';
    return 'manual';
  }
  return entry && entry.source === 'vendor' ? 'kept' : 'vendor';
};

const PROVENANCE = {
  vendor: { short: 'V', label: "Supplier's value", help: 'Comes straight from the supplier. Nobody has changed it.', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600' },
  ai: { short: 'AI', label: 'AI, not reviewed', help: 'Written by the AI. Nobody has checked it yet.', cls: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700' },
  ai_ok: { short: 'AI ✓', label: 'AI, reviewed', help: 'Written by the AI, and a person marked it as correct.', cls: 'bg-white dark:bg-gray-800 text-purple-700 dark:text-purple-300 border-purple-400 dark:border-purple-600' },
  manual: { short: '✎', label: 'Edited by hand', help: 'Typed by a person. Nothing overwrites it: not the supplier, not the AI.', cls: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-700' },
  kept: { short: 'V ✓', label: "Kept as supplier's", help: "A person chose to keep the supplier's value. The AI will not fill this field.", cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700' },
};
const PROVENANCE_ORDER = ['vendor', 'ai', 'ai_ok', 'manual', 'kept'];

const formatStamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

// Who did it and when, for a tooltip or a line under a field.
// What the supplier sent for a field, as a short phrase — "none" when it sent nothing.
const supplierPhrase = (vendorValues, key) => {
  const v = vendorValues ? vendorValues[key] : null;
  if (Array.isArray(v)) return v.length ? (v.length > 12 ? `${v.length} codes` : v.join(', ')) : 'none';
  return v == null || String(v).trim() === '' ? 'none' : String(v);
};

const provenanceDetail = (product, key) => {
  const state = provenanceOf(product, key);
  const entry = (product.enrichmentMeta && product.enrichmentMeta[key]) || {};
  const who = (by, at) => [by ? `by ${by}` : '', at ? `on ${formatStamp(at)}` : ''].filter(Boolean).join(' ');
  // When our value sits on top of the supplier's, say what the supplier sent (S8): a person
  // reviewing an AI value must be able to see what it was derived from without opening the row.
  const supplier = state === 'vendor' ? '' : ` · supplier sent: ${supplierPhrase(product.vendorValues, key)}`;
  // A name the restyle run brought to the guide's form keeps the person's original beside it (S9).
  const original = typeof entry.previous_manual_value === 'string' && entry.previous_manual_value.trim() ? ` · your original: ${entry.previous_manual_value}` : '';
  if (state === 'ai_ok') return `Reviewed ${who(entry.reviewed_by, entry.reviewed_at)}`.trim() + original + supplier;
  if (state === 'ai') return (entry.set_at ? `Written on ${formatStamp(entry.set_at)}` : 'Written by the AI') + original + supplier;
  if (state === 'manual' || state === 'kept') return who(entry.set_by, entry.set_at) + supplier;
  return '';
};

const ProvenanceBadge = ({ state, detail = '' }) => {
  const p = PROVENANCE[state] || PROVENANCE.vendor;
  return (
    <span
      data-provenance={state}
      title={`${p.label} — ${p.help}${detail ? ` (${detail})` : ''}`}
      className={`inline-flex items-center justify-center shrink-0 px-1 min-w-[1.25rem] h-4 text-[10px] leading-none font-semibold rounded border whitespace-nowrap ${p.cls}`}
    >
      {p.short}
    </span>
  );
};

// The key to the badges. Shown above the Products table and in the Edit window.
const ProvenanceLegend = ({ onHelp = null }) => (
  <div data-testid="provenance-legend" className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600 dark:text-gray-400">
    <span className="font-medium text-gray-700 dark:text-gray-300">Where a value came from:</span>
    {PROVENANCE_ORDER.map(state => (
      <span key={state} className="inline-flex items-center gap-1.5" title={PROVENANCE[state].help}>
        <ProvenanceBadge state={state} />
        {PROVENANCE[state].label}
      </span>
    ))}
    {onHelp && (
      <button type="button" onClick={onHelp} className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 hover:underline">
        <HelpCircle className="w-3.5 h-3.5" /> How this works
      </button>
    )}
  </div>
);

// ---- F32: pictures -----------------------------------------------------------
// A product's `image` is `{ source, via, kind, ref, … }` or null, resolved by the
// server: `manual` (a person's upload, for the product or for its whole brand),
// `vendor` (the supplier's), `igdb` (the game database's cover). What the staff
// screen shows for each: an upload is fetched as a blob through the API (the
// token is in localStorage, so a plain <img src> gets 401); a supplier's picture
// and a cover are shown straight from where they live — this is the staff
// screen, which already names suppliers. ⛔ Customers never see those URLs: the
// hub copies the bytes and publishes its own id.
const IGDB_IMAGE_URL = (imageId, size) => `https://images.igdb.com/igdb/image/upload/t_${size}/${imageId}.jpg`;

const pictureSrc = (image, { small = true } = {}) => {
  if (!image) return null;
  if (image.source === 'manual') return `${API_URL}/api/images/${image.id}`;
  if (image.source === 'igdb') return IGDB_IMAGE_URL(image.ref, small ? 'cover_small' : 'cover_big_2x');
  return image.ref;
};

const pictureSourcePhrase = (image) => {
  if (!image) return 'No picture';
  if (image.source === 'manual') return image.via === 'brand' ? `Yours, for every ${image.brand || 'product of this brand'} product` : 'Yours, for this product';
  if (image.source === 'vendor') return image.via === 'brand' ? "The supplier's, through the brand" : "The supplier's";
  if (image.source === 'igdb') return "The game database's cover";
  return 'Picture';
};

// An <img> that goes through the API with the token: fetches the blob, shows it,
// and frees it when it goes away. Used only for uploads; the rest are plain URLs.
const AuthImg = ({ src, alt, className, title }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    let objectUrl = null;
    setUrl(null);
    setFailed(false);
    axios.get(src, { responseType: 'blob' })
      .then(r => { if (!active) return; objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [src]);
  if (failed) return <span className={className} title="The picture could not be loaded" />;
  return url ? <img src={url} alt={alt} title={title} className={className} /> : <span className={`${className} animate-pulse bg-gray-100 dark:bg-gray-700`} />;
};

// The small picture in a table cell or a form. An honest empty frame when there is
// none — never a placeholder picture of ours.
const PictureThumb = ({ image, size = 32, className = '' }) => {
  const box = `shrink-0 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 object-contain ${className}`;
  const style = { width: size, height: size };
  const title = pictureSourcePhrase(image);
  if (!image) return <span data-testid="picture-none" className={`${box} inline-flex items-center justify-center text-gray-300 dark:text-gray-600 border-dashed`} style={style} title={title}><Image className="w-3.5 h-3.5" /></span>;
  const src = pictureSrc(image);
  if (image.source === 'manual') return <AuthImg src={src} alt={title} title={title} className={box + ''} />;
  return <img data-testid={`picture-${image.source}`} src={src} alt={title} title={title} loading="lazy" className={box} style={style} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />;
};

// The Picture block of the Edit window: what the product shows now and where it
// came from, Upload / Remove for THIS product, and the brand library's buttons.
// Every action is saved the moment it is done (a picture is not part of the form).
const PictureEditor = ({ product, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const fileRef = useRef(null);
  const brandFileRef = useRef(null);
  const image = product.image;
  const brand = product.brandImage ? product.brandImage.brand : (product.brandName || null);
  const brandIsManual = Boolean(product.brandImage && product.brandImage.source === 'manual');
  const brandCanTakeThis = Boolean(brand && image && image.source === 'manual' && image.via === 'sku');

  const run = async (label, fn) => {
    setBusy(true); setError(''); setDone('');
    try {
      await fn();
      setDone(label);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };
  const refresh = async () => {
    const r = await axios.get(`${API_URL}/api/products/${product.id}`);
    onChanged(toProduct(r.data));
  };
  const uploadFor = (file, forBrand) => run(forBrand ? `Saved — every ${brand} product now shows this picture.` : 'Saved — this product now shows your picture.', async () => {
    const form = new FormData();
    form.append('file', file);
    if (forBrand) {
      form.append('brand', brand);
      await axios.post(`${API_URL}/api/brand-images`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refresh();
    } else {
      const r = await axios.post(`${API_URL}/api/products/${product.id}/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChanged(toProduct(r.data));
    }
  });

  return (
    <div data-testid="picture-editor" className="mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
      <div className="flex flex-wrap items-start gap-3">
        <PictureThumb image={image} size={64} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-sm font-medium text-gray-900 dark:text-white">Picture</div>
          <div data-testid="picture-source" className="text-xs text-gray-600 dark:text-gray-400 break-words">
            {pictureSourcePhrase(image)}
            {image && image.width && image.height ? ` · ${image.width} × ${image.height}` : ''}
            {image && image.source === 'manual' && image.uploaded_by ? ` · uploaded by ${image.uploaded_by}` : ''}
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="picture-file" onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) uploadFor(f, false); }} />
            <button type="button" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50">
              <Upload className="w-3.5 h-3.5" /> {image && image.source === 'manual' && image.via === 'sku' ? 'Replace picture' : 'Upload a picture'}
            </button>
            {image && image.source === 'manual' && image.via === 'sku' && (
              <button type="button" disabled={busy} data-testid="picture-remove" onClick={() => run('Removed — the product shows the next picture in line.', async () => { const r = await axios.delete(`${API_URL}/api/products/${product.id}/image`); onChanged(toProduct(r.data)); })} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" /> Remove my picture
              </button>
            )}
            {brandCanTakeThis && (
              <button type="button" disabled={busy} data-testid="picture-brand-use" onClick={() => run(`Saved — every ${brand} product now shows this picture.`, async () => { await axios.post(`${API_URL}/api/brand-images`, { brand, imageId: image.id }); await refresh(); })} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-800 dark:text-amber-300 disabled:opacity-50">
                Use for the whole brand ({brand})
              </button>
            )}
            {brand && !brandCanTakeThis && (
              <>
                <input ref={brandFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="picture-brand-file" onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) uploadFor(f, true); }} />
                <button type="button" disabled={busy} onClick={() => brandFileRef.current && brandFileRef.current.click()} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50">
                  <Upload className="w-3.5 h-3.5" /> {brandIsManual ? `Replace the ${brand} brand picture` : `Upload one for the whole brand (${brand})`}
                </button>
              </>
            )}
            {brandIsManual && (
              <button type="button" disabled={busy} data-testid="picture-brand-remove" onClick={() => run(`Done — ${brand} products are back to the supplier's picture, where there is one.`, async () => { await axios.delete(`${API_URL}/api/brand-images`, { params: { brand } }); await refresh(); })} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50">
                Stop using it for the brand
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 max-w-prose">PNG, JPEG or WebP, up to 2 MB. A picture you upload is saved at once and the supplier's hourly update never replaces it. Customers get the picture through the API; they never see the supplier's address.</p>
          {error && <p data-testid="picture-error" className="text-xs text-red-700 dark:text-red-400">{error}</p>}
          {done && <p data-testid="picture-done" className="text-xs text-green-700 dark:text-green-400">{done}</p>}
        </div>
      </div>
    </div>
  );
};

// Names for the codes, from the browser — so nobody has to know that "DE" is
// Germany or "pt-BR" is Brazilian Portuguese. Falls back to the code itself.
const displayNames = (type) => {
  try { return new Intl.DisplayNames(['en'], { type }); } catch (e) { return null; }
};
const REGION_NAMES = displayNames('region');
const LANGUAGE_NAMES = displayNames('language');
const codeName = (kind, code) => {
  try {
    const name = (kind === 'regions' ? REGION_NAMES : LANGUAGE_NAMES)?.of(code);
    return name && name !== code ? name : code;
  } catch (e) { return code; }
};
const COMMON_LANGUAGE_CODES = 'en es fr de it pt pt-BR nl pl ru uk tr ar zh zh-Hans zh-Hant ja ko sv da no fi cs sk hu ro bg el he hi th vi id ms ca hr sr sl et lv lt es-MX fr-CA'.split(' ');
const EU_COUNTRY_CODES = 'AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE'.split(' ');
const codeOptions = (kind) => (kind === 'regions' ? ISO_COUNTRY_CODES : COMMON_LANGUAGE_CODES);

/**
 * Pick countries or languages BY NAME. `value` is a list of codes (ISO-2
 * countries, or language codes like `en` / `pt-BR`); nobody types one: search
 * by name and click, or paste a list of codes and press Enter.
 */
const CodeListPicker = ({ kind, value, onChange, vendorList = null }) => {
  const [query, setQuery] = useState('');
  const selected = Array.isArray(value) ? value : [];
  const noun = kind === 'regions' ? 'country' : 'language';
  const all = useMemo(() => {
    const codes = [...new Set([...codeOptions(kind), ...selected])];
    return codes.map(code => ({ code, name: codeName(kind, code) })).sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, selected.join(',')]);
  const q = query.trim().toLowerCase();
  const matches = all.filter(o => !selected.includes(o.code) && (q === '' || o.name.toLowerCase().includes(q) || o.code.toLowerCase() === q));
  const add = (codes) => onChange([...new Set([...selected, ...codes])]);
  const remove = (code) => onChange(selected.filter(c => c !== code));
  // The codes a supplier list holds that we recognise (WOCK sends ISO-2 countries).
  const vendorCodes = (kind === 'regions' && Array.isArray(vendorList))
    ? vendorList.map(c => String(c).toUpperCase()).filter(c => ISO_COUNTRY_CODES.includes(c)) : [];

  const onEnter = () => {
    // A pasted list ("ES, FR, DE") adds every code it recognises; otherwise the top match.
    const known = new Map(all.map(o => [o.code.toLowerCase(), o.code]));
    const pasted = query.split(/[\s,;]+/).filter(Boolean).map(t => known.get(t.toLowerCase())).filter(Boolean);
    if (pasted.length > 1) { add(pasted); setQuery(''); return; }
    if (matches.length > 0 && q !== '') { add([matches[0].code]); setQuery(''); }
  };

  return (
    <div data-testid={`picker-${kind}`} className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
          {selected.map(code => (
            <span key={code} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200">
              {codeName(kind, code)} <span className="font-mono opacity-60">{code}</span>
              <button type="button" onClick={() => remove(code)} aria-label={`Remove ${codeName(kind, code)}`} className="p-0.5 rounded-full hover:bg-amber-200 dark:hover:bg-amber-800">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-gray-500 dark:text-gray-400">{selected.length} selected</span>
        {vendorCodes.length > 0 && (
          <button type="button" onClick={() => onChange(vendorCodes)} className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Start from the supplier's list ({vendorCodes.length})
          </button>
        )}
        {kind === 'regions' && (
          <button type="button" onClick={() => add(EU_COUNTRY_CODES)} className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            + All EU countries
          </button>
        )}
        {selected.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="px-2 py-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
            Remove all
          </button>
        )}
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEnter(); } }}
          placeholder={`Search for a ${noun} by name…`}
          aria-label={`Search for a ${noun}`}
          className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        />
      </div>
      <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
        {matches.length === 0 ? (
          <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{q ? `No ${noun} matches "${query}"` : `Every ${noun} is already selected`}</div>
        ) : matches.slice(0, 60).map(o => (
          <button key={o.code} type="button" onClick={() => { add([o.code]); setQuery(''); }} className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left text-gray-800 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-amber-900/20">
            <span>{o.name}</span><span className="font-mono text-xs text-gray-400">{o.code}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// A code list as words, for a line of text: "Spain, France, Germany +24".
const codeListSummary = (kind, codes, max = 4) => {
  if (!Array.isArray(codes)) return '—';
  if (codes.length === 0) return kind === 'regions' ? 'Worldwide (no restriction)' : '—';
  const names = codes.slice(0, max).map(c => codeName(kind, c)).join(', ');
  return codes.length > max ? `${names} +${codes.length - max}` : names;
};

// ---- the Edit window's "Catalogue details" block ---------------------------
// The draft holds, per field: text → a string ('' = no value of ours, publish
// the supplier's); list → { mode: 'vendor' | 'global' | 'choose', list }.
const curatedDraftOf = (product) => {
  const draft = {};
  for (const f of CURATED_FIELDS) {
    const current = product && product.curated ? product.curated[f.key] : null;
    if (f.kind === 'text') draft[f.key] = current || '';
    else if (current == null) draft[f.key] = { mode: 'vendor', list: [] };
    else if (current.length === 0 && f.kind === 'regions') draft[f.key] = { mode: 'global', list: [] };
    else draft[f.key] = { mode: 'choose', list: current };
  }
  return draft;
};
// What the draft means as a value to send: null = use the supplier's.
const curatedDraftValue = (field, d) => {
  if (field.kind === 'text') return d.trim() === '' ? null : d.trim();
  if (d.mode === 'vendor') return null;
  if (d.mode === 'global') return [];
  return d.list.length === 0 ? null : d.list;
};
const sameCurated = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const CuratedFieldRow = ({ field, product, draft, onDraft, marks, onMark, suggestListId }) => {
  const original = product && product.curated ? product.curated[field.key] : null;
  const vendorValue = product && product.vendorValues ? product.vendorValues[field.key] : null;
  const state = product ? provenanceOf(product, field.key) : 'vendor';
  const detail = product ? provenanceDetail(product, field.key) : '';
  // The name a person typed before the restyle run (S9) brought it to the guide — kept on the entry.
  const originalName = field.key === 'name' && product && product.enrichmentMeta && product.enrichmentMeta.name ? product.enrichmentMeta.name.previous_manual_value : null;
  const value = curatedDraftValue(field, draft);
  const changed = !sameCurated(value, original);
  const hasVendor = field.kind === 'text' ? !!vendorValue : Array.isArray(vendorValue) && vendorValue.length > 0;
  const vendorText = field.kind === 'text' ? vendorValue : (field.kind === 'regions' ? codeListSummary('regions', vendorValue) : (vendorValue || []).join(', '));
  const inputId = `curated-${field.key}`;
  const radio = (mode, text) => (
    <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer">
      <input type="radio" name={inputId} checked={draft.mode === mode} onChange={() => onDraft({ ...draft, mode })} className="mt-0.5 text-amber-600 focus:ring-amber-500" />
      <span>{text}</span>
    </label>
  );

  return (
    <div data-testid={`curated-field-${field.key}`} className="py-3 first:pt-0 last:pb-0">
      <div className="flex items-center gap-2 mb-1">
        <label htmlFor={inputId} className="text-sm font-medium text-gray-700 dark:text-gray-300">{field.label}</label>
        {product && <ProvenanceBadge state={changed ? (value == null ? 'kept' : 'manual') : state} detail={changed ? 'after you save' : detail} />}
        {product && !changed && detail && <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{detail}</span>}
      </div>

      {field.kind === 'text' ? (
        <div className="flex gap-2">
          <input
            id={inputId}
            type="text"
            value={draft}
            list={suggestListId || undefined}
            onChange={(e) => onDraft(e.target.value)}
            placeholder={hasVendor ? `Supplier's value: ${vendorText}` : (field.key === 'name' ? 'e.g., Spotify Gift Card 10€ [ES]' : 'No value')}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
          {product && draft.trim() !== '' && (
            <button type="button" onClick={() => onDraft('')} title="Go back to the supplier's value" className="shrink-0 inline-flex items-center gap-1 px-2 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              <RotateCcw className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Use supplier's</span>
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {radio('vendor', hasVendor ? `Use the supplier's list (${vendorText})` : "Use the supplier's list (it has none)")}
          {field.kind === 'regions' && radio('global', 'Worldwide — it can be used in any country')}
          {radio('choose', field.kind === 'regions' ? 'Only in the countries I choose' : 'The languages I choose')}
          {draft.mode === 'choose' && (
            <div className="sm:ml-6">
              <CodeListPicker kind={field.kind} value={draft.list} onChange={(list) => onDraft({ ...draft, list })} vendorList={vendorValue} />
            </div>
          )}
        </div>
      )}

      {field.kind === 'text' && hasVendor && draft.trim() !== '' && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 break-words">Supplier's value: {vendorText}</p>
      )}
      {field.key === 'name' && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This is the product name customers see. Leave it empty to show the vendor description instead.</p>
      )}
      {field.key === 'name' && product && typeof originalName === 'string' && originalName.trim() !== '' && draft.trim() !== originalName.trim() && (
        <p className="mt-1 text-xs text-gray-700 dark:text-gray-300 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="break-words">Your original name was <b>{originalName}</b> (the AI brought it to the style guide).</span>
          <button data-testid="restore-original-name" type="button" onClick={() => onDraft(originalName)} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
            <RotateCcw className="w-3.5 h-3.5" /> Restore my original name
          </button>
        </p>
      )}
      {product && changed && value == null && original != null && (
        <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">When you save, this goes back to the supplier's value and stays that way — the AI will not fill it in.</p>
      )}
      {product && !changed && state === 'ai' && (
        <label className="mt-2 flex items-center gap-2 text-sm text-purple-800 dark:text-purple-300 cursor-pointer">
          <input type="checkbox" checked={!!marks.accept[field.key]} onChange={(e) => onMark('accept', e.target.checked)} className="rounded text-purple-600 focus:ring-purple-500" />
          I have checked this — the AI's value is correct
        </label>
      )}
      {product && !changed && state === 'kept' && (
        <label className="mt-2 flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300 cursor-pointer">
          <input type="checkbox" checked={!!marks.release[field.key]} onChange={(e) => onMark('release', e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500" />
          Let the AI fill this field in again
        </label>
      )}
    </div>
  );
};

// ---- the in-app guide. This IS the user documentation for the curated layer;
// keep it in step with backend/src/enrichment/curated.js when a rule changes.
const CatalogueHelpModal = ({ onClose }) => {
  const Section = ({ title, children }) => (
    <section className="space-y-1.5">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h4>
      <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1.5 max-w-prose">{children}</div>
    </section>
  );
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div data-testid="catalogue-help" role="dialog" aria-label="How catalogue details work" className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">How catalogue details work</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X className="w-5 h-5" /></button>
        </div>

        <Section title="The idea in one minute">
          <p>Every product has eight details customers can see: its <b>name</b>, <b>brand</b>, <b>category</b>, <b>platform</b>, <b>region</b>, <b>language</b>, the list of <b>languages</b> and the list of <b>countries</b> it works in.</p>
          <p>Each detail has two layers. The <b>supplier's value</b> arrives with the supplier's catalogue and is refreshed about every hour. <b>Our value</b> is one we set ourselves — typed by a person, or written by the AI. If we have a value of our own, customers see ours. If not, they see the supplier's.</p>
          <p>The supplier's update never touches our value, so a correction you make stays made.</p>
        </Section>

        <Section title="What the badges mean">
          <ul className="space-y-1.5">
            {PROVENANCE_ORDER.map(state => (
              <li key={state} className="flex items-start gap-2">
                <span className="mt-0.5"><ProvenanceBadge state={state} /></span>
                <span><b>{PROVENANCE[state].label}.</b> {PROVENANCE[state].help}</span>
              </li>
            ))}
          </ul>
          <p>Hover over a badge to see who set the value and when.</p>
        </Section>

        <Section title="Changing one product">
          <p><b>In the table:</b> click a name, brand, category, platform, region or language, type the new value and press Enter. Press Esc to cancel. Clearing the box and pressing Enter puts the field back to the supplier's value.</p>
          <p><b>In the Edit window</b> (the cog at the end of a row): every detail is listed under "Catalogue details". Languages and countries are picked by name from a list — you never need to know a code. For countries you can also choose "Worldwide", or start from the supplier's list and remove a few.</p>
          <p>As you type a brand, category, platform, region or language, values already used in the catalogue are suggested, so the same thing is always spelled the same way.</p>
        </Section>

        <Section title="Changing many products at once">
          <p>Tick the rows you want — one by one, or with <b>Select: Visible</b> (the page you are looking at) or <b>Select: All matching</b> (every product your search and filters match, on every page). Then use the bar that appears above the table:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Mark AI values reviewed</b> — one click: every AI value on the ticked rows becomes "AI, reviewed".</li>
            <li><b>Catalogue details…</b> — choose a detail and set the same value on every ticked row (for example Platform = Steam), put details back to the supplier's value, or let the AI fill them again.</li>
          </ul>
          <p>Nothing is changed on a row the action does not apply to, and you are told how many rows actually changed. If more products are selected than fit on one page, you are asked to confirm first.</p>
          <p><b>In a spreadsheet:</b> the <b>Internal Descriptions</b> menu downloads the products you have filtered as a CSV or Excel file. Beside the name ("Internal Description") it now carries every detail as a pair of columns: <b>Vendor Brand</b> is the supplier's value, for reading only (grey in Excel), and <b>Internal Brand</b> is ours — the same for category, platform, region, language, languages and countries. Type into the "Internal …" columns and import the file back: what you typed is saved as yours; an "Internal …" cell left <b>empty</b> puts that detail back to the supplier's value; a column you delete from the file is left alone on every row. Languages and countries are written as codes separated by commas (<span className="font-mono">en, pt-BR</span> · <span className="font-mono">ES, PT</span>), and <b>Worldwide</b> in "Internal Countries" means the product works everywhere. A row with a code that is not valid is reported and skipped; the rest go in. Importing a file you have not changed changes nothing.</p>
        </Section>

        <Section title="Working with the AI">
          <p>The AI only ever fills a detail that is empty or that it wrote itself. It never overwrites something a person typed.</p>
          <p>When you have read an AI value and it is right, mark it as reviewed. If it is wrong, just type the right one — your correction is kept, and the AI learns from it the next time it runs.</p>
          <p>If you put a detail <b>back to the supplier's value</b>, that is remembered as your decision (<ProvenanceBadge state="kept" />) and the AI leaves it alone. To undo that, tick "Let the AI fill this field in again".</p>
          <p>Use the <b>"AI, not reviewed"</b> choice in the enrichment filter above the table to see only what still needs a look. <b>"Name waiting for you"</b> shows the products whose name the AI proposed but would not apply — customers see the supplier's wording on these until you decide; with the stock filter on "In Stock" that is the list that matters most. <b>"Anything waiting for you"</b> adds the products where only a brand, category or other detail waits. The deciding itself is done on Configuration → Enrichment, under "Waiting for you".</p>
        </Section>

        <Section title="Running the AI (Configuration → Enrichment)">
          <p>A <b>run</b> sends products to the AI in small groups (a gift card with all its amounts, a game with all its editions) so that related products get matching names. The AI answers with a name, the details, and how confident it is in the name.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Fill what is missing</b> names only products that have no name of ours yet. <b>Redo what the AI wrote</b> does every AI value again with the current prompt and your latest corrections. Neither ever touches a value a person typed.</li>
            <li><b>Bring my own names to the style guide</b> is the one exception, made for the names typed before the guide existed: it sends the AI the names a person typed and asks for the same name in the guide's form — the facts in your name stay (where it is valid, what it is, the amounts), only the form changes. A name that already follows the guide is left alone. A changed name is badged <ProvenanceBadge state="ai" /> and <b>your original is kept</b>: hover the badge to see it, and the Edit window has <b>"Restore my original name"</b>. Their empty details are filled on the way. A brand, platform or category you typed is never touched, by this run or any other.</li>
            <li>A name the AI is <b>not confident enough</b> about, one it flags, or one that names a supplier is <b>not written</b> — it goes to the <b>Waiting for you</b> list, where you take, edit or reject it. Taking it writes it as an AI value, marked reviewed. <b>Edit</b> lets you write it your way: your wording is saved as yours and what the AI proposed is kept with it, so the next run learns from the correction (typing the correction on the Products grid records the same thing). Rejecting it means the next run tries again.</li>
            <li>A <b>dry run</b> asks the AI and shows what it would do, but writes nothing to any product. Try one before a real run.</li>
            <li>A real run goes through Anthropic's <b>batch</b> queue at half price — but the queue can take up to 24 hours and a request can expire unanswered (the product then simply waits for the next run). Tick <b>Direct lane</b> for answers in minutes at full price: after the first request a few are sent at a time, and a whole supplier takes a couple of hours. There is no product limit on it — the <b>spend cap</b> is what stops it, so set the cap to what you are willing to spend on that run. A running direct run has a <b>Stop</b> in the history: what is in flight finishes and is applied, nothing more is sent, and the products never sent wait for the next run.</li>
            <li>When a supplier labels a game with the <b>store</b> as its brand (WOCK sends "Microsoft" on Xbox games), the AI is not shown the game database's publisher — the supplier's value stands — and the brand waits for you. Such a waiting row shows <b>"Game database names the publisher: …"</b> for your eyes only: nothing writes it; you type the publisher if you agree.</li>
            <li><b>Game facts from the game database (IGDB)</b>, when its credentials are on the server: for a game key the AI is also shown the official spelling of the title, the publisher (only when the supplier gave none) and what tells editions apart — looked up once per title and kept. <b>The supplier's data always wins where the supplier published it</b>: a supplier's mistake is the supplier's liability and can be claimed back, one taken from a database would be ours — so the supplier's platform, region, countries and languages are never replaced, by the AI or by the database. What the database said is shown beside any value waiting for you.</li>
            <li><b>Details the run may write</b> lets a run touch only some fields — for example, fill the brands of game keys and leave every name alone. The AI still answers everything; the unticked details are simply not written.</li>
            <li><b>Only products in stock</b> leaves out what has a stock count of zero (out-of-stock games); products with no count at all — most gift cards — are always included. Untick it to name everything.</li>
            <li>Every run records how many products it changed, how many wait for you, and what it <b>cost</b> — measured, not estimated. A spend cap is checked before anything is sent.</li>
            <li><b>Every night</b>, at the time set on the tab (Madrid time), the AI runs <b>Fill what is missing</b> by itself: on the direct lane, in-stock products only, under its own spend cap — so the products the suppliers add each day get their names and details without anyone pressing a button. If another run is in flight at that moment it waits and starts when the engine is free, the same day. A night with nothing to name is recorded as such. A night the spend cap refuses, or a run that fails (for example, the API key stopped working), is shown in red on the tab and, when alerts are set up, sent by email — nothing is retried silently. The tab says when the next one is due and what the last one did; untick "Run every night" to stop it.</li>
          </ul>
          <p>The <b>prompt</b> is your instructions and style guide, in your words. Saving it opens a line-by-line comparison and asks you to confirm; every save is a new version and any older version can be restored. <b>Saving changes no product</b> — only a run does. The AI can also <b>propose</b> changes to the prompt from the names you typed over its own; the proposal is shown in the same comparison and is never saved until you confirm it.</p>
          <p>The <b>model</b> list comes straight from Anthropic, so a newer model appears by itself. If the API key stops working, the tab says so in red and no run can start.</p>
        </Section>

        <Section title="Pictures">
          <p>Every product can carry one <b>picture</b> — a brand logo for a gift card or top-up, the cover (box art) for a game. Customers' stores get it through the API. The first column of the table shows it; the Edit window has a <b>Picture</b> block at the top of "Catalogue details".</p>
          <p>Where a product's picture comes from, in order: <b>yours for this product</b> (uploaded in the Edit window) · <b>yours for the whole brand</b> ("Use for the whole brand" — one upload covers every amount and every supplier of that brand) · <b>the supplier's</b> (two suppliers send pictures; one sends none. Only Bamboo's brand logos are shared across suppliers — a Bamboo logo also pictures the same brand's cards from ePay, Ubiqfy or WOCK; Ubiqfy's pictures are card artwork drawn for Brazil and stay on Ubiqfy's own products) · <b>the game database's cover</b>, for a game key, once that is switched on (Configuration → Enrichment) · none.</p>
          <p>A picture you upload is saved at once — it is not part of the Save button — and the supplier's hourly update never replaces it. "Remove my picture" puts the product back to the next one in line. PNG, JPEG or WebP, up to 2 MB.</p>
          <p>Customers never see where a picture came from: the API copies the file and hands it out under its own name.</p>
        </Section>

        <Section title="Good to know">
          <ul className="list-disc pl-5 space-y-1">
            <li>A change reaches the customer portal in about ten minutes.</li>
            <li>"Worldwide" and "no country list" are different: Worldwide says the product works everywhere; an empty supplier list just means the supplier did not tell us.</li>
            <li>The "Vendor Description" is the supplier's own name for the product. It is overwritten by the supplier's update — put your wording in <b>Name</b> instead.</li>
          </ul>
        </Section>

        <div className="flex justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg text-sm">Got it</button>
        </div>
      </div>
    </div>
  );
};

// ---- "Catalogue details…" for the ticked rows ------------------------------
const BULK_CURATED_ACTIONS = [
  { key: 'set', label: 'Set a value', help: 'Give every ticked product the same value for one detail.' },
  { key: 'accept', label: 'Mark AI values as reviewed', help: 'You have read them and they are right. Only values the AI wrote are affected.' },
  { key: 'revert', label: "Put back to the supplier's value", help: "Removes our value. The AI will not fill these details in again unless you allow it." },
  { key: 'release', label: 'Let the AI fill them again', help: "Lifts a \"kept as supplier's\" decision, so the next AI run may write a value." },
];

const BulkCuratedModal = ({ count, productIds, onClose, onDone }) => {
  const [action, setAction] = useState('set');
  const [fields, setFields] = useState([]);
  const [setField, setSetField] = useState('brand');
  const [text, setText] = useState('');
  const [listDraft, setListDraft] = useState({ mode: 'choose', list: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const settable = CURATED_FIELDS.filter(f => f.key !== 'name');
  const field = CURATED_FIELD[setField];
  const toggleField = (key) => setFields(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const allChosen = fields.length === CURATED_FIELDS.length;

  const value = field.kind === 'text' ? (text.trim() || null) : (listDraft.mode === 'global' ? [] : (listDraft.list.length ? listDraft.list : null));
  const ready = action === 'set' ? value !== null : fields.length > 0;

  const apply = async () => {
    setError('');
    if (count > 50 && !window.confirm(`Change ${count.toLocaleString()} products?\n\nThat is more than you can see on one page.`)) return;
    setBusy(true);
    try {
      const body = action === 'set'
        ? { productIds, action, fields: [setField], value }
        : { productIds, action, fields };
      const response = await axios.post(`${API_URL}/api/products/bulk-curated`, body);
      onDone(response.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div data-testid="bulk-curated-modal" className="bg-white dark:bg-gray-800 rounded-lg max-w-xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto shadow-xl space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Catalogue details for {count} product{count > 1 ? 's' : ''}</h3>
        {error && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">{error}</div>}

        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">What do you want to do?</p>
          {BULK_CURATED_ACTIONS.map(a => (
            <label key={a.key} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${action === a.key ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
              <input type="radio" name="bulk-curated-action" checked={action === a.key} onChange={() => setAction(a.key)} className="mt-1 text-amber-600 focus:ring-amber-500" />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-white">{a.label}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{a.help}</span>
              </span>
            </label>
          ))}
        </div>

        {action === 'set' ? (
          <div className="space-y-3">
            <div>
              <label htmlFor="bulk-curated-field" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Which detail?</label>
              <select id="bulk-curated-field" value={setField} onChange={(e) => setSetField(e.target.value)} className="w-full sm:w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                {settable.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="bulk-curated-value" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New value</label>
              {field.kind === 'text' ? (
                <input id="bulk-curated-value" type="text" value={text} list={`curated-suggest-${field.key}`} onChange={(e) => setText(e.target.value)} placeholder={`Type the ${field.label.toLowerCase()}`} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              ) : (
                <div className="space-y-2">
                  {field.kind === 'regions' && (
                    <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer">
                      <input type="checkbox" checked={listDraft.mode === 'global'} onChange={(e) => setListDraft({ ...listDraft, mode: e.target.checked ? 'global' : 'choose' })} className="rounded text-amber-600 focus:ring-amber-500" />
                      Worldwide — they can be used in any country
                    </label>
                  )}
                  {listDraft.mode !== 'global' && <CodeListPicker kind={field.kind} value={listDraft.list} onChange={(list) => setListDraft({ ...listDraft, list })} />}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Which details?</p>
              <button type="button" onClick={() => setFields(allChosen ? [] : CURATED_FIELDS.map(f => f.key))} className="text-xs text-amber-700 dark:text-amber-400 hover:underline">
                {allChosen ? 'Untick all' : 'Tick all'}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {CURATED_FIELDS.map(f => (
                <label key={f.key} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer">
                  <input type="checkbox" checked={fields.includes(f.key)} onChange={() => toggleField(f.key)} className="rounded text-amber-600 focus:ring-amber-500" />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">Cancel</button>
          <button type="button" onClick={apply} disabled={!ready || busy} className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium">
            {busy ? 'Applying…' : `Apply to ${count} product${count > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const getDefaultVisibleColumns = () => PRODUCT_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);

const loadVisibleColumns = () => {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) { /* ignore */ }
  return getDefaultVisibleColumns();
};

const Products = ({ products, setProducts }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [vendorFilter, setVendorFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [enrichmentFilter, setEnrichmentFilter] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [showBulkSkuModal, setShowBulkSkuModal] = useState(false);
  const [bulkSku, setBulkSku] = useState('');
  const [bulkSkuBusy, setBulkSkuBusy] = useState(false);
  const exportMenuRef = useRef(null);
  const [showInternalDescMenu, setShowInternalDescMenu] = useState(false);
  const [internalDescBusy, setInternalDescBusy] = useState(false);
  const internalDescMenuRef = useRef(null);
  const internalDescFileInputRef = useRef(null);
  const [syncingCatalog, setSyncingCatalog] = useState(false);
  const [syncingUbiqfyCatalog, setSyncingUbiqfyCatalog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef(null);
  const [bulkPriceTooltip, setBulkPriceTooltip] = useState(null);
  const [showBulkCuratedModal, setShowBulkCuratedModal] = useState(false);
  const [bulkCuratedBusy, setBulkCuratedBusy] = useState(false);
  const [showCatalogueHelp, setShowCatalogueHelp] = useState(false);

  // Close column picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target)) {
        setShowColumnPicker(false);
      }
    };
    if (showColumnPicker) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnPicker]);

  const toggleColumn = (key) => {
    setVisibleColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const resetColumns = () => {
    const defaults = getDefaultVisibleColumns();
    setVisibleColumns(defaults);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(defaults));
  };

  const colVisible = (key) => visibleColumns.includes(key);
  const visibleColCount = visibleColumns.length;

  // Close export menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // Close internal descriptions menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (internalDescMenuRef.current && !internalDescMenuRef.current.contains(e.target)) {
        setShowInternalDescMenu(false);
      }
    };
    if (showInternalDescMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showInternalDescMenu]);

  const handleExportProducts = async (format) => {
    setShowExportMenu(false);
    setIsExporting(true);
    try {
      const endpoint = format === 'csv' ? '/api/products/export/csv' : '/api/products/export/excel';
      const response = await axios.post(`${API_URL}${endpoint}`, {
        vendor: vendorFilter,
        currency: currencyFilter,
        type: typeFilter,
        showInactive,
        stockFilter,
        search: searchTerm
      });
      const { downloadUrl, recordCount, filename } = response.data;
      const dlResponse = await axios.get(`${API_URL}${downloadUrl}`, { responseType: 'blob' });
      const blob = new Blob([dlResponse.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      alert(`Exported ${recordCount} products`);
    } catch (error) {
      alert('Export failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadInternalDescTemplate = async (format) => {
    setShowInternalDescMenu(false);
    setInternalDescBusy(true);
    try {
      const endpoint = format === 'csv'
        ? '/api/products/internal-descriptions/export/csv'
        : '/api/products/internal-descriptions/export/excel';
      const response = await axios.post(`${API_URL}${endpoint}`, {
        vendor: vendorFilter,
        currency: currencyFilter,
        type: typeFilter,
        showInactive,
        stockFilter,
        search: searchTerm
      });
      const { downloadUrl, recordCount, filename } = response.data;
      const dlResponse = await axios.get(`${API_URL}${downloadUrl}`, { responseType: 'blob' });
      const blob = new Blob([dlResponse.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      alert(`Downloaded template with ${recordCount} products`);
    } catch (error) {
      alert('Download failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setInternalDescBusy(false);
    }
  };

  const handleImportInternalDescriptions = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setShowInternalDescMenu(false);
    setInternalDescBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/api/products/internal-descriptions/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { updated, skipped, errors, columns } = response.data;
      const columnWords = { name: 'name', brand: 'brand', category: 'category', platform: 'platform', region: 'region', language: 'language', languages: 'languages', regions: 'countries' };
      let message = `Import complete: ${updated} updated`;
      if (Array.isArray(columns) && columns.length) message += ` (${columns.map(c => columnWords[c] || c).join(', ')})`;
      if (skipped > 0) message += `, ${skipped} skipped`;
      if (errors && errors.length > 0) {
        const preview = errors.slice(0, 5).map(err => `Row ${err.row} (${err.vendorSku}): ${err.error}`).join('\n');
        message += `\n\nErrors:\n${preview}`;
        if (errors.length > 5) message += `\n... and ${errors.length - 5} more`;
      }
      alert(message);
      // Products will be refreshed via the broadcast `products:updated` socket event
    } catch (error) {
      alert('Import failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setInternalDescBusy(false);
      // Reset input so the same file can be selected again
      if (internalDescFileInputRef.current) internalDescFileInputRef.current.value = '';
    }
  };

  const handleSyncBambooCatalog = async () => {
    setSyncingCatalog(true);
    try {
      const response = await axios.post(`${API_URL}/api/bamboo/catalog/sync`);
      alert(`Bamboo catalog synced: ${response.data.synced || 0} products`);
    } catch (error) {
      alert('Catalog sync failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setSyncingCatalog(false);
    }
  };

  const handleSyncUbiqfyCatalog = async () => {
    setSyncingUbiqfyCatalog(true);
    try {
      const response = await axios.post(`${API_URL}/api/ubiqfy/catalog/sync`);
      alert(`Ubiqfy catalog synced: ${response.data.synced || 0} products`);
    } catch (error) {
      alert('Ubiqfy catalog sync failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setSyncingUbiqfyCatalog(false);
    }
  };

  const uniqueCurrencies = [...new Set(products.map(p => p.currencyCode).filter(Boolean))].sort();
  // Derived from the whole loaded catalog, not from the visible page: this
  // screen loads every product (`/api/products?includeInactive=true`), so these
  // lists are complete. Do not copy the pattern anywhere that pages.
  const uniquePlatforms = [...new Set(products.map(p => p.platform).filter(Boolean))].sort();
  const uniqueRegions = [...new Set(products.map(p => p.region).filter(Boolean))].sort();

  // Values already in use, offered as suggestions while typing a curated text
  // field so one brand is not spelled three ways. Complete for the same reason
  // as the lists above; capped because a <datalist> of thousands is slow.
  const curatedSuggestions = useMemo(() => {
    const out = {};
    for (const f of CURATED_FIELDS) {
      if (f.kind !== 'text' || f.key === 'name') continue;
      out[f.key] = [...new Set(products.map(p => p[f.column]).filter(Boolean))].sort().slice(0, 3000);
    }
    return out;
  }, [products]);
  const hasProvenance = (p, state) => CURATED_FIELDS.some(f => provenanceOf(p, f.key) === state);

  // Filter products
  const filteredProducts = products.filter(p => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' ||
      (p.sku || '').toLowerCase().includes(q) ||
      (p.vendorSku || '').toLowerCase().includes(q) ||
      p.name.toLowerCase().includes(q) ||
      (p.internalDescription || '').toLowerCase().includes(q) ||
      // The metadata columns are searchable, not only filterable — owner-stated
      // 2026-09-05. Typing "Steam" used to work only because platform is glued
      // into the WOCK product name; now it matches the column itself.
      (p.brandName || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q) ||
      (p.platform || '').toLowerCase().includes(q) ||
      (p.region || '').toLowerCase().includes(q) ||
      (p.language || '').toLowerCase().includes(q) ||
      p.ean.includes(searchTerm);
    const matchesActive = showInactive || p.active;
    const matchesVendor = vendorFilter === 'all' || p.vendor === vendorFilter;
    const matchesPlatform = platformFilter === 'all' || p.platform === platformFilter;
    const matchesRegion = regionFilter === 'all' || p.region === regionFilter;
    const matchesCurrency = currencyFilter === 'all' || p.currencyCode === currencyFilter;
    const matchesType = typeFilter === 'all' ||
      (typeFilter === 'variable' ? p.isVariableAmount : !p.isVariableAmount);
    const matchesStock = stockFilter === 'all' ||
      (stockFilter === 'in_stock' && p.stockQuantity !== 0) ||
      (stockFilter === 'out_of_stock' && p.stockQuantity === 0);
    const matchesEnrichment = enrichmentFilter === 'all' ||
      (enrichmentFilter === 'no_description' && !p.internalDescription) ||
      (enrichmentFilter === 'ai_unreviewed' && hasProvenance(p, 'ai')) ||
      (enrichmentFilter === 'hand_edited' && hasProvenance(p, 'manual')) ||
      (enrichmentFilter === 'kept_vendor' && hasProvenance(p, 'kept')) ||
      (enrichmentFilter === 'name_waiting' && p.waiting.includes('name')) ||
      (enrichmentFilter === 'any_waiting' && p.waiting.length > 0) ||
      (enrichmentFilter === 'no_sku' && !p.sku);
    return matchesSearch && matchesActive && matchesVendor && matchesPlatform && matchesRegion && matchesCurrency && matchesType && matchesStock && matchesEnrichment;
  });

  // Sort and paginate
  const sortedProducts = [...filteredProducts].sort((a, b) => (a.sku || a.vendorSku || '').localeCompare(b.sku || b.vendorSku || ''));
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const paginatedProducts = sortedProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Since "Select all" can tick the whole catalogue (owner, 2026-10-05), every
  // bulk action on more than one page of products asks first. One page (50) or
  // fewer is what a person can see, so it goes straight through as before.
  const confirmBulk = (what) => selectedProducts.size <= itemsPerPage ||
    window.confirm(`${what} on ${selectedProducts.size.toLocaleString()} products?\n\nThat is more than the ${itemsPerPage} you can see on one page.`);

  // Bulk SKU assignment handler
  const handleBulkSkuAssign = async () => {
    if (!bulkSku.trim()) return;
    if (!confirmBulk(`Assign ERP SKU "${bulkSku.trim()}"`)) return;
    setBulkSkuBusy(true);
    try {
      const response = await axios.post(`${API_URL}/api/products/bulk-erp-sku`, {
        productIds: Array.from(selectedProducts),
        erpSku: bulkSku.trim()
      });
      alert(`ERP SKU "${response.data.erpSku}" assigned to ${response.data.updated} product${response.data.updated > 1 ? 's' : ''}`);
      setProducts(prev => prev.map(p =>
        selectedProducts.has(p.id) ? { ...p, erpSku: response.data.erpSku } : p
      ));
      setSelectedProducts(new Set());
      setShowBulkSkuModal(false);
      setBulkSku('');
    } catch (error) {
      alert('Bulk ERP SKU assignment failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setBulkSkuBusy(false);
    }
  };

  const handleBulkActivate = async (active) => {
    if (!confirmBulk(active ? 'Activate' : 'Deactivate')) return;
    try {
      const response = await axios.post(`${API_URL}/api/products/bulk-activate`, {
        productIds: Array.from(selectedProducts),
        active
      });
      setProducts(prev => prev.map(p =>
        selectedProducts.has(p.id) ? { ...p, active } : p
      ));
      setSelectedProducts(new Set());
    } catch (error) {
      alert('Bulk activate failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleBulkApiEnable = async (enable) => {
    if (!confirmBulk(enable ? 'Enable the API' : 'Disable the API')) return;
    try {
      const response = await axios.post(`${API_URL}/api/products/bulk-api-enable`, {
        productIds: Array.from(selectedProducts),
        apiEnabled: enable
      });
      setProducts(prev => prev.map(p =>
        selectedProducts.has(p.id) ? { ...p, apiEnabled: enable } : p
      ));
      setSelectedProducts(new Set());
    } catch (error) {
      alert('Bulk API update failed: ' + (error.response?.data?.error || error.message));
    }
  };

  // Inline-edit helper used by EditableTextCell. Sends a single-field PUT and
  // replaces the row with the one the backend returns (`toProduct` is the one
  // transform for a `products` row), so the provenance badge moves with the value.
  const updateProductField = async (productId, field, newValue) => {
    const response = await axios.put(`${API_URL}/api/products/${productId}`, { [field]: newValue });
    const updated = toProduct(response.data);
    setProducts(prev => prev.map(p => p.id === productId ? updated : p));
  };

  // The curated layer for the ticked rows. `data.products` are the fresh rows.
  const applyBulkCuratedResult = (data, what) => {
    // A large selection comes back without rows; the `products:updated` reload repaints it.
    const fresh = new Map((data.products || []).map(row => [row.id, toProduct(row)]));
    if (fresh.size > 0) setProducts(prev => prev.map(p => fresh.get(p.id) || p));
    const untouched = data.requested - data.updated;
    alert(`${what}: ${data.updated} product${data.updated === 1 ? '' : 's'} changed` +
      (untouched > 0 ? `, ${untouched} had nothing to change.` : '.'));
  };

  const handleBulkAcceptAi = async () => {
    if (!confirmBulk('Mark every AI value as reviewed')) return;
    setBulkCuratedBusy(true);
    try {
      const response = await axios.post(`${API_URL}/api/products/bulk-curated`, {
        productIds: Array.from(selectedProducts),
        action: 'accept',
        fields: CURATED_FIELDS.map(f => f.key)
      });
      applyBulkCuratedResult(response.data, 'AI values marked as reviewed');
    } catch (error) {
      alert('Could not mark as reviewed: ' + (error.response?.data?.error || error.message));
    } finally {
      setBulkCuratedBusy(false);
    }
  };

  // One table cell of the curated layer: where the value came from, then the
  // value. Text fields edit in place and write OUR value (never the vendor
  // column, which the next sync would overwrite); the two lists are edited in
  // the Edit window, where they can be picked by name.
  const curatedCell = (product, key) => {
    const field = CURATED_FIELD[key];
    const badge = <ProvenanceBadge state={provenanceOf(product, key)} detail={provenanceDetail(product, key)} />;
    if (field.kind !== 'text') {
      const codes = product[field.column];
      return (
        <span className="inline-flex items-center gap-1.5">
          {badge}
          {key === 'regions' && Array.isArray(codes) && codes.length === 0 && product.curated.regions != null
            ? <span className="whitespace-nowrap">Worldwide</span>
            : codeListCell(codes)}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 max-w-full">
        {badge}
        <EditableTextCell
          value={product[field.column]}
          placeholder="—"
          listId={key === 'name' ? null : `curated-suggest-${key}`}
          ariaLabel={`${field.label} for ${product.name || product.vendorSku || 'product'}`}
          onSave={(newValue) => updateProductField(product.id, field.body, newValue)}
        />
      </span>
    );
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Are you sure you want to deactivate "${product.sku || product.vendorSku}"?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/products/${product.id}`);
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, active: false } : p));
    } catch (error) {
      alert('Failed to deactivate product: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleReactivate = async (product) => {
    try {
      const response = await axios.put(`${API_URL}/api/products/${product.id}`, { active: true });
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, active: true } : p));
    } catch (error) {
      alert('Failed to reactivate product: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Product Management</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => downloadTemplate('csv')}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Download className="w-4 h-4" />
            CSV Template
          </button>
          <button
            onClick={() => downloadTemplate('xlsx')}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Download className="w-4 h-4" />
            XLSX Template
          </button>
          <button
            onClick={handleSyncBambooCatalog}
            disabled={syncingCatalog}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncingCatalog ? 'animate-spin' : ''}`} />
            {syncingCatalog ? 'Syncing...' : 'Sync Bamboo'}
          </button>
          <button
            onClick={handleSyncUbiqfyCatalog}
            disabled={syncingUbiqfyCatalog}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncingUbiqfyCatalog ? 'animate-spin' : ''}`} />
            {syncingUbiqfyCatalog ? 'Syncing...' : 'Sync Ubiqfy'}
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600"
          >
            <FileText className="w-4 h-4" />
            Bulk Upload
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search SKU, name, EAN, brand, category, platform, region or language..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className={`w-full pl-10 pr-3 py-2 border ${searchTerm ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <div className="flex flex-wrap gap-3">
          <select
            value={vendorFilter}
            onChange={(e) => { setVendorFilter(e.target.value); setCurrentPage(1); }}
            className={`px-3 py-2 border ${vendorFilter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          >
            <option value="all">All Vendors</option>
            <option value="epay">ePay</option>
            <option value="bamboo">Bamboo</option>
            <option value="ubiqfy">Ubiqfy</option>
            <option value="wock">WOCK</option>
          </select>
          <select
            value={currencyFilter}
            onChange={(e) => { setCurrencyFilter(e.target.value); setCurrentPage(1); }}
            className={`px-3 py-2 border ${currencyFilter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          >
            <option value="all">All Currencies</option>
            {uniqueCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {/* Only rendered when some product actually has one, so the ePay-only
              view is not cluttered with two permanently-empty dropdowns. */}
          {uniquePlatforms.length > 0 && (
            <select
              value={platformFilter}
              onChange={(e) => { setPlatformFilter(e.target.value); setCurrentPage(1); }}
              className={`px-3 py-2 border ${platformFilter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
            >
              <option value="all">All Platforms</option>
              {uniquePlatforms.map(pl => <option key={pl} value={pl}>{pl}</option>)}
            </select>
          )}
          {uniqueRegions.length > 0 && (
            <select
              value={regionFilter}
              onChange={(e) => { setRegionFilter(e.target.value); setCurrentPage(1); }}
              className={`px-3 py-2 border ${regionFilter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
            >
              <option value="all">All Regions</option>
              {uniqueRegions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
            className={`px-3 py-2 border ${typeFilter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          >
            <option value="all">All Types</option>
            <option value="fixed">Fixed</option>
            <option value="variable">Variable</option>
          </select>
          <select
            value={stockFilter}
            onChange={(e) => { setStockFilter(e.target.value); setCurrentPage(1); }}
            className={`px-3 py-2 border ${stockFilter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          >
            <option value="all">All Stock</option>
            <option value="in_stock">In Stock</option>
            <option value="out_of_stock">Out of Stock</option>
          </select>
          <select
            value={enrichmentFilter}
            onChange={(e) => { setEnrichmentFilter(e.target.value); setCurrentPage(1); }}
            className={`px-3 py-2 border ${enrichmentFilter !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
          >
            <option value="all">All Enrichment</option>
            <option value="no_description">No Description</option>
            <option value="ai_unreviewed">AI, not reviewed</option>
            <option value="hand_edited">Edited by hand</option>
            <option value="kept_vendor">Kept as supplier's</option>
            <option value="name_waiting">Name waiting for you</option>
            <option value="any_waiting">Anything waiting for you</option>
            <option value="no_sku">No SKU</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => { setShowInactive(e.target.checked); setCurrentPage(1); }}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Show inactive
          </label>
          {(searchTerm || vendorFilter !== 'all' || currencyFilter !== 'all' || typeFilter !== 'all' || stockFilter !== 'all' || enrichmentFilter !== 'all' || platformFilter !== 'all' || regionFilter !== 'all' || showInactive) && (
            <button
              onClick={() => { setSearchTerm(''); setVendorFilter('all'); setCurrencyFilter('all'); setTypeFilter('all'); setStockFilter('all'); setEnrichmentFilter('all'); setPlatformFilter('all'); setRegionFilter('all'); setShowInactive(false); setCurrentPage(1); }}
              className="flex items-center gap-1 px-2 py-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Clear all filters"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
          <div className="relative ml-auto" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={isExporting}
              className="flex items-center gap-1 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              title="Export products"
            >
              {isExporting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export
            </button>
            {showExportMenu && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1">
                <button onClick={() => handleExportProducts('csv')} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                  Export as CSV
                </button>
                <button onClick={() => handleExportProducts('xlsx')} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                  Export as Excel
                </button>
              </div>
            )}
          </div>
          <div className="relative" ref={internalDescMenuRef}>
            <button
              onClick={() => setShowInternalDescMenu(!showInternalDescMenu)}
              disabled={internalDescBusy}
              className="flex items-center gap-1 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              title="Bulk edit internal descriptions"
            >
              {internalDescBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />} Internal Descriptions
              <ChevronDown className="w-3 h-3" />
            </button>
            {showInternalDescMenu && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1">
                <button onClick={() => handleDownloadInternalDescTemplate('csv')} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                  <Download className="w-3.5 h-3.5" /> Download template (CSV)
                </button>
                <button onClick={() => handleDownloadInternalDescTemplate('xlsx')} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                  <Download className="w-3.5 h-3.5" /> Download template (XLSX)
                </button>
                <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                <button onClick={() => internalDescFileInputRef.current?.click()} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                  <Upload className="w-3.5 h-3.5" /> Import file…
                </button>
              </div>
            )}
            <input
              ref={internalDescFileInputRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={handleImportInternalDescriptions}
              className="hidden"
            />
          </div>
          <div className="relative" ref={columnPickerRef}>
            <button
              onClick={() => setShowColumnPicker(!showColumnPicker)}
              className="flex items-center gap-1 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Toggle columns"
            >
              <Columns className="w-3.5 h-3.5" /> Columns
            </button>
            {showColumnPicker && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-2">
                {PRODUCT_COLUMNS.map(col => (
                  <label key={col.key} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(col.key)}
                      onChange={() => toggleColumn(col.key)}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    {col.label}
                  </label>
                ))}
                <div className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1 px-3">
                  <button onClick={resetColumns} className="text-xs text-amber-600 dark:text-amber-400 hover:underline">Reset to defaults</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedProducts.size > 0 && (
        <div data-testid="bulk-bar" className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-3 flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
            {selectedProducts.size} product{selectedProducts.size > 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => setShowBulkSkuModal(true)}
            className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium"
          >
            Assign ERP SKU
          </button>
          <button
            onClick={() => handleBulkApiEnable(true)}
            className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
          >
            Enable API
          </button>
          <button
            onClick={() => handleBulkApiEnable(false)}
            className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
          >
            Disable API
          </button>
          <span className="border-l border-amber-300 dark:border-amber-600 h-5"></span>
          <button
            onClick={() => handleBulkActivate(true)}
            className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Activate
          </button>
          <button
            onClick={() => handleBulkActivate(false)}
            className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium"
          >
            Deactivate
          </button>
          <span className="border-l border-amber-300 dark:border-amber-600 h-5"></span>
          <button
            onClick={handleBulkAcceptAi}
            disabled={bulkCuratedBusy}
            title="Every AI value on the ticked rows becomes 'AI, reviewed'"
            className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
          >
            {bulkCuratedBusy ? 'Marking…' : 'Mark AI values reviewed'}
          </button>
          <button
            onClick={() => setShowBulkCuratedModal(true)}
            className="px-3 py-1.5 text-xs bg-white dark:bg-gray-700 border border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-300 rounded-lg hover:bg-amber-100 dark:hover:bg-gray-600 font-medium"
          >
            Catalogue details…
          </button>
          <button
            onClick={() => setSelectedProducts(new Set())}
            className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Clear selection
          </button>
        </div>
      )}

      {showBulkCuratedModal && (
        <BulkCuratedModal
          count={selectedProducts.size}
          productIds={Array.from(selectedProducts)}
          onClose={() => setShowBulkCuratedModal(false)}
          onDone={(data) => {
            setShowBulkCuratedModal(false);
            applyBulkCuratedResult(data, 'Catalogue details');
          }}
        />
      )}
      {showCatalogueHelp && <CatalogueHelpModal onClose={() => setShowCatalogueHelp(false)} />}
      {Object.entries(curatedSuggestions).map(([key, values]) => (
        <datalist key={key} id={`curated-suggest-${key}`}>
          {values.map(v => <option key={v} value={v} />)}
        </datalist>
      ))}

      {/* Bulk SKU Assignment Modal */}
      {showBulkSkuModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Assign ERP SKU to {selectedProducts.size} product{selectedProducts.size > 1 ? 's' : ''}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              This links selected products under a shared internal ERP/billing reference.
            </p>
            <input
              type="text"
              value={bulkSku}
              onChange={(e) => setBulkSku(e.target.value)}
              placeholder="Enter ERP SKU (e.g., 106982)"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && bulkSku.trim()) {
                  handleBulkSkuAssign();
                }
              }}
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowBulkSkuModal(false); setBulkSku(''); }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                disabled={bulkSkuBusy}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkSkuAssign}
                disabled={!bulkSku.trim() || bulkSkuBusy}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium"
              >
                {bulkSkuBusy ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The key to the provenance badges in the table below */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
        <ProvenanceLegend onHelp={() => setShowCatalogueHelp(true)} />
      </div>

      {/* Products Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Selecting rows: the page you can see, or everything the filters match. */}
        <div data-testid="select-bar" className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
          <span className="font-medium text-gray-700 dark:text-gray-300">Select:</span>
          <button
            type="button"
            disabled={paginatedProducts.length === 0}
            onClick={() => setSelectedProducts(new Set([...selectedProducts, ...paginatedProducts.map(p => p.id)]))}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Visible ({paginatedProducts.length})
          </button>
          <button
            type="button"
            disabled={sortedProducts.length === 0}
            onClick={() => setSelectedProducts(new Set(sortedProducts.map(p => p.id)))}
            title="Every product the search and filters above match, on every page"
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            All {sortedProducts.length.toLocaleString()} matching
          </button>
          {selectedProducts.size > 0 && (
            <>
              <button type="button" onClick={() => setSelectedProducts(new Set())} className="px-2 py-1 hover:text-gray-900 dark:hover:text-gray-100">None</button>
              <span className="font-medium text-amber-800 dark:text-amber-300">{selectedProducts.size.toLocaleString()} selected</span>
            </>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={paginatedProducts.length > 0 && paginatedProducts.every(p => selectedProducts.has(p.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedProducts(new Set([...selectedProducts, ...paginatedProducts.map(p => p.id)]));
                      } else {
                        const pageIds = new Set(paginatedProducts.map(p => p.id));
                        setSelectedProducts(new Set([...selectedProducts].filter(id => !pageIds.has(id))));
                      }
                    }}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                </th>
                {colVisible('picture') && <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase w-10"><span className="sr-only">Picture</span><Image className="w-3.5 h-3.5" aria-hidden="true" /></th>}
                {colVisible('sku') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer SKU</th>}
                {colVisible('erpSku') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ERP SKU</th>}
                {colVisible('vendorSku') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor ID</th>}
                {colVisible('vendor') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>}
                {colVisible('ean') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">EAN</th>}
                {colVisible('internalDescription') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Internal Description</th>}
                {colVisible('description') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor Description</th>}
                {colVisible('denomination') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Denomination</th>}
                {colVisible('brandName') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>}
                {colVisible('category') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>}
                {colVisible('platform') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>}
                {colVisible('region') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Region</th>}
                {colVisible('language') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Language</th>}
                {colVisible('languages') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Languages</th>}
                {colVisible('regions') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Countries</th>}
                {colVisible('stock') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>}
                {colVisible('cost') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Our Cost</th>}
                {colVisible('discount') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>}
                {colVisible('status') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>}
                {colVisible('api') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">API</th>}
                {colVisible('actions') && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedProducts.length === 0 ? (
                <tr>
                  <td colSpan={visibleColCount + 1} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {products.length === 0 ? 'No products yet. Add your first product!' : 'No products match your search'}
                  </td>
                </tr>
              ) : (
                paginatedProducts.map(product => (
                  <tr key={product.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${!product.active ? 'opacity-50' : ''} ${selectedProducts.has(product.id) ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                    <td className="px-2 py-1.5 w-8">
                      <input
                        type="checkbox"
                        checked={selectedProducts.has(product.id)}
                        onChange={(e) => {
                          const next = new Set(selectedProducts);
                          if (e.target.checked) next.add(product.id);
                          else next.delete(product.id);
                          setSelectedProducts(next);
                        }}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                    </td>
                    {colVisible('picture') && <td className="px-2 py-1 w-10"><PictureThumb image={product.image} size={32} /></td>}
                    {colVisible('sku') && <td className="px-3 py-1.5 text-sm font-medium text-gray-900 dark:text-white">
                      {product.vendor !== 'epay' ? (
                        <span className="font-mono">{product.sku || '—'}</span>
                      ) : (
                        <EditableTextCell
                          value={product.sku}
                          placeholder="Not set"
                          ariaLabel={`SKU for ${product.name || product.vendorSku || 'product'}`}
                          onSave={(newValue) => updateProductField(product.id, 'sku', newValue)}
                        />
                      )}
                      {product.isVariableAmount && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 rounded-full">Variable</span>
                      )}
                    </td>}
                    {colVisible('erpSku') && <td className="px-3 py-1.5 text-sm font-mono text-gray-500 dark:text-gray-400">
                      {product.erpSku || '—'}
                    </td>}
                    {colVisible('vendorSku') && <td className="px-3 py-1.5 text-sm font-mono text-gray-500 dark:text-gray-400">
                      {product.vendorSku || '—'}
                    </td>}
                    {colVisible('vendor') && <td className="px-3 py-1.5">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                        product.vendor === 'bamboo'
                          ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                          : product.vendor === 'ubiqfy'
                            ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400'
                            : product.vendor === 'wock'
                              ? 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-400'
                              : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                      }`}>
                        {product.vendor === 'bamboo' ? 'Bamboo' : product.vendor === 'ubiqfy' ? 'Ubiqfy' : product.vendor === 'wock' ? 'WOCK' : 'ePay'}
                      </span>
                    </td>}
                    {colVisible('ean') && <td className="px-3 py-1.5 text-sm font-mono text-gray-600 dark:text-gray-400">{product.ean}</td>}
                    {colVisible('internalDescription') && <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-white">
                      {curatedCell(product, 'name')}
                    </td>}
                    {colVisible('description') && <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-white">{product.name}</td>}
                    {colVisible('denomination') && <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-white">
                      {product.isVariableAmount ? (
                        <span className="text-purple-600 dark:text-purple-400">{currencySymbol(product.currencyCode)}{(product.minAmountMinor / 100).toFixed(2)} - {currencySymbol(product.currencyCode)}{(product.maxAmountMinor / 100).toFixed(2)}</span>
                      ) : (
                        `${currencySymbol(product.currencyCode)}${(product.faceValue / 100).toFixed(2)}`
                      )}
                    </td>}
                    {colVisible('brandName') && <td className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300">{curatedCell(product, 'brand')}</td>}
                    {colVisible('category') && <td className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300">{curatedCell(product, 'category')}</td>}
                    {colVisible('platform') && <td className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{curatedCell(product, 'platform')}</td>}
                    {colVisible('region') && <td className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{curatedCell(product, 'region')}</td>}
                    {colVisible('language') && <td className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{curatedCell(product, 'language')}</td>}
                    {/* A product can carry 250+ country codes, so the cell shows
                        the first few and the full list on hover. Printing them
                        all would make one row taller than the table. */}
                    {colVisible('languages') && <td className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300">{curatedCell(product, 'languages')}</td>}
                    {colVisible('regions') && <td className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300">{curatedCell(product, 'regions')}</td>}
                    {colVisible('stock') && <td className="px-3 py-1.5 text-sm">
                      {product.vendor === 'epay' || product.vendor === 'ubiqfy' ? (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      ) : product.stockQuantity == null ? (
                        <span className="text-gray-500 dark:text-gray-400" title="Unlimited">∞</span>
                      ) : product.stockQuantity === 0 ? (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400">Out of stock</span>
                      ) : (
                        <span className="text-green-700 dark:text-green-400 font-medium">{product.stockQuantity.toLocaleString()}</span>
                      )}
                    </td>}
                    {colVisible('cost') && <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-white">
                      {product.isVariableAmount ? (
                        product.costPriceMinor != null ? (
                          <span>{currencySymbol(product.currencyCode)}{(product.costPriceMinor / 100).toFixed(2)}</span>
                        ) : (() => {
                          const sym = currencySymbol(product.currencyCode);
                          const costMin = Math.round(product.minAmountMinor * (1 - product.discountPercent / 100));
                          const costMax = Math.round(product.maxAmountMinor * (1 - product.discountPercent / 100));
                          return <span className="text-purple-600 dark:text-purple-400">{sym}{(costMin / 100).toFixed(2)} - {sym}{(costMax / 100).toFixed(2)}</span>;
                        })()
                      ) : (() => {
                        const cost = product.costPriceMinor != null
                          ? product.costPriceMinor
                          : Math.round(product.faceValue * (1 - product.discountPercent / 100));
                        const sym = currencySymbol(product.currencyCode);
                        const display = `${sym}${(cost / 100).toFixed(2)}`;
                        if (product.vendor === 'wock' && product.wockBulkPrices && product.wockBulkPrices.length > 0) {
                          const sorted = [...product.wockBulkPrices].sort((a, b) => a.minimumQuantity - b.minimumQuantity);
                          const hasTiers = sorted.length > 1;
                          return (
                            <span className="cursor-help inline-flex items-center gap-1"
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setBulkPriceTooltip({ x: rect.left, y: rect.top - 8, tiers: sorted, sym });
                              }}
                              onMouseLeave={() => setBulkPriceTooltip(null)}>
                              <span className="text-pink-600 dark:text-pink-400 font-medium">{display}</span>
                              {hasTiers && <span className="px-1 py-0.5 text-[10px] rounded bg-pink-100 dark:bg-pink-900/40 text-pink-600 dark:text-pink-400 font-medium">Tiered</span>}
                            </span>
                          );
                        }
                        return display;
                      })()}
                      {/* #58 — the vendor bills this card in another currency; show what it said and the rate we converted at */}
                      {product.vendorCostCurrency && product.vendorCostCurrency !== product.currencyCode && product.vendorCostMinor != null && (
                        <span className="block text-[10px] leading-tight text-gray-500 dark:text-gray-400"
                          title={product.vendorCostFxRate != null
                            ? `Billed in ${product.vendorCostCurrency}; converted at the vendor's own rate of ${product.vendorCostFxRate} ${product.currencyCode} per ${product.vendorCostCurrency}`
                            : `Billed in ${product.vendorCostCurrency}; no rate was available, so no cost is stored and the denomination is used instead`}>
                          {product.vendorCostCurrency} {(product.vendorCostMinor / 100).toFixed(2)}{product.vendorCostFxRate != null ? ` @ ${product.vendorCostFxRate.toFixed(4)}` : ' · no rate'}
                        </span>
                      )}
                    </td>}
                    {colVisible('discount') && <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-white">
                      {product.vendor === 'wock' ? (
                        <span className="text-pink-600 dark:text-pink-400 text-xs">Tiered</span>
                      ) : (
                        `${product.discountPercent}%`
                      )}
                    </td>}
                    {colVisible('status') && <td className="px-3 py-1.5">
                      <span className={`px-2 py-1 text-xs rounded-full ${product.active ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' : 'bg-gray-100 text-gray-600'}`}>
                        {product.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>}
                    {colVisible('api') && <td className="px-3 py-1.5">
                      <span className={`px-2 py-1 text-xs rounded-full ${product.apiEnabled ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                        {product.apiEnabled ? 'Enabled' : 'Off'}
                      </span>
                    </td>}
                    {colVisible('actions') && <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingProduct(product)}
                          className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                          title="Edit"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        {product.active ? (
                          <button
                            onClick={() => handleDelete(product)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Deactivate"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivate(product)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Reactivate"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={sortedProducts.length}
        itemsPerPage={itemsPerPage}
      />

      {/* Bulk Pricing Tooltip */}
      {bulkPriceTooltip && (
        <div className="fixed z-[9999] pointer-events-none" style={{ left: bulkPriceTooltip.x, top: bulkPriceTooltip.y, transform: 'translateY(-100%)' }}>
          <div className="bg-gray-900 dark:bg-gray-700 rounded-lg shadow-xl px-4 py-3 border border-pink-500/30">
            <div className="text-pink-400 font-semibold text-xs mb-2 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-pink-400"></span>
              Bulk Pricing Tiers
            </div>
            {bulkPriceTooltip.tiers.map((bp, i) => (
              <div key={i} className="flex items-center justify-between gap-4 text-sm">
                <span className="text-gray-400">{bp.minimumQuantity}+ units</span>
                <span className="text-white font-medium">{bulkPriceTooltip.sym}{bp.price.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create/Edit Product Modal */}
      {(showCreateModal || editingProduct) && (
        <ProductModal
          product={editingProduct}
          onClose={() => { setShowCreateModal(false); setEditingProduct(null); }}
          onSave={(savedProduct) => {
            if (editingProduct) {
              setProducts(prev => prev.map(p => p.id === savedProduct.id ? savedProduct : p));
            } else {
              setProducts(prev => [savedProduct, ...prev]);
            }
            setShowCreateModal(false);
            setEditingProduct(null);
          }}
        />
      )}

      {/* Bulk Upload Modal */}
      {showUploadModal && (
        <BulkUploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            // Products will be refreshed via socket event
          }}
        />
      )}
    </div>
  );
};

const ProductModal = ({ product, onClose, onSave }) => {
  const isVendorProduct = product && (product.vendor === 'bamboo' || product.vendor === 'ubiqfy' || product.vendor === 'wock');
  const [formData, setFormData] = useState({
    sku: product?.sku || '',
    erpSku: product?.erpSku || '',
    ean: product?.ean || '',
    productName: product?.name || '',
    faceValueMinor: product ? (product.faceValue / 100).toFixed(2) : '',
    discountPercent: product?.discountPercent || 0,
    apiEnabled: product?.apiEnabled || false,
    currencyCode: product?.currencyCode || 'EUR',
    isVariableAmount: product?.isVariableAmount || false,
    minAmountMinor: product?.minAmountMinor ? (product.minAmountMinor / 100).toFixed(2) : '10.00',
    maxAmountMinor: product?.maxAmountMinor ? (product.maxAmountMinor / 100).toFixed(2) : '300.00'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // The curated layer (F26): our own value for each descriptive field, and the
  // two marks that change no value ("the AI is right", "let the AI fill it again").
  const [curatedDraft, setCuratedDraft] = useState(() => curatedDraftOf(product));
  const [marks, setMarks] = useState({ accept: {}, release: {} });
  const [showHelp, setShowHelp] = useState(false);
  // F32: the picture is saved the moment it changes (it is not part of the form), so
  // the block keeps its own fresh copy of the product's picture fields.
  const [pictureState, setPictureState] = useState(() => (product ? { image: product.image || null, brandImage: product.brandImage || null, brandName: product.brandName || null } : null));
  const setMark = (kind, key, on) => setMarks(prev => ({ ...prev, [kind]: { ...prev[kind], [key]: on } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if ((!isVendorProduct && !formData.sku) || !formData.ean || !formData.productName) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate variable amount fields
    if (formData.isVariableAmount) {
      const minAmount = parseFloat(formData.minAmountMinor);
      const maxAmount = parseFloat(formData.maxAmountMinor);
      if (!minAmount || !maxAmount) {
        setError('Variable amount products require min and max amounts');
        return;
      }
      if (minAmount > maxAmount) {
        setError('Minimum amount must be less than or equal to maximum amount');
        return;
      }
    } else if (!formData.faceValueMinor) {
      setError('Please enter a denomination for fixed amount products');
      return;
    }

    // Only the curated fields that CHANGED are sent: `null` puts a field back to
    // the supplier's value, and an untouched field must not be re-stamped.
    const curatedPayload = {};
    const acceptKeys = [];
    const releaseKeys = [];
    for (const field of CURATED_FIELDS) {
      if (!product && field.key !== 'name') continue;
      const d = curatedDraft[field.key];
      if (field.kind === 'regions' && d.mode === 'choose' && d.list.length === 0) {
        setError('Countries: choose at least one country, or pick "Worldwide" or the supplier\'s list.');
        return;
      }
      const value = curatedDraftValue(field, d);
      const original = product?.curated ? product.curated[field.key] : null;
      if (!sameCurated(value, original)) curatedPayload[field.body] = value;
      else if (marks.accept[field.key]) acceptKeys.push(field.key);
      else if (marks.release[field.key]) releaseKeys.push(field.key);
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...(isVendorProduct ? {} : { sku: formData.sku || null }),
        erp_sku: formData.erpSku || null,
        ean: formData.ean,
        productName: formData.productName,
        ...curatedPayload,
        ...(acceptKeys.length ? { curatedAccept: acceptKeys } : {}),
        ...(releaseKeys.length ? { curatedRelease: releaseKeys } : {}),
        faceValueMinor: formData.isVariableAmount ? 0 : Math.round(parseFloat(formData.faceValueMinor) * 100),
        discountPercent: parseFloat(formData.discountPercent) || 0,
        apiEnabled: formData.apiEnabled,
        currencyCode: formData.currencyCode,
        isVariableAmount: formData.isVariableAmount,
        minAmountMinor: formData.isVariableAmount ? Math.round(parseFloat(formData.minAmountMinor) * 100) : null,
        maxAmountMinor: formData.isVariableAmount ? Math.round(parseFloat(formData.maxAmountMinor) * 100) : null
      };

      let response;
      if (product) {
        response = await axios.put(`${API_URL}/api/products/${product.id}`, payload);
      } else {
        response = await axios.post(`${API_URL}/api/products`, payload);
      }

      const savedProduct = toProduct(response.data);

      onSave(savedProduct);
    } catch (error) {
      setError(error.response?.data?.error || error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div data-testid="product-modal" className={`bg-white dark:bg-gray-800 rounded-lg ${product ? 'max-w-2xl' : 'max-w-md'} w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto`}>
        {showHelp && <CatalogueHelpModal onClose={() => setShowHelp(false)} />}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {product ? 'Edit Product' : 'Add New Product'}
        </h3>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Customer SKU {!isVendorProduct && '*'}
            </label>
            {isVendorProduct ? (
              <div className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 text-sm font-mono">
                {product.sku || 'Auto-generated on sync'}
              </div>
            ) : (
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                placeholder="e.g., SPOTIFY-10-ES"
              />
            )}
            {product?.vendorSku && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Vendor ID: <span className="font-mono">{product.vendorSku}</span>
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ERP SKU</label>
            <input
              type="text"
              value={formData.erpSku}
              onChange={(e) => setFormData({ ...formData, erpSku: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="Internal ERP/billing reference"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Internal billing reference. Not visible to customers.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EAN *</label>
            <input
              type="text"
              value={formData.ean}
              onChange={(e) => setFormData({ ...formData, ean: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="13-digit EAN code"
              maxLength={13}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vendor Description *</label>
            <input
              type="text"
              value={formData.productName}
              onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="e.g., Spotify €10 Gift Card ES"
            />
          </div>

          {/* Catalogue details — the curated layer. A new product has only the
              name; the other fields appear once the product exists. */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Catalogue details</h4>
              <button type="button" onClick={() => setShowHelp(true)} className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 hover:underline">
                <HelpCircle className="w-3.5 h-3.5" /> How this works
              </button>
            </div>
            {product && pictureState && (
              <PictureEditor
                product={{ id: product.id, ...pictureState }}
                onChanged={(fresh) => setPictureState({ image: fresh.image || null, brandImage: fresh.brandImage || null, brandName: fresh.brandName || null })}
              />
            )}
            {product && (
              <div className="mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                <ProvenanceLegend />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 max-w-prose">
                  What you type here is what customers see, and the supplier's hourly update never overwrites it. Leave a field empty to show the supplier's value.
                </p>
              </div>
            )}
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {CURATED_FIELDS.filter(f => product || f.key === 'name').map(field => (
                <CuratedFieldRow
                  key={field.key}
                  field={field}
                  product={product}
                  draft={curatedDraft[field.key]}
                  onDraft={(d) => setCuratedDraft(prev => ({ ...prev, [field.key]: d }))}
                  marks={marks}
                  onMark={(kind, on) => setMark(kind, field.key, on)}
                  suggestListId={field.kind === 'text' && field.key !== 'name' ? `curated-suggest-${field.key}` : null}
                />
              ))}
            </div>
          </div>

          {/* Variable Amount Toggle */}
          <div className={`p-4 rounded-lg border-2 ${formData.isVariableAmount ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-600'}`}>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isVariableAmount}
                onChange={(e) => setFormData({ ...formData, isVariableAmount: e.target.checked })}
                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
              />
              <div>
                <span className="font-medium text-gray-900 dark:text-white">Variable Amount Product</span>
                <p className="text-sm text-gray-500 dark:text-gray-400">Allow custom face values within a range</p>
              </div>
            </label>

            {formData.isVariableAmount && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">Min ({currencySymbol(formData.currencyCode)}) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.minAmountMinor}
                    onChange={(e) => setFormData({ ...formData, minAmountMinor: e.target.value })}
                    className="w-full px-3 py-2 border border-purple-300 dark:border-purple-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="10.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">Max ({currencySymbol(formData.currencyCode)}) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.maxAmountMinor}
                    onChange={(e) => setFormData({ ...formData, maxAmountMinor: e.target.value })}
                    className="w-full px-3 py-2 border border-purple-300 dark:border-purple-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="300.00"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Fixed denomination (only shown when not variable) */}
          {!formData.isVariableAmount && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Denomination ({currencySymbol(formData.currencyCode)}) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.faceValueMinor}
                  onChange={(e) => setFormData({ ...formData, faceValueMinor: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="10.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Discount (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.discountPercent}
                  onChange={(e) => setFormData({ ...formData, discountPercent: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="5.00"
                />
              </div>
            </div>
          )}

          {formData.isVariableAmount && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Discount (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.discountPercent}
                onChange={(e) => setFormData({ ...formData, discountPercent: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                placeholder="5.00"
              />
            </div>
          )}

          {/* API Access Toggle */}
          <div className={`p-4 rounded-lg border-2 ${formData.apiEnabled ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-600'}`}>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.apiEnabled}
                onChange={(e) => setFormData({ ...formData, apiEnabled: e.target.checked })}
                className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
              />
              <div>
                <span className="font-medium text-gray-900 dark:text-white">API Enabled</span>
                <p className="text-sm text-gray-500 dark:text-gray-400">Allow this product to be purchased via the external API</p>
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : (product ? 'Save Changes' : 'Add Product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BulkUploadModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setIsUploading(true);
    setError('');
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/products/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
    } catch (error) {
      setError(error.response?.data?.error || error.message);
      if (error.response?.data?.details) {
        setError(prev => prev + '\n' + error.response.data.details.join('\n'));
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Upload Products</h3>

        {!result ? (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Upload a CSV or XLSX file with product data. Download the template to see the required format.
            </p>

            <div className="flex gap-2 mb-4">
              <button
                onClick={() => downloadTemplate('csv')}
                className="text-sm text-amber-600 hover:underline"
              >
                Download CSV Template
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={() => downloadTemplate('xlsx')}
                className="text-sm text-amber-600 hover:underline"
              >
                Download XLSX Template
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm whitespace-pre-wrap">
                {error}
              </div>
            )}

            <div className="mb-4">
              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => setFile(e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || isUploading}
                className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg disabled:opacity-50"
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">Upload Complete!</h4>
              <p className="text-sm text-green-700">
                Created: {result.created} products<br />
                Updated: {result.updated} products
              </p>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 pt-2 border-t border-green-200">
                  <p className="text-sm text-red-600 font-medium">Errors ({result.errors.length}):</p>
                  <ul className="text-xs text-red-600 mt-1">
                    {result.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err.sku}: {err.error}</li>
                    ))}
                    {result.errors.length > 5 && <li>...and {result.errors.length - 5} more</li>}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => { onSuccess(); onClose(); }}
                className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// CUSTOMERS (API KEY MANAGEMENT)
// ============================================================================

const Customers = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [defaultPricingFormula, setDefaultPricingFormula] = useState('');
  const [newKeyDisplay, setNewKeyDisplay] = useState(null);
  const [pricingKey, setPricingKey] = useState(null);
  const [webhookLogKey, setWebhookLogKey] = useState(null);

  const loadApiKeys = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/api-keys`);
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApiKeys();
    axios.get(`${API_URL}/api/config/pricing`).then(r => setDefaultPricingFormula(r.data.defaultFormula || '')).catch(() => {});
  }, []);

  const handleCreate = async (formData) => {
    try {
      const response = await axios.post(`${API_URL}/api/api-keys`, {
        name: formData.name,
        description: formData.description,
        environment: formData.environment,
        allowed_ips: formData.allowedIps.filter(ip => ip.trim()),
        rate_limit_per_minute: parseInt(formData.rateLimit) || 30,
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        auto_fx: formData.autoFx,
        fx_priority: formData.fxPriority,
        daily_spend_cap_eur_minor: formData.dailySpendCapEurEnabled ? Math.round(parseFloat(formData.dailySpendCapEur) * 100) : null,
        daily_spend_cap_usd_minor: formData.dailySpendCapUsdEnabled ? Math.round(parseFloat(formData.dailySpendCapUsd) * 100) : null,
        max_quantity_per_order: formData.maxQuantityEnabled ? parseInt(formData.maxQuantityPerOrder) : null,
        webhook_url: formData.webhookUrl || null
      });
      setNewKeyDisplay(response.data.key);
      setShowCreateModal(false);
      setMessage({ type: 'success', text: 'API key created successfully' });
      loadApiKeys();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to create API key' });
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      await axios.put(`${API_URL}/api/api-keys/${id}`, {
        name: formData.name,
        description: formData.description,
        environment: formData.environment,
        allowed_ips: formData.allowedIps.filter(ip => ip.trim()),
        rate_limit_per_minute: parseInt(formData.rateLimit) || 30,
        is_active: formData.isActive,
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        auto_fx: formData.autoFx,
        fx_priority: formData.fxPriority,
        daily_spend_cap_eur_minor: formData.dailySpendCapEurEnabled ? Math.round(parseFloat(formData.dailySpendCapEur) * 100) : null,
        daily_spend_cap_usd_minor: formData.dailySpendCapUsdEnabled ? Math.round(parseFloat(formData.dailySpendCapUsd) * 100) : null,
        max_quantity_per_order: formData.maxQuantityEnabled ? parseInt(formData.maxQuantityPerOrder) : null,
        webhook_url: formData.webhookUrl || null
      });
      setEditingKey(null);
      setMessage({ type: 'success', text: 'API key updated' });
      loadApiKeys();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update API key' });
    }
  };

  const handleRotate = async (key) => {
    if (!window.confirm(`Rotate the API key for "${key.customer_name || key.name}"? The old key will stop working immediately.`)) return;
    try {
      const response = await axios.post(`${API_URL}/api/api-keys/${key.id}/rotate`);
      setNewKeyDisplay(response.data.key);
      setMessage({ type: 'success', text: 'API key rotated. Save the new key now.' });
      loadApiKeys();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to rotate API key' });
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Delete API key "${key.customer_name || key.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/api-keys/${key.id}`);
      setMessage({ type: 'success', text: 'API key deleted' });
      loadApiKeys();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete API key' });
    }
  };

  const handleSetBalance = async (key, currency = 'EUR') => {
    const symbol = currencySymbol(currency);
    const currentBalance = currency === 'USD' ? parseInt(key.balance_usd_minor || 0) : parseInt(key.balance_eur_minor || 0);
    const input = window.prompt(`Set ${currency} balance for "${key.customer_name || key.name}":`, (currentBalance / 100).toFixed(2));
    if (input === null) return;
    const amount = parseFloat(input);
    if (isNaN(amount) || amount < 0) {
      setMessage({ type: 'error', text: 'Invalid amount' });
      return;
    }
    try {
      await axios.put(`${API_URL}/api/api-keys/${key.id}/balance`, { currency, balance_minor: Math.round(amount * 100) });
      setMessage({ type: 'success', text: `${currency} balance set to ${symbol}${amount.toFixed(2)}` });
      loadApiKeys();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to set balance' });
    }
  };

  const handleSavePricing = async (data) => {
    try {
      await axios.put(`${API_URL}/api/api-keys/${pricingKey.id}`, {
        vendor_pricing: data.vendor_pricing,
        use_default_formula: data.use_default_formula
      });
      setPricingKey(null);
      setMessage({ type: 'success', text: 'Vendor pricing updated' });
      loadApiKeys();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save pricing' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: 'Copied to clipboard' });
  };

  const filteredKeys = apiKeys.filter(k => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (k.name || '').toLowerCase().includes(term) ||
      (k.customer_name || '').toLowerCase().includes(term) ||
      (k.customer_email || '').toLowerCase().includes(term) ||
      (k.key_prefix || '').toLowerCase().includes(term);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffd71f]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Customers & API Keys</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg text-sm"
        >
          <Plus className="w-4 h-4" />
          New API Key
        </button>
      </div>

      {message && (
        <div className={`p-4 rounded-lg flex items-center justify-between ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'}`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} className="ml-2"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* New key display banner */}
      {newKeyDisplay && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            <span className="font-semibold text-yellow-800 dark:text-yellow-300">Save this API key now — it will not be shown again!</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-yellow-300 dark:border-yellow-600 rounded font-mono text-sm text-gray-900 dark:text-white break-all">{newKeyDisplay}</code>
            <button onClick={() => copyToClipboard(newKeyDisplay)} className="p-2 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 rounded" title="Copy">
              <Copy className="w-5 h-5" />
            </button>
          </div>
          <button onClick={() => setNewKeyDisplay(null)} className="mt-2 text-sm text-yellow-700 dark:text-yellow-400 underline">Dismiss</button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, or key prefix..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        />
      </div>

      {/* API Keys Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Environment</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">IPs</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredKeys.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  {apiKeys.length === 0 ? 'No API keys yet. Create one to get started.' : 'No results match your search.'}
                </td>
              </tr>
            ) : (
              filteredKeys.map(k => (
                <tr key={k.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${!k.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-1.5">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {k.customer_name || k.name}
                      {k.use_default_formula ? (
                        <span className="ml-2 px-1.5 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 rounded-full">Default</span>
                      ) : k.vendor_pricing && Object.values(k.vendor_pricing).some(f => f && f.trim()) ? (
                        <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded-full">Custom</span>
                      ) : null}
                    </div>
                    {k.customer_email && <div className="text-xs text-gray-500 dark:text-gray-400">{k.customer_email}</div>}
                    {k.customer_name && k.name !== k.customer_name && <div className="text-xs text-gray-400 dark:text-gray-500">{k.name}</div>}
                  </td>
                  <td className="px-3 py-1.5">
                    <code className="text-xs font-mono text-gray-600 dark:text-gray-400">{k.key_prefix}...</code>
                    {k.last_used_at && <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Used {new Date(k.last_used_at).toLocaleDateString()}</div>}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`px-2 py-1 text-xs rounded-full ${k.environment === 'LIVE' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400' : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'}`}>
                      {k.environment}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="space-y-0.5">
                      <button onClick={() => handleSetBalance(k, 'EUR')} className="block text-sm font-medium text-gray-900 dark:text-white hover:text-amber-600 dark:hover:text-amber-400" title="Click to set EUR balance">
                        €{(parseInt(k.balance_eur_minor || 0) / 100).toFixed(2)}
                      </button>
                      <button onClick={() => handleSetBalance(k, 'USD')} className="block text-xs text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400" title="Click to set USD balance">
                        ${(parseInt(k.balance_usd_minor || 0) / 100).toFixed(2)}
                        <span className="text-gray-400 dark:text-gray-500"> (AED {(parseInt(k.balance_usd_minor || 0) / 100 * 3.67).toFixed(2)})</span>
                      </button>
                      {k.auto_fx && <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400">FX</span>}
                    </div>
                  </td>
                  <td className="px-3 py-1.5">
                    {k.allowed_ips && k.allowed_ips.length > 0 ? (
                      <div className="flex items-center gap-1">
                        <Shield className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">{k.allowed_ips.length} IP{k.allowed_ips.length > 1 ? 's' : ''}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">All</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`px-2 py-1 text-xs rounded-full ${k.is_active ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                      {k.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {(k.daily_spend_cap_eur_minor != null || k.daily_spend_cap_usd_minor != null || k.max_quantity_per_order != null) && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded-full cursor-default"
                        title={`EUR: ${k.daily_spend_cap_eur_minor != null ? '€' + (k.daily_spend_cap_eur_minor/100).toFixed(0) + '/day' : '—'} | USD: ${k.daily_spend_cap_usd_minor != null ? '$' + (k.daily_spend_cap_usd_minor/100).toFixed(0) + '/day' : '—'} | Qty: ${k.max_quantity_per_order || '—'}/order`}>
                        Caps
                      </span>
                    )}
                    {k.webhook_url && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 rounded-full cursor-default" title={k.webhook_url}>
                        Webhook
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setPricingKey(k)} className="p-1 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded" title="Pricing">
                        <DollarSign className="w-4 h-4" />
                      </button>
                      {k.webhook_url && (
                        <button onClick={() => setWebhookLogKey(k)} className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded" title="Webhook delivery log">
                          <Bell className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => setEditingKey(k)} className="p-1 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded" title="Edit">
                        <Settings className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleRotate(k)} className="p-1 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded" title="Rotate key">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(k)} className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingKey) && (
        <ApiKeyModal
          apiKey={editingKey}
          onClose={() => { setShowCreateModal(false); setEditingKey(null); }}
          onSave={(formData) => {
            if (editingKey) {
              handleUpdate(editingKey.id, formData);
            } else {
              handleCreate(formData);
            }
          }}
        />
      )}

      {pricingKey && (
        <VendorPricingModal
          apiKey={pricingKey}
          defaultPricingFormula={defaultPricingFormula}
          onClose={() => setPricingKey(null)}
          onSave={handleSavePricing}
        />
      )}

      {webhookLogKey && (
        <WebhookLogModal
          apiKey={webhookLogKey}
          onClose={() => setWebhookLogKey(null)}
        />
      )}
    </div>
  );
};

const WebhookLogModal = ({ apiKey, onClose }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const PAGE_SIZE = 20;

  const loadDeliveries = async (offset = 0) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/api-keys/${apiKey.id}/webhook/deliveries?limit=${PAGE_SIZE}&offset=${offset}`);
      setDeliveries(response.data.deliveries);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load webhook deliveries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDeliveries(page * PAGE_SIZE); }, [page]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Webhook Delivery Log — {apiKey.customer_name || apiKey.name}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Time</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Event</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Order</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Attempt</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Status</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Duration</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan="7" className="px-3 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : deliveries.length === 0 ? (
                <tr><td colSpan="7" className="px-3 py-8 text-center text-gray-500">No webhook deliveries yet</td></tr>
              ) : deliveries.map((d) => (
                <React.Fragment key={d.id}>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => setExpandedRow(expandedRow === d.id ? null : d.id)}>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{new Date(d.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 text-xs rounded ${d.event === 'test' ? 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300' : d.event === 'order.completed' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'}`}>
                        {d.event}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{d.order_id || '—'}</td>
                    <td className="px-3 py-2 text-center text-xs text-gray-600 dark:text-gray-400">{d.attempt}/3</td>
                    <td className="px-3 py-2 text-center text-xs text-gray-600 dark:text-gray-400">{d.response_status || '—'}</td>
                    <td className="px-3 py-2 text-right text-xs text-gray-600 dark:text-gray-400">{d.duration_ms}ms</td>
                    <td className="px-3 py-2 text-center">
                      {d.success
                        ? <Check className="w-4 h-4 text-green-600 dark:text-green-400 inline" />
                        : <X className="w-4 h-4 text-red-500 dark:text-red-400 inline" />}
                    </td>
                  </tr>
                  {expandedRow === d.id && (
                    <tr>
                      <td colSpan="7" className="px-4 py-3 bg-gray-50 dark:bg-gray-900">
                        <div className="space-y-2 text-xs">
                          <div><span className="font-medium text-gray-700 dark:text-gray-300">URL:</span> <span className="font-mono text-gray-600 dark:text-gray-400">{d.url}</span></div>
                          {d.error_message && <div><span className="font-medium text-red-600">Error:</span> <span className="text-red-600 dark:text-red-400">{d.error_message}</span></div>}
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Request:</span>
                            <pre className="mt-1 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto text-xs max-h-32 overflow-y-auto">{JSON.stringify(d.request_body, null, 2)}</pre>
                          </div>
                          {d.response_body && (
                            <div>
                              <span className="font-medium text-gray-700 dark:text-gray-300">Response:</span>
                              <pre className="mt-1 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto text-xs max-h-32 overflow-y-auto">{d.response_body}</pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">{total} deliveries</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-2 py-1 text-xs border rounded disabled:opacity-50">Prev</button>
              <span className="text-gray-600 dark:text-gray-400">{page + 1} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs border rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const VENDORS = ['epay', 'bamboo', 'ubiqfy', 'wock'];
const VENDOR_LABELS = { epay: 'ePay', bamboo: 'Bamboo', ubiqfy: 'Ubiqfy', wock: 'WOCK' };
const VENDOR_LABEL_CLASSES = {
  epay: 'text-blue-700 dark:text-blue-400',
  bamboo: 'text-green-700 dark:text-green-400',
  ubiqfy: 'text-orange-700 dark:text-orange-400',
  wock: 'text-pink-700 dark:text-pink-400'
};

const VendorPricingModal = ({ apiKey, defaultPricingFormula, onClose, onSave }) => {
  const vp = apiKey.vendor_pricing || {};
  const [useDefault, setUseDefault] = useState(apiKey.use_default_formula || false);
  const [vendorModes, setVendorModes] = useState(() => {
    const modes = {};
    VENDORS.forEach(v => { modes[v] = (vp[v] && vp[v].trim()) ? 'custom' : 'default'; });
    return modes;
  });
  const [vendorFormulas, setVendorFormulas] = useState(() => {
    const formulas = {};
    VENDORS.forEach(v => { formulas[v] = vp[v] || ''; });
    return formulas;
  });
  const [validations, setValidations] = useState({});
  const [saving, setSaving] = useState(false);

  const validateFormula = async (vendor, formula) => {
    if (!formula || !formula.trim()) {
      setValidations(prev => ({ ...prev, [vendor]: null }));
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/api-keys/validate-formula`, { formula });
      setValidations(prev => ({ ...prev, [vendor]: response.data }));
    } catch (error) {
      setValidations(prev => ({ ...prev, [vendor]: { valid: false, error: error.response?.data?.error || 'Validation failed' } }));
    }
  };

  const handleSave = async () => {
    // Block save if any custom vendor has empty or invalid formula
    for (const v of VENDORS) {
      if (!useDefault && vendorModes[v] === 'custom') {
        if (!vendorFormulas[v]?.trim()) {
          setValidations(prev => ({ ...prev, [v]: { valid: false, error: 'Formula is required when Custom is selected' } }));
          return;
        }
        if (validations[v] && !validations[v].valid) return;
      }
    }
    setSaving(true);
    try {
      const vendor_pricing = {};
      VENDORS.forEach(v => {
        vendor_pricing[v] = vendorModes[v] === 'custom' ? vendorFormulas[v].trim() : null;
      });
      await onSave({ vendor_pricing, use_default_formula: useDefault });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Pricing — {apiKey.customer_name || apiKey.name}
          </h3>
        </div>

        <div className="p-6 space-y-5">
          {/* Global default override */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={useDefault}
              onChange={(e) => setUseDefault(e.target.checked)}
              className="w-5 h-5 mt-0.5 text-purple-600 rounded focus:ring-purple-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">Use global default for all vendors</span>
              {defaultPricingFormula ? (
                <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                  Currently: <code className="font-mono bg-purple-50 dark:bg-purple-900/30 px-1 rounded">{defaultPricingFormula}</code>
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">No global default set — face value will be used</p>
              )}
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Overrides all vendor-specific formulas below</p>
            </div>
          </label>

          {/* Vendor pricing sections */}
          <div className={`space-y-4 ${useDefault ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Vendor Pricing</span>
            </div>

            {VENDORS.map(v => {
              return (
                <div key={v} className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-semibold w-16 ${VENDOR_LABEL_CLASSES[v]}`}>{VENDOR_LABELS[v]}</span>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name={`mode-${v}`}
                        checked={vendorModes[v] === 'default'}
                        onChange={() => setVendorModes(prev => ({ ...prev, [v]: 'default' }))}
                        className="text-gray-600"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">System default</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name={`mode-${v}`}
                        checked={vendorModes[v] === 'custom'}
                        onChange={() => setVendorModes(prev => ({ ...prev, [v]: 'custom' }))}
                        className="text-amber-600"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">Custom</span>
                    </label>
                  </div>

                  {vendorModes[v] === 'default' ? (
                    <p className="text-xs text-purple-600 dark:text-purple-400 ml-[76px]">
                      {defaultPricingFormula
                        ? <>Using: <code className="font-mono bg-purple-50 dark:bg-purple-900/30 px-1 rounded">{defaultPricingFormula}</code></>
                        : <span className="text-gray-400 dark:text-gray-500">No default — face value</span>}
                    </p>
                  ) : (
                    <div className="ml-[76px]">
                      <input
                        type="text"
                        value={vendorFormulas[v]}
                        onChange={(e) => { setVendorFormulas(prev => ({ ...prev, [v]: e.target.value })); setValidations(prev => ({ ...prev, [v]: null })); }}
                        onBlur={(e) => validateFormula(v, e.target.value)}
                        className={`w-full px-3 py-1.5 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono ${
                          validations[v] ? (validations[v].valid ? 'border-green-400' : 'border-red-400') : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="[cost] * 1.15"
                      />
                      {validations[v] && (
                        <p className={`text-xs mt-1 ${validations[v].valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {validations[v].valid
                            ? `Valid — sample: cost=10.00, face=12.00 → ${validations[v].sampleDisplay || (validations[v].sampleResult / 100).toFixed(2)}`
                            : `Error: ${validations[v].error}`}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Variable reference */}
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Variables: <code className="font-mono">[cost]</code> <code className="font-mono">[face_value]</code> <code className="font-mono">[quantity]</code> <code className="font-mono">[order_face_value]</code> <code className="font-mono">[order_cost_value]</code>
          </p>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-amber-600 dark:bg-amber-500 text-white rounded-lg hover:bg-amber-700 dark:hover:bg-amber-600 disabled:opacity-50 text-sm"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ApiKeyModal = ({ apiKey, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: apiKey?.name || '',
    description: apiKey?.description || '',
    customerName: apiKey?.customer_name || '',
    customerEmail: apiKey?.customer_email || '',
    environment: apiKey?.environment || 'TEST',
    rateLimit: apiKey?.rate_limit_per_minute || 30,
    isActive: apiKey?.is_active !== false,
    allowedIps: apiKey?.allowed_ips?.length > 0 ? [...apiKey.allowed_ips] : [''],
    autoFx: apiKey?.auto_fx || false,
    fxPriority: apiKey?.fx_priority || 'native',
    dailySpendCapEurEnabled: apiKey?.daily_spend_cap_eur_minor != null,
    dailySpendCapEur: apiKey?.daily_spend_cap_eur_minor != null ? String(apiKey.daily_spend_cap_eur_minor / 100) : '',
    dailySpendCapUsdEnabled: apiKey?.daily_spend_cap_usd_minor != null,
    dailySpendCapUsd: apiKey?.daily_spend_cap_usd_minor != null ? String(apiKey.daily_spend_cap_usd_minor / 100) : '',
    maxQuantityEnabled: apiKey?.max_quantity_per_order != null,
    maxQuantityPerOrder: apiKey?.max_quantity_per_order != null ? String(apiKey.max_quantity_per_order) : '',
    webhookUrl: apiKey?.webhook_url || '',
    webhookSecret: apiKey?.webhook_secret || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState(null);

  const addIpField = () => {
    setFormData(prev => ({ ...prev, allowedIps: [...prev.allowedIps, ''] }));
  };

  const removeIpField = (index) => {
    setFormData(prev => ({
      ...prev,
      allowedIps: prev.allowedIps.filter((_, i) => i !== index)
    }));
  };

  const updateIp = (index, value) => {
    setFormData(prev => ({
      ...prev,
      allowedIps: prev.allowedIps.map((ip, i) => i === index ? value : ip)
    }));
  };

  const testWebhook = async () => {
    if (!formData.webhookUrl) return;
    setTestingWebhook(true);
    setWebhookTestResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/api-keys/${apiKey?.id || 0}/webhook/test`, {
        webhook_url: formData.webhookUrl,
        webhook_secret: formData.webhookSecret || undefined
      });
      setWebhookTestResult(response.data);
    } catch (error) {
      setWebhookTestResult({ success: false, error: error.response?.data?.error || error.message });
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSave(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {apiKey ? 'Edit API Key' : 'Create API Key'}
          </h3>
        </div>

        <div className="p-6 space-y-4">
          {/* Customer Info */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer Name</label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                placeholder="e.g., Poriyo SL"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer Email</label>
              <input
                type="email"
                value={formData.customerEmail}
                onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                placeholder="e.g., api@poriyo.com"
              />
            </div>
          </div>

          {/* Key Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key Label *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="e.g., Poriyo Production"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="Optional notes"
            />
          </div>

          {/* Environment & Rate Limit */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Environment</label>
              <select
                value={formData.environment}
                onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="TEST">TEST</option>
                <option value="LIVE">LIVE</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rate Limit (req/min)</label>
              <input
                type="number"
                min="1"
                value={formData.rateLimit}
                onChange={(e) => setFormData({ ...formData, rateLimit: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>

          {/* IP Allowlist */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IP Allowlist</label>
              <button type="button" onClick={addIpField} className="text-xs text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add IP
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Leave empty to allow all IPs.</p>
            <div className="space-y-2">
              {formData.allowedIps.map((ip, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={ip}
                    onChange={(e) => updateIp(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                    placeholder="e.g., 203.0.113.10"
                  />
                  {formData.allowedIps.length > 1 && (
                    <button type="button" onClick={() => removeIpField(index)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Auto FX */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.autoFx}
                onChange={(e) => setFormData({ ...formData, autoFx: e.target.checked })}
                className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Auto FX (cross-currency fallback)</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-8">
              When enabled, if the native currency wallet is insufficient, automatically converts from the other wallet at live Wise rates + spread.
            </p>
            {formData.autoFx && (
              <div className="mt-3 ml-8">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">FX Priority</label>
                <select
                  value={formData.fxPriority}
                  onChange={(e) => setFormData({ ...formData, fxPriority: e.target.value })}
                  className="w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="native">Native first</option>
                  <option value="eur">EUR first</option>
                  <option value="usd">USD first</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Which wallet to try first when auto-converting.</p>
              </div>
            )}
          </div>

          {/* Guardrails */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Guardrails</h4>

            {/* Daily EUR Spend Cap */}
            <div className="mb-3">
              <label className="flex items-center gap-3 cursor-pointer mb-1">
                <input type="checkbox" checked={formData.dailySpendCapEurEnabled}
                  onChange={(e) => setFormData({...formData, dailySpendCapEurEnabled: e.target.checked, ...(!e.target.checked && {dailySpendCapEur: ''})})}
                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily EUR spend cap</span>
              </label>
              {formData.dailySpendCapEurEnabled ? (
                <div className="ml-7">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">€</span>
                    <input type="number" min="0" step="0.01" value={formData.dailySpendCapEur}
                      onChange={(e) => setFormData({...formData, dailySpendCapEur: e.target.value})}
                      className="w-36 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      placeholder="5000.00" />
                    <span className="text-xs text-gray-400">/day</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Resets midnight UTC</p>
                </div>
              ) : (
                <p className="text-xs text-gray-400 ml-7">No daily cap — limited by available balance</p>
              )}
            </div>

            {/* Daily USD Spend Cap */}
            <div className="mb-3">
              <label className="flex items-center gap-3 cursor-pointer mb-1">
                <input type="checkbox" checked={formData.dailySpendCapUsdEnabled}
                  onChange={(e) => setFormData({...formData, dailySpendCapUsdEnabled: e.target.checked, ...(!e.target.checked && {dailySpendCapUsd: ''})})}
                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily USD spend cap</span>
              </label>
              {formData.dailySpendCapUsdEnabled ? (
                <div className="ml-7">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">$</span>
                    <input type="number" min="0" step="0.01" value={formData.dailySpendCapUsd}
                      onChange={(e) => setFormData({...formData, dailySpendCapUsd: e.target.value})}
                      className="w-36 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      placeholder="3000.00" />
                    <span className="text-xs text-gray-400">/day</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Resets midnight UTC</p>
                </div>
              ) : (
                <p className="text-xs text-gray-400 ml-7">No daily cap — limited by available balance</p>
              )}
            </div>

            {/* Max Quantity Per Order */}
            <div className="mb-1">
              <label className="flex items-center gap-3 cursor-pointer mb-1">
                <input type="checkbox" checked={formData.maxQuantityEnabled}
                  onChange={(e) => setFormData({...formData, maxQuantityEnabled: e.target.checked, ...(!e.target.checked && {maxQuantityPerOrder: ''})})}
                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Max quantity per order</span>
              </label>
              {formData.maxQuantityEnabled ? (
                <div className="ml-7">
                  <input type="number" min="1" max="100000" value={formData.maxQuantityPerOrder}
                    onChange={(e) => setFormData({...formData, maxQuantityPerOrder: e.target.value})}
                    className="w-36 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="50" />
                  <p className="text-xs text-gray-500 mt-1">Max codes per single purchase</p>
                </div>
              ) : (
                <p className="text-xs text-gray-400 ml-7">No per-order limit (system max 100,000)</p>
              )}
            </div>
          </div>

          {/* Webhooks */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Webhooks</h4>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Webhook URL</label>
              <input
                type="url"
                value={formData.webhookUrl}
                onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                placeholder="https://example.com/webhooks/orders"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Receives order.completed / order.failed events via HTTP POST</p>
            </div>
            {(formData.webhookSecret || (apiKey && apiKey.webhook_secret)) && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Webhook Secret</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={formData.webhookSecret}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-mono"
                  />
                  <button type="button" onClick={() => { navigator.clipboard.writeText(formData.webhookSecret); }}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" title="Copy">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Used for HMAC-SHA256 signature verification (X-Webhook-Signature header). Auto-generated on save.</p>
              </div>
            )}
            {apiKey && formData.webhookUrl && (
              <div className="mb-2">
                <button type="button" onClick={testWebhook} disabled={testingWebhook}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                  {testingWebhook ? 'Testing...' : 'Test Webhook'}
                </button>
                {webhookTestResult && (
                  <span className={`ml-3 text-sm ${webhookTestResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {webhookTestResult.success
                      ? `Success (HTTP ${webhookTestResult.status}, ${webhookTestResult.durationMs}ms)`
                      : `Failed${webhookTestResult.status ? ` (HTTP ${webhookTestResult.status})` : ''}: ${webhookTestResult.error}`}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Active toggle (edit only) */}
          {apiKey && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</span>
            </label>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.name}
            className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg disabled:opacity-50 text-sm"
          >
            {isSubmitting ? 'Saving...' : (apiKey ? 'Save Changes' : 'Create Key')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CONFIGURATION → ENRICHMENT (F26 S7): the owner's prompt, the model, runs, and
// what the AI left waiting for a person. The engine is backend/src/enrichment/.
// ============================================================================

// A line-by-line diff of two texts (longest common subsequence), as rows for a
// side-by-side view: { kind: 'same' | 'del' | 'add' | 'change', old, new }.
const lineDiff = (oldText, newText) => {
  const a = (oldText || '').split('\n');
  const b = (newText || '').split('\n');
  const n = a.length;
  const m = b.length;
  const ops = [];
  if (n * m > 9000000) {
    // Far beyond any prompt body; show the whole thing as replaced rather than hang.
    a.forEach(line => ops.push(['del', line]));
    b.forEach(line => ops.push(['add', line]));
  } else {
    const w = m + 1;
    const dp = new Uint32Array((n + 1) * w);
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        dp[i * w + j] = a[i] === b[j] ? dp[(i + 1) * w + j + 1] + 1 : Math.max(dp[(i + 1) * w + j], dp[i * w + j + 1]);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (a[i] === b[j]) { ops.push(['same', a[i]]); i++; j++; }
      else if (dp[(i + 1) * w + j] >= dp[i * w + j + 1]) { ops.push(['del', a[i]]); i++; }
      else { ops.push(['add', b[j]]); j++; }
    }
    while (i < n) ops.push(['del', a[i++]]);
    while (j < m) ops.push(['add', b[j++]]);
  }
  // Pair a run of removed lines with the run of added lines that follows it.
  const rows = [];
  let k = 0;
  while (k < ops.length) {
    if (ops[k][0] === 'same') { rows.push({ kind: 'same', old: ops[k][1], new: ops[k][1] }); k++; continue; }
    const dels = [];
    const adds = [];
    while (k < ops.length && ops[k][0] === 'del') dels.push(ops[k++][1]);
    while (k < ops.length && ops[k][0] === 'add') adds.push(ops[k++][1]);
    for (let r = 0; r < Math.max(dels.length, adds.length); r++) {
      const hasOld = r < dels.length;
      const hasNew = r < adds.length;
      rows.push({ kind: hasOld && hasNew ? 'change' : hasOld ? 'del' : 'add', old: hasOld ? dels[r] : null, new: hasNew ? adds[r] : null });
    }
  }
  return rows;
};

// Changed rows with two lines of context; longer unchanged stretches are folded.
const foldUnchanged = (rows, context = 2) => {
  const keep = new Array(rows.length).fill(false);
  rows.forEach((row, i) => {
    if (row.kind === 'same') return;
    for (let d = -context; d <= context; d++) if (rows[i + d]) keep[i + d] = true;
  });
  const out = [];
  let hidden = 0;
  rows.forEach((row, i) => {
    if (keep[i]) {
      if (hidden) { out.push({ kind: 'fold', count: hidden }); hidden = 0; }
      out.push(row);
    } else hidden++;
  });
  if (hidden) out.push({ kind: 'fold', count: hidden });
  return out;
};

const DIFF_CELL = {
  same: 'text-gray-700 dark:text-gray-300',
  del: 'bg-red-50 dark:bg-red-900/30 text-red-900 dark:text-red-200',
  add: 'bg-green-50 dark:bg-green-900/30 text-green-900 dark:text-green-200',
  empty: 'bg-gray-50 dark:bg-gray-900/40',
};

// The confirmation the owner asked for: what will change, line by line, before a
// prompt version is created or restored. Saving changes NO product — only a run does.
const PromptDiffModal = ({ mode, oldLabel, newLabel, oldBody, newBody, versionNumber, note, onNote, busy, error, onConfirm, onClose }) => {
  const [showAll, setShowAll] = useState(false);
  const rows = useMemo(() => lineDiff(oldBody, newBody), [oldBody, newBody]);
  const changed = rows.filter(r => r.kind !== 'same').length;
  const shown = showAll ? rows : foldUnchanged(rows);
  const title = mode === 'restore' ? `Restore version ${versionNumber}?` : `Save as version ${versionNumber}?`;
  const cellClass = (row, side) => {
    if (row.kind === 'same') return DIFF_CELL.same;
    const text = side === 'old' ? row.old : row.new;
    if (text === null) return DIFF_CELL.empty;
    return side === 'old' ? DIFF_CELL.del : DIFF_CELL.add;
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 sm:p-4" onClick={onClose}>
      <div data-testid="prompt-diff-modal" role="dialog" aria-label={title} className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-6xl max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-4 sm:p-6 pb-3">
          <div className="space-y-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-prose">
              {changed === 0
                ? 'The two texts are identical — there is nothing to save.'
                : `${changed} line${changed === 1 ? ' differs' : 's differ'}. Left: ${oldLabel}. Right: ${newLabel}.`}
              {' '}<b>Saving changes no product.</b> Only a run does, and a run uses whichever version is active when it starts.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-4 sm:px-6 flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
          <span className="inline-flex items-center gap-1"><span className={`inline-block w-3 h-3 rounded ${DIFF_CELL.del}`}></span> removed</span>
          <span className="inline-flex items-center gap-1"><span className={`inline-block w-3 h-3 rounded ${DIFF_CELL.add}`}></span> added</span>
          <label className="inline-flex items-center gap-1 ml-auto cursor-pointer">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="rounded" /> show unchanged lines
          </label>
        </div>

        <div className="flex-1 overflow-auto mx-4 sm:mx-6 my-3 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="hidden md:grid grid-cols-2 text-xs font-semibold text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 sticky top-0">
            <div className="px-3 py-1.5 border-r border-gray-200 dark:border-gray-700">{oldLabel}</div>
            <div className="px-3 py-1.5">{newLabel}</div>
          </div>
          <div className="font-mono text-xs leading-5">
            {shown.map((row, i) => row.kind === 'fold' ? (
              <div key={i} className="px-3 py-1 text-center text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900/30 border-y border-gray-100 dark:border-gray-700/60">… {row.count} unchanged line{row.count === 1 ? '' : 's'} …</div>
            ) : (
              <div key={i} data-diff={row.kind} className="grid grid-cols-1 md:grid-cols-2">
                <div className={`px-3 whitespace-pre-wrap break-words md:border-r border-gray-200 dark:border-gray-700 ${cellClass(row, 'old')} ${row.kind === 'same' ? '' : 'md:border-b-0 border-b border-dashed border-gray-200 dark:border-gray-700'}`}>
                  {row.kind !== 'same' && row.old !== null && <span className="md:hidden select-none text-red-500 mr-1">−</span>}{row.old === null ? ' ' : (row.old || ' ')}
                </div>
                <div className={`px-3 whitespace-pre-wrap break-words ${cellClass(row, 'new')}`}>
                  {row.kind !== 'same' && row.new !== null && <span className="md:hidden select-none text-green-600 mr-1">+</span>}{row.new === null ? ' ' : (row.new || ' ')}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 sm:p-6 pt-0 space-y-3">
          {mode !== 'restore' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">What changed and why <span className="text-gray-400 font-normal">(kept with the version)</span></label>
              <input data-testid="prompt-note" type="text" value={note} onChange={(e) => onNote(e.target.value)} placeholder="e.g. PlayStation wallet cards always say Gift Card"
                className="w-full max-w-prose px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
            </div>
          )}
          {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 text-sm">{error}</div>}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm disabled:opacity-50">Cancel</button>
            <button data-testid="prompt-confirm" type="button" onClick={onConfirm} disabled={busy || changed === 0}
              className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg text-sm font-medium disabled:opacity-50">
              {busy ? 'Saving…' : mode === 'restore' ? `Restore version ${versionNumber}` : `Save as version ${versionNumber}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ENRICHMENT_SCOPES = [
  { key: 'missing', label: 'Fill what is missing', help: 'Products with no name of ours and nothing already waiting for you. What a nightly run would do.' },
  { key: 'rewrite-ai', label: 'Redo what the AI wrote', help: 'Every value the AI wrote, and every name still waiting, done again with the active prompt and your latest corrections. Values a person typed are never touched.' },
  { key: 'restyle-manual', label: 'Bring my own names to the style guide', help: 'The names a person typed by hand, re-written in the guide\'s form — the facts in your name stay (where it is valid, what it is, the amounts), only the form changes. A name that already follows the guide is left alone. A changed name is badged AI and your original is kept beside it: the Edit window has "Restore my original name". Their brand, platform and category are filled where empty. This is the ONLY run that may change a name you typed; your brands and categories are never touched.' },
];

// The details a run may be limited to (S10) — the engine's AI_FIELDS, in the words of the grid.
const RUN_FIELD_LABELS = { name: 'Name', brand: 'Brand', platform: 'Platform', category: 'Category', languages: 'Languages', regions: 'Countries', region: 'Region' };

const HELD_REASON = {
  held_low_confidence: { label: 'Not confident enough', help: 'The model\'s own confidence in the name was below the run\'s threshold.' },
  held_needs_review: { label: 'Model asked for a look', help: 'The model guessed a fact that shows in the name, or the guide told it to flag this.' },
  held_unsure: { label: 'Model was unsure', help: 'The model could not establish this detail from the supplier\'s data.' },
  held_supplier_word: { label: 'Names a supplier', help: 'The value contains the name of one of our suppliers, which customers must never see.' },
  held_invalid: { label: 'Not a valid value', help: 'Not a known country or language code, or not on the closed list. It can only be rejected.' },
};

const RUN_STATUS_CLASS = {
  COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  PROBING: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  RUNNING: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  SUBMITTED: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  APPLYING: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
};
const RUN_IN_FLIGHT = ['PROBING', 'RUNNING', 'SUBMITTED', 'APPLYING'];
const RUN_STATUS_WORDS = {
  PROBING: 'Checking the model', RUNNING: 'Running', SUBMITTED: 'Waiting for Anthropic', APPLYING: 'Applying answers', COMPLETED: 'Completed', FAILED: 'Failed',
};

const usd = (value) => (value === null || value === undefined ? '—' : `$${Number(value).toFixed(2)}`);
const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const tokensLabel = (n) => (n == null ? '—' : n >= 1000000 ? `${(n / 1000000).toFixed(n % 1000000 ? 1 : 0)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(n));
const errorText = (error, fallback) => error?.response?.data?.error || error?.message || fallback;

const RunDetailModal = ({ runId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/enrichment/runs/${runId}?results=1&limit=200`)
      .then(r => { if (!cancelled) setDetail(r.data); })
      .catch(e => { if (!cancelled) setError(errorText(e, 'Could not load the run')); });
    return () => { cancelled = true; };
  }, [runId]);
  const fieldRows = useMemo(() => {
    if (!detail) return [];
    const byField = {};
    detail.fields.forEach(f => { byField[f.field] = byField[f.field] || {}; byField[f.field][f.disposition] = f.count; });
    return Object.entries(byField);
  }, [detail]);
  const describe = (result, dispositions) => {
    const heldFields = Object.entries(dispositions || {}).filter(([, d]) => String(d).startsWith('held'));
    const applied = Object.entries(dispositions || {}).filter(([, d]) => d === 'applied' || d === 'would_apply').map(([k]) => k);
    return { heldFields, applied };
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 sm:p-4" onClick={onClose}>
      <div role="dialog" aria-label={`Run ${runId}`} className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-5xl max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-4 sm:p-6 pb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Run {runId}{detail?.run?.dry_run ? ' — dry run (no product was changed)' : ''}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-auto px-4 sm:px-6 pb-6 space-y-4 text-sm">
          {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300">{error}</div>}
          {!detail && !error && <p className="text-gray-500">Loading…</p>}
          {detail && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[['Products', detail.run.product_count], ['Answered', detail.products.done || 0], ['Changed', detail.run.applied_count ?? '—'], ['Waiting for you', detail.run.review_count ?? '—'],
                  ['Unanswered', detail.products.failed || 0], ['Cost (measured)', usd(detail.run.cost_usd_measured)], ['Model', detail.run.model], ['Prompt version', detail.run.prompt_version_id ? `#${detail.run.prompt_version_id}` : '—']].map(([label, value]) => (
                  <div key={label} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 min-w-0">
                    <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
                    <div className="font-semibold text-gray-900 dark:text-white break-words">{String(value)}</div>
                  </div>
                ))}
              </div>
              {detail.run.error && <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200">{detail.run.error}</div>}
              {fieldRows.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">What happened to each detail</h4>
                  <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400"><tr><th className="text-left px-3 py-2">Detail</th><th className="text-left px-3 py-2">Outcome → products</th></tr></thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {fieldRows.map(([field, outcomes]) => (
                          <tr key={field}><td className="px-3 py-1.5 font-medium text-gray-900 dark:text-white">{field}</td>
                            <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{Object.entries(outcomes).map(([d, c]) => `${d.replace(/_/g, ' ')}: ${c}`).join(' · ')}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {detail.results?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Answers{detail.results.length < (detail.run.product_count || 0) ? ` (first ${detail.results.length})` : ''}</h4>
                  <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400"><tr><th className="text-left px-3 py-2">SKU</th><th className="text-left px-3 py-2">Supplier's name</th><th className="text-left px-3 py-2">Proposed name</th><th className="text-left px-3 py-2">Outcome</th></tr></thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {detail.results.map(r => {
                          const { heldFields, applied } = describe(r.result, r.dispositions);
                          return (
                            <tr key={r.product_id}>
                              <td className="px-3 py-1.5 font-mono whitespace-nowrap text-gray-700 dark:text-gray-300">{r.sku}</td>
                              <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400">{r.product_name}</td>
                              <td className="px-3 py-1.5 text-gray-900 dark:text-white">{r.result?.name || <span className="text-gray-400">—</span>}{typeof r.result?.confidence === 'number' && <span className="ml-1 text-gray-400">({Math.round(r.result.confidence * 100)}%)</span>}</td>
                              <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">
                                {r.status === 'failed' ? <span className="text-red-600 dark:text-red-400">{r.error || 'failed'}</span> : (
                                  <>
                                    {applied.length > 0 && <span>{detail.run.dry_run ? 'would set' : 'set'}: {applied.join(', ')}</span>}
                                    {heldFields.length > 0 && <span className="block text-amber-700 dark:text-amber-300">waiting: {heldFields.map(([k, d]) => `${k} (${(HELD_REASON[d] || { label: d }).label.toLowerCase()})`).join(', ')}</span>}
                                    {!applied.length && !heldFields.length && <span className="text-gray-400">nothing to change</span>}
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EnrichmentTab = ({ onHelp }) => {
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [models, setModels] = useState(null);
  const [modelChoice, setModelChoice] = useState('');
  const [savingModel, setSavingModel] = useState(false);
  const [prompts, setPrompts] = useState(null);
  const [draft, setDraft] = useState('');
  const [showScaffold, setShowScaffold] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [diff, setDiff] = useState(null); // { mode, oldBody, newBody, oldLabel, newLabel, versionNumber, targetId }
  const [note, setNote] = useState('');
  const [diffBusy, setDiffBusy] = useState(false);
  const [diffError, setDiffError] = useState(null);
  const [proposing, setProposing] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [runs, setRuns] = useState([]);
  // `fields` empty = the run may write every field; `grounding` = game facts from IGDB (S10);
  // `inStock` = only products in stock or with no count (S12 — the owner's rule since the pilot).
  const [runForm, setRunForm] = useState({ scope: 'missing', dryRun: true, direct: false, grounding: true, inStock: true, fields: [], vendor: '', brand: '', search: '', limit: '', threshold: '0.85', spendCapUsd: '50' });
  // The nightly run's settings (S12), edited in place and saved as one.
  const [nightlyForm, setNightlyForm] = useState(null);
  const [savingNightly, setSavingNightly] = useState(false);
  const [starting, setStarting] = useState(false);
  const [runError, setRunError] = useState(null);
  const [openRun, setOpenRun] = useState(null);
  const [waiting, setWaiting] = useState(null);
  const [waitingVendor, setWaitingVendor] = useState('');
  const [waitingField, setWaitingField] = useState('');     // '' = everything; a field key = only rows where THAT waits
  const [waitingInStock, setWaitingInStock] = useState(false);
  const [ticked, setTicked] = useState(new Set());
  const [settling, setSettling] = useState(false);
  // "Take with my wording" (S8): one row at a time, its text fields as inputs
  // prefilled with the proposal; saving writes YOUR value and keeps the proposal
  // as what the AI wrote, so the engine learns from the correction.
  const [editing, setEditing] = useState(null); // { productId, values: {field: text} }
  const [message, setMessage] = useState(null);

  const flash = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 6000); };

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/enrichment/status`);
      setStatus(r.data);
      setStatusError(null);
      if (r.data.nightly) setNightlyForm(prev => prev && prev.dirty ? prev : { enabled: r.data.nightly.enabled, time: r.data.nightly.time, spendCapUsd: String(r.data.nightly.spendCapUsd), inStockOnly: r.data.nightly.inStockOnly, dirty: false });
      return r.data;
    } catch (e) {
      setStatusError(errorText(e, 'Could not read the enrichment status'));
      return null;
    }
  }, []);
  const loadModels = useCallback(async (refresh = false) => {
    try {
      const r = await axios.get(`${API_URL}/api/enrichment/models${refresh ? '?refresh=1' : ''}`);
      setModels(r.data);
      setModelChoice(r.data.current);
    } catch (e) {
      setModels({ ok: false, models: [], error: errorText(e, 'Could not read the model list'), inactive: e?.response?.status === 503 });
    }
  }, []);
  const loadPrompts = useCallback(async () => {
    const r = await axios.get(`${API_URL}/api/enrichment/prompts`);
    setPrompts(r.data);
    return r.data;
  }, []);
  const loadRuns = useCallback(async () => {
    const r = await axios.get(`${API_URL}/api/enrichment/runs`);
    setRuns(r.data.runs);
    return r.data.runs;
  }, []);
  const loadWaiting = useCallback(async (vendor = waitingVendor, field = waitingField, inStock = waitingInStock) => {
    const query = [vendor && `vendor=${encodeURIComponent(vendor)}`, field && `field=${encodeURIComponent(field)}`, inStock && 'inStock=1'].filter(Boolean).join('&');
    const r = await axios.get(`${API_URL}/api/enrichment/waiting${query ? `?${query}` : ''}`);
    setWaiting(r.data);
    setTicked(new Set());
  }, [waitingVendor, waitingField, waitingInStock]);

  useEffect(() => {
    loadStatus();
    loadModels();
    loadPrompts().then(p => setDraft(p.active?.body || '')).catch(e => flash('error', errorText(e, 'Could not load the prompt')));
    loadRuns().catch(() => {});
    loadWaiting().catch(() => {});
  }, [loadStatus, loadModels, loadPrompts, loadRuns, loadWaiting]);

  // While a run is in flight, keep the history and the waiting list fresh.
  const inFlight = runs.some(r => RUN_IN_FLIGHT.includes(r.status));
  useEffect(() => {
    if (!inFlight) return undefined;
    const timer = setInterval(async () => {
      const fresh = await loadRuns().catch(() => null);
      if (fresh && !fresh.some(r => RUN_IN_FLIGHT.includes(r.status))) { loadStatus(); loadWaiting().catch(() => {}); }
    }, 10000);
    return () => clearInterval(timer);
  }, [inFlight, loadRuns, loadStatus, loadWaiting]);

  const activeBody = prompts?.active?.body ?? '';
  const draftDirty = draft !== activeBody;
  const nextVersion = prompts ? Math.max(0, ...prompts.versions.map(v => v.version)) + 1 : 1;

  // ---- the model
  const saveModel = async () => {
    setSavingModel(true);
    try {
      await axios.put(`${API_URL}/api/enrichment/model`, { model: modelChoice });
      flash('success', `Model set to ${modelChoice}. The next run uses it.`);
      await loadModels(true);
      await loadStatus();
    } catch (e) {
      flash('error', errorText(e, 'The model could not be saved'));
    } finally {
      setSavingModel(false);
    }
  };
  const chosen = models?.models?.find(m => m.id === modelChoice) || null;

  // ---- the nightly run (S12)
  const editNightly = (patch) => setNightlyForm(prev => ({ ...prev, ...patch, dirty: true }));
  const saveNightly = async () => {
    if (!nightlyForm) return;
    setSavingNightly(true);
    try {
      const r = await axios.put(`${API_URL}/api/enrichment/nightly`, { enabled: nightlyForm.enabled, time: nightlyForm.time, spendCapUsd: Number(nightlyForm.spendCapUsd), inStockOnly: nightlyForm.inStockOnly });
      const n = r.data.nightly;
      setNightlyForm({ enabled: n.enabled, time: n.time, spendCapUsd: String(n.spendCapUsd), inStockOnly: n.inStockOnly, dirty: false });
      flash('success', n.enabled ? `Saved: the AI runs every day at ${n.time} (Madrid time), spending at most ${usd(n.spendCapUsd)} a night.` : 'Saved: the nightly run is switched off. Nothing runs by itself.');
      await loadStatus();
    } catch (e) {
      flash('error', errorText(e, 'The nightly settings could not be saved'));
    } finally {
      setSavingNightly(false);
    }
  };
  const nightlyLastWords = (n) => {
    if (!n?.last) return 'It has not been due yet since this was switched on.';
    const l = n.last;
    const day = l.day ? new Date(`${l.day}T12:00:00`).toLocaleDateString() : '—';
    if (l.outcome === 'started') {
      const run = n.lastRun && n.lastRun.id === l.runId ? n.lastRun : null;
      if (!run || RUN_IN_FLIGHT.includes(run.status)) return `${day}: run ${l.runId} started with ${l.products} products — still running.`;
      if (run.status === 'FAILED') return `${day}: run ${l.runId} FAILED — ${run.error || 'no reason recorded'}`;
      return `${day}: run ${l.runId} named ${run.applied_count ?? 0} of ${run.product_count} products, ${run.review_count ?? 0} waiting for you, ${usd(run.cost_usd_measured)}.`;
    }
    if (l.outcome === 'nothing_to_do') return `${day}: nothing to do — every product in stock already had a name or was waiting for you.`;
    if (l.outcome === 'refused') return `${day}: NOT run — ${l.message || 'the spend cap refused it'}`;
    return `${day}: could not start (attempt ${l.attempts}) — ${l.message || ''}`;
  };

  // ---- the prompt
  const openSave = () => {
    setNote(proposal && draft === proposal.body ? `Proposed by the AI from ${proposal.correctionsUsed} of my corrections: ${proposal.summary}`.slice(0, 1000) : '');
    setDiffError(null);
    setDiff({ mode: 'save', oldBody: activeBody, newBody: draft, oldLabel: `version ${prompts.active.version} (active)`, newLabel: `version ${nextVersion} (new)`, versionNumber: nextVersion });
  };
  const openRestore = async (version) => {
    try {
      const r = await axios.get(`${API_URL}/api/enrichment/prompts/${version.id}`);
      setDiffError(null);
      setDiff({ mode: 'restore', oldBody: activeBody, newBody: r.data.body, oldLabel: `version ${prompts.active.version} (active now)`, newLabel: `version ${version.version} (to restore)`, versionNumber: version.version, targetId: version.id });
    } catch (e) {
      flash('error', errorText(e, 'Could not load that version'));
    }
  };
  const confirmDiff = async () => {
    setDiffBusy(true);
    setDiffError(null);
    try {
      if (diff.mode === 'save') {
        const r = await axios.post(`${API_URL}/api/enrichment/prompts`, { body: draft, note });
        flash('success', `Saved as version ${r.data.saved.version}; it is now the active prompt. No product changed.`);
        setProposal(null);
      } else {
        const r = await axios.post(`${API_URL}/api/enrichment/prompts/${diff.targetId}/activate`);
        flash('success', `Version ${r.data.active.version} is the active prompt again. No product changed.`);
      }
      const p = await loadPrompts();
      setDraft(p.active?.body || '');
      setDiff(null);
      loadStatus();
    } catch (e) {
      setDiffError(errorText(e, 'The save failed'));
    } finally {
      setDiffBusy(false);
    }
  };
  const propose = async () => {
    if (!window.confirm('Ask the AI to propose changes to the prompt from your corrections?\n\nIt reads the names you typed over its earlier names and suggests the rules the guide is missing. The proposal lands in the editor and nothing is saved until you confirm it. This costs about $0.30–0.50 and takes a minute or two.')) return;
    setProposing(true);
    try {
      // A background job on the server (the model writes the whole body back); poll it.
      let job = (await axios.post(`${API_URL}/api/enrichment/prompts/propose`, {})).data;
      while (job.status === 'running') {
        await new Promise(resolve => setTimeout(resolve, 3000));
        job = (await axios.get(`${API_URL}/api/enrichment/prompts/propose/${job.id}`)).data;
      }
      if (job.status !== 'done') throw new Error(job.error || 'The proposal failed');
      setProposal(job.result);
      setDraft(job.result.body);
      flash('success', `${job.result.changes.length} change${job.result.changes.length === 1 ? '' : 's'} proposed from ${job.result.correctionsUsed} corrections (${usd(job.result.costUsd)}). Review them, then press Save.`);
    } catch (e) {
      flash('error', errorText(e, 'The proposal failed'));
    } finally {
      setProposing(false);
    }
  };

  // ---- runs
  const startRun = async () => {
    setRunError(null);
    const body = {
      scope: runForm.scope,
      dryRun: runForm.dryRun,
      direct: !runForm.dryRun && runForm.direct,
      grounding: runForm.grounding,
      inStock: runForm.inStock,
      fields: runForm.fields.length ? runForm.fields : undefined,
      vendor: runForm.vendor || undefined,
      brand: runForm.brand.trim() || undefined,
      search: runForm.search.trim() || undefined,
      limit: runForm.limit.trim() ? Number(runForm.limit) : undefined,
      threshold: Number(runForm.threshold),
      spendCapUsd: Number(runForm.spendCapUsd),
    };
    if (!runForm.dryRun && !window.confirm(`Start a REAL run${body.direct ? ' on the DIRECT lane (full price, answers in minutes)' : ' through the batch (half price, may wait up to 24 hours)'}?\n\nIt will ask the model about every product in this selection, spend up to ${usd(body.spendCapUsd)}, and write names and details that pass the threshold. Values a person typed are never touched. A name the model is not sure of waits for you instead.\n\nTick "Dry run" to see what it would do first.`)) return;
    setStarting(true);
    try {
      const r = await axios.post(`${API_URL}/api/enrichment/runs`, body);
      flash('success', `Run ${r.data.run.id} started: ${r.data.run.product_count} products in ${r.data.run.family_count} requests${r.data.run.dry_run ? ' (dry run — nothing will be written)' : r.data.run.filters?.direct ? ' (direct — minutes)' : ' (batch — may wait up to 24 hours)'}. Estimated at most ${usd(r.data.run.usage?.estimate_usd)}.`);
      await loadRuns();
      await loadStatus();
    } catch (e) {
      setRunError(errorText(e, 'The run could not start'));
    } finally {
      setStarting(false);
    }
  };
  const cancelRun = async (run) => {
    const running = run.status === 'RUNNING';
    if (!window.confirm(running
      ? `Stop run ${run.id}?\n\nThe requests already in flight finish and are applied; nothing more is sent. The products never sent stay as they are and the next run picks them up.`
      : `Cancel run ${run.id}?\n\nAnswers Anthropic has already finished still come back and are applied; the rest are dropped.`)) return;
    try {
      await axios.post(`${API_URL}/api/enrichment/runs/${run.id}/cancel`);
      flash('success', running ? `Stop requested for run ${run.id} — it ends once the requests in flight have answered.` : `Cancel requested for run ${run.id}.`);
      loadRuns();
    } catch (e) {
      flash('error', errorText(e, 'Could not cancel the run'));
    }
  };

  // ---- what waits
  const settle = async (productIds, action) => {
    if (!productIds.length) return;
    if (productIds.length > 50 && !window.confirm(`${action === 'take' ? 'Take' : 'Reject'} ${productIds.length} proposals?\n\nThat is more than fits on one screen.`)) return;
    setSettling(true);
    try {
      const r = await axios.post(`${API_URL}/api/enrichment/waiting`, { productIds, action });
      flash('success', action === 'take'
        ? `${r.data.taken} taken${r.data.skipped ? `, ${r.data.skipped} skipped (decided by a person since, or not valid)` : ''}. Taken values show as AI, reviewed.`
        : `${r.data.rejected} rejected${r.data.skipped ? `, ${r.data.skipped} skipped` : ''}. The next "fill what is missing" run proposes afresh.`);
      await loadWaiting();
      await loadStatus();
    } catch (e) {
      flash('error', errorText(e, 'That did not work'));
    } finally {
      setSettling(false);
    }
  };
  const toggleTick = (id) => setTicked(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const EDITABLE_HELD = ['name', 'brand', 'category', 'platform', 'region']; // the text fields; lists are picked by name in the Edit window
  const startEditHeld = (item) => {
    const values = {};
    for (const h of item.held) if (EDITABLE_HELD.includes(h.field)) values[h.field] = h.value == null ? '' : String(h.value);
    setEditing({ productId: item.productId, values });
  };
  const saveEditHeld = async () => {
    if (!editing) return;
    const values = {};
    for (const [k, v] of Object.entries(editing.values)) if (v.trim() !== '') values[k] = v.trim();
    if (!Object.keys(values).length) { flash('error', 'Write a value, or use Reject.'); return; }
    setSettling(true);
    try {
      const r = await axios.post(`${API_URL}/api/enrichment/waiting`, { productIds: [editing.productId], action: 'edit', values });
      const p = r.data.products[0];
      const words = p ? Object.entries(p.fields).map(([k, how]) => `${k} ${how === 'taken' ? 'taken as proposed' : 'saved as yours'}`).join(', ') : 'nothing changed';
      flash('success', `Saved: ${words}. What the AI proposed is kept with it, so the next run learns from your wording.`);
      setEditing(null);
      await loadWaiting();
      await loadStatus();
    } catch (e) {
      flash('error', errorText(e, 'That did not save'));
    } finally {
      setSettling(false);
    }
  };

  const input = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm';
  const card = 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6 space-y-4';
  const primary = 'px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg text-sm font-medium disabled:opacity-50';
  const secondary = 'px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm disabled:opacity-50';

  return (
    <div className="space-y-6" data-testid="enrichment-tab">
      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'}`}>{message.text}</div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-prose">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI naming and catalogue details</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">The AI names products your way and fills in their details, following the prompt below. Anything a person typed is never overwritten; anything it is not sure of waits for you here.</p>
        </div>
        <button type="button" onClick={onHelp} className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"><HelpCircle className="w-4 h-4" /> How this works</button>
      </div>

      {statusError && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-sm">{statusError}</div>}

      {/* ---- key and model ---- */}
      <div className={card} data-testid="enrichment-model">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="font-semibold text-gray-900 dark:text-white">API key and model</h4>
          <button type="button" onClick={() => loadModels(true)} className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"><RefreshCw className="w-4 h-4" /> Check again</button>
        </div>
        {status && !status.active && (
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200 text-sm max-w-prose">
            <b>The AI is switched off on this server:</b> there is no Anthropic API key in its settings file. No run can start and nothing is sent. The prompt below can still be edited.
          </div>
        )}
        {models && status?.active && (models.ok
          ? <p className="text-sm text-green-700 dark:text-green-400" data-testid="key-status">✅ Key accepted — {models.models.length} models available to it. Checked {when(models.checkedAt)}.</p>
          : <p className="text-sm text-red-700 dark:text-red-400 max-w-prose" data-testid="key-status">⛔ <b>The key is not working.</b> {models.error} A run on this server would fail at once; nothing is sent until this is fixed.</p>)}
        {status?.grounding && (
          <p className="text-sm text-gray-700 dark:text-gray-300 max-w-prose" data-testid="grounding-status">
            {status.grounding.configured
              ? <>🎮 <b>Game database (IGDB): on.</b> {status.grounding.titles == null ? 'Its table is missing on this server — run the IGDB migration.' : `${status.grounding.titles} game titles looked up so far, ${status.grounding.matched} found.`}{status.grounding.lastError ? <span className="block text-amber-700 dark:text-amber-300">Last problem: {status.grounding.lastError}</span> : null}</>
              : <>🎮 <b>Game database (IGDB): off</b> — no IGDB credentials in this server's settings file (<span className="font-mono">IGDB_CLIENT_ID</span> / <span className="font-mono">IGDB_CLIENT_SECRET</span>). Game keys are named from the supplier's row alone.</>}
          </p>
        )}
        {status?.images && !status.images.error && (
          <div className="text-sm text-gray-700 dark:text-gray-300 max-w-prose space-y-1" data-testid="images-status">
            <p>🖼️ <b>Pictures.</b> {status.images.products_vendor.toLocaleString()} products carry a supplier's picture, {status.images.brands_seeded.toLocaleString()} brands have one through the brand library, {status.images.brands_manual.toLocaleString()} brands and {status.images.products_manual.toLocaleString()} products show one a person uploaded ({status.images.uploads.toLocaleString()} uploads).{status.grounding?.configured && status.grounding.covers != null ? ` The game database has a cover for ${status.grounding.covers.toLocaleString()} of the ${status.grounding.matched.toLocaleString()} titles it knows${status.grounding.coversUnchecked ? ` (${status.grounding.coversUnchecked.toLocaleString()} still to check)` : ''}.` : ''}</p>
            <label className={`flex items-start gap-2 ${status.grounding?.configured ? 'cursor-pointer' : 'opacity-50'}`}>
              <input data-testid="images-igdb-switch" type="checkbox" className="mt-1 rounded" checked={Boolean(status.images.igdbEnabled)} disabled={!status.grounding?.configured}
                onChange={async (e) => {
                  const enabled = e.target.checked;
                  if (enabled && !window.confirm('Use the game database\'s covers as the picture of game keys that have none of their own?\n\nOnly switch this on once IGDB has agreed to our storing and serving its cover images.')) return;
                  try { await axios.put(`${API_URL}/api/images/igdb`, { enabled }); await loadStatus(); } catch (err) { window.alert(errorText(err, 'The switch could not be changed')); }
                }} />
              <span><b className="text-gray-900 dark:text-white">Use the game database's covers as game pictures</b><span className="block text-gray-600 dark:text-gray-400">A game key with no picture of its own gets the cover (box art) of its title. Off until IGDB has agreed to our storing and serving its cover images; a supplier's picture always comes first.</span></span>
            </label>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Model</label>
            <select data-testid="model-select" value={modelChoice} onChange={(e) => setModelChoice(e.target.value)} disabled={!models?.ok} className={`${input} max-w-md`}>
              {models?.ok && models.currentListed === false && <option value={models.current}>{models.current} — no longer offered to this key</option>}
              {(models?.models || []).map(m => <option key={m.id} value={m.id}>{m.displayName} — {m.id}{m.id === models.defaultModel ? ' (default)' : ''}</option>)}
              {!models?.ok && status?.model && <option value={status.model}>{status.model}</option>}
            </select>
            {models?.ok && models.currentListed === false && (
              <p className="text-sm text-red-700 dark:text-red-400 max-w-prose"><s>{models.current}</s> is the saved model but Anthropic no longer offers it to this key. A run is refused until another is chosen.</p>
            )}
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={saveModel} disabled={!models?.ok || savingModel || modelChoice === models?.current} className={primary}>{savingModel ? 'Saving…' : 'Use this model'}</button>
              {models?.ok && modelChoice !== models.current && <span className="self-center text-xs text-gray-500">In use now: {models.current}</span>}
            </div>
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
            {chosen ? (
              <>
                <div><b>{chosen.displayName}</b> <span className="text-gray-500">({chosen.id})</span></div>
                <div>Released {chosen.createdAt ? new Date(chosen.createdAt).toLocaleDateString() : '—'} · reads up to {tokensLabel(chosen.maxInputTokens)} tokens · writes up to {tokensLabel(chosen.maxOutputTokens)}</div>
                <div>
                  {[['adaptive thinking', chosen.supports.adaptiveThinking], ['structured answers', chosen.supports.structuredOutputs], ['effort: high', chosen.supports.effortHigh]].map(([what, ok]) => (
                    <span key={what} className={`inline-block mr-2 mb-1 px-2 py-0.5 rounded text-xs ${ok === true ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : ok === false ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {ok === true ? '✓' : ok === false ? '✗' : '?'} {what}
                    </span>
                  ))}
                  {chosen.supports.adaptiveThinking === false || chosen.supports.structuredOutputs === false ? <span className="block text-red-700 dark:text-red-400">This model lacks something the engine sends; a run on it would fail at its first request, before anything is written.</span> : null}
                </div>
                <div className="text-gray-500 dark:text-gray-400 max-w-prose">
                  {models.pricedModels?.includes(chosen.id)
                    ? 'Its price is in the engine\'s table, so the spend-cap estimate is accurate.'
                    : 'Its price is not in the engine\'s table (Anthropic does not publish prices through the API), so the spend-cap estimate uses the dearest known rate — it can only be too cautious. The real cost is measured after each run.'}
                </div>
              </>
            ) : <p className="text-gray-500">Choose a model to see its details.</p>}
          </div>
        </div>
      </div>

      {/* ---- the prompt ---- */}
      <div className={card} data-testid="enrichment-prompt">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white">The prompt — your instructions and style guide</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-prose">Active: <b>version {prompts?.active?.version ?? '—'}</b>{prompts?.versions?.find(v => v.isActive)?.note ? ` — ${prompts.versions.find(v => v.isActive).note}` : ''}. Every save makes a new version; older ones can be restored.</p>
          </div>
          <button type="button" onClick={() => setShowVersions(s => !s)} className={secondary}>{showVersions ? 'Hide' : 'Show'} version history ({prompts?.versions?.length ?? 0})</button>
        </div>

        {showVersions && prompts && (
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg" data-testid="prompt-versions">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400 text-xs">
                <tr><th className="text-left px-3 py-2">Version</th><th className="text-left px-3 py-2">Saved</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">Note</th><th className="text-left px-3 py-2">Length</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {prompts.versions.map(v => (
                  <tr key={v.id} data-version={v.version} className={v.isActive ? 'bg-amber-50/60 dark:bg-amber-900/20' : ''}>
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">v{v.version}{v.isActive && <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-[#ffd71f] text-[#080300]">active</span>}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{when(v.createdAt)}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{v.createdBy || '—'}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300 max-w-md">{v.note || <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{v.bodyChars.toLocaleString()} chars</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{!v.isActive && <button type="button" onClick={() => openRestore(v)} className="inline-flex items-center gap-1 text-sm text-amber-700 dark:text-amber-300 hover:underline"><RotateCcw className="w-4 h-4" /> Restore…</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {proposal && draft === proposal.body && (
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-sm text-blue-900 dark:text-blue-100 space-y-2" data-testid="proposal-banner">
            <div><b>The AI proposes {proposal.changes.length} change{proposal.changes.length === 1 ? '' : 's'}</b> from {proposal.correctionsUsed} of your corrections ({usd(proposal.costUsd)}). Nothing is saved yet — the editor below holds the proposal; press Save to review it line by line, or Discard.</div>
            <p className="max-w-prose">{proposal.summary}</p>
            <ul className="list-disc pl-5 space-y-1 max-w-prose">{proposal.changes.map((c, i) => <li key={i}><b>{c.rule}</b> <span className="text-blue-800/80 dark:text-blue-200/80">— {c.evidence}</span></li>)}</ul>
            <button type="button" onClick={() => { setProposal(null); setDraft(activeBody); }} className={secondary}>Discard the proposal</button>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Editable text (instructions + the style guide)</label>
          <textarea data-testid="prompt-body" value={draft} onChange={(e) => setDraft(e.target.value)} rows={18} spellCheck={false}
            className={`${input} font-mono text-xs leading-5 resize-y`} />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{draft.length.toLocaleString()} characters{draftDirty ? ' — unsaved changes' : ' — matches the active version'}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button data-testid="prompt-save" type="button" onClick={openSave} disabled={!prompts || !draftDirty} className={primary}>Save as version {nextVersion}…</button>
          <button type="button" onClick={() => { setDraft(activeBody); setProposal(null); }} disabled={!draftDirty} className={secondary}>Undo my edits</button>
          <button data-testid="prompt-propose" type="button" onClick={propose} disabled={proposing || !status?.active} className={secondary} title={!status?.active ? 'Needs the API key' : ''}>{proposing ? 'Asking the AI… (a minute or two)' : 'Let the AI propose changes from my corrections…'}</button>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <button type="button" onClick={() => setShowScaffold(s => !s)} className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
            {showScaffold ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} The fixed part the model also receives (read-only)
          </button>
          {showScaffold && prompts && (
            <div className="mt-2 space-y-2 text-sm" data-testid="prompt-scaffold">
              <p className="text-gray-600 dark:text-gray-400 max-w-prose">This part is code, not text you edit: it tells the model the exact shape of its answer, the closed lists it may pick from, and that no name may say which supplier we buy from. Changing it would not make names better or worse — it would break every request — so it is shown here only so you can see the whole of what the model is told. Scaffold version {prompts.scaffold.version}.</p>
              <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-5 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">{prompts.scaffold.text}{'\n\n'}{prompts.scaffold.vocabularies}</pre>
              <details><summary className="cursor-pointer text-gray-600 dark:text-gray-400">The answer shape (JSON schema)</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-xs leading-5 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">{JSON.stringify(prompts.scaffold.schema, null, 2)}</pre>
              </details>
            </div>
          )}
        </div>
      </div>

      {/* ---- run ---- */}
      <div className={card} data-testid="enrichment-run">
        <h4 className="font-semibold text-gray-900 dark:text-white">Run the AI</h4>
        {status?.runInFlight && (
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100 text-sm">Run {status.runInFlight.id} is {RUN_STATUS_WORDS[status.runInFlight.status]?.toLowerCase() || status.runInFlight.status}. One run at a time — the next can start when it finishes.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="space-y-2">
              {ENRICHMENT_SCOPES.map(s => (
                <label key={s.key} className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="enrichment-scope" value={s.key} checked={runForm.scope === s.key} onChange={() => setRunForm({ ...runForm, scope: s.key })} className="mt-1" />
                  <span className="text-sm"><b className="text-gray-900 dark:text-white">{s.label}</b><span className="block text-gray-600 dark:text-gray-400 max-w-prose">{s.help}</span></span>
                </label>
              ))}
            </div>
            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
              <input data-testid="run-dry" type="checkbox" checked={runForm.dryRun} onChange={(e) => setRunForm({ ...runForm, dryRun: e.target.checked })} className="mt-1 rounded" />
              <span className="text-sm"><b className="text-gray-900 dark:text-white">Dry run</b><span className="block text-gray-600 dark:text-gray-400 max-w-prose">Ask the model and show what it would do, but write nothing to any product. Up to {status?.defaults?.maxDryRunProducts ?? 100} products. Untick for a real run.</span></span>
            </label>
            <label className={`flex items-start gap-2 ${runForm.dryRun ? 'opacity-50' : ''}`}>
              <input data-testid="run-direct" type="checkbox" checked={runForm.direct} disabled={runForm.dryRun} onChange={(e) => setRunForm({ ...runForm, direct: e.target.checked })} className="mt-1 rounded" />
              <span className="text-sm"><b className="text-gray-900 dark:text-white">Direct lane</b><span className="block text-gray-600 dark:text-gray-400 max-w-prose">Answers in minutes at full price (about twice the batch). Unticked, a real run goes through Anthropic's batch queue: half price, but it may wait up to 24 hours and a request can expire unanswered. There is no product limit on a direct run: the spend cap is what stops it. After the first request, {status?.defaults?.directConcurrency ?? 4} are sent at a time — a whole supplier takes a couple of hours. A running direct run can be stopped from the history below: what is in flight finishes and is applied, nothing more is sent.</span></span>
            </label>
            <label className={`flex items-start gap-2 ${status?.grounding?.configured ? 'cursor-pointer' : 'opacity-50'}`}>
              <input data-testid="run-grounding" type="checkbox" checked={runForm.grounding && Boolean(status?.grounding?.configured)} disabled={!status?.grounding?.configured} onChange={(e) => setRunForm({ ...runForm, grounding: e.target.checked })} className="mt-1 rounded" />
              <span className="text-sm"><b className="text-gray-900 dark:text-white">Game facts from the game database (IGDB)</b><span className="block text-gray-600 dark:text-gray-400 max-w-prose">For game keys only: the publisher (when the supplier gave none), the official spelling of the title, and what tells editions apart are looked up once per title and shown to the AI beside the supplier's row. The supplier's own platform, region, countries and languages always win — the AI is told so, and the engine refuses to write over them.{status?.grounding?.configured ? '' : ' Switched off on this server: no IGDB credentials in its settings file.'}</span></span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="run-instock" type="checkbox" checked={runForm.inStock} onChange={(e) => setRunForm({ ...runForm, inStock: e.target.checked })} className="mt-1 rounded" />
              <span className="text-sm"><b className="text-gray-900 dark:text-white">Only products in stock</b><span className="block text-gray-600 dark:text-gray-400 max-w-prose">Leave out what cannot be sold right now (a stock count of zero). Products with no count at all — most gift cards — are always included. Untick to name everything, out-of-stock games too.</span></span>
            </label>
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50" data-testid="run-fields">
              <div className="text-sm"><b className="text-gray-900 dark:text-white">Details the run may write</b><span className="block text-gray-600 dark:text-gray-400 max-w-prose">Untick a detail to leave it alone on every product — for example, fill only the brands of game keys. Nothing ticked means all of them.</span></div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {(status?.fields || ['name', 'brand', 'platform', 'category', 'languages', 'regions', 'region']).map(f => (
                  <label key={f} className="inline-flex items-center gap-1 text-sm cursor-pointer">
                    <input type="checkbox" data-testid={`run-field-${f}`} checked={!runForm.fields.length || runForm.fields.includes(f)} onChange={(e) => {
                      const all = status?.fields || ['name', 'brand', 'platform', 'category', 'languages', 'regions', 'region'];
                      const current = runForm.fields.length ? runForm.fields : all;
                      const next = e.target.checked ? [...new Set([...current, f])] : current.filter(x => x !== f);
                      setRunForm({ ...runForm, fields: next.length === all.length ? [] : next });
                    }} className="rounded" />
                    {RUN_FIELD_LABELS[f] || f}
                  </label>
                ))}
              </div>
              {runForm.fields.length > 0 && runForm.fields.length < (status?.fields || []).length && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Only {runForm.fields.map(f => RUN_FIELD_LABELS[f] || f).join(', ')} will be written; the rest is left as it is.</p>}
              {runForm.fields.length === 0 && false}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Supplier</label>
              <select value={runForm.vendor} onChange={(e) => setRunForm({ ...runForm, vendor: e.target.value })} className={input}>
                <option value="">All suppliers</option>
                {VENDORS.map(v => <option key={v} value={v}>{VENDOR_LABELS[v]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brand</label>
              <input type="text" value={runForm.brand} onChange={(e) => setRunForm({ ...runForm, brand: e.target.value })} placeholder="any" className={input} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Supplier's name contains</label>
              <input type="text" value={runForm.search} onChange={(e) => setRunForm({ ...runForm, search: e.target.value })} placeholder="any" className={input} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">At most (products)</label>
              <input data-testid="run-limit" type="number" min="1" step="1" value={runForm.limit} onChange={(e) => setRunForm({ ...runForm, limit: e.target.value })} placeholder="all" className={input} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confidence to apply a name</label>
              <input type="number" min="0" max="1" step="0.01" value={runForm.threshold} onChange={(e) => setRunForm({ ...runForm, threshold: e.target.value })} className={input} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Spend cap (USD)</label>
              <input type="number" min="1" step="1" value={runForm.spendCapUsd} onChange={(e) => setRunForm({ ...runForm, spendCapUsd: e.target.value })} className={input} />
            </div>
          </div>
        </div>
        {runError && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 text-sm" data-testid="run-error">{runError}</div>}
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="run-start" type="button" onClick={startRun} disabled={starting || !status?.active || Boolean(status?.runInFlight)} className={`${primary} inline-flex items-center gap-1`}><Play className="w-4 h-4" /> {starting ? 'Starting…' : runForm.dryRun ? 'Start a dry run' : 'Start a real run'}</button>
          <span className="text-xs text-gray-500 dark:text-gray-400 max-w-prose">A name under the confidence figure, or one the model flags, is not written: it waits for you below. The spend cap is checked against a cautious estimate before anything is sent.</span>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h5 className="font-medium text-gray-900 dark:text-white">Run history</h5>
            <button type="button" onClick={() => loadRuns()} className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"><RefreshCw className="w-4 h-4" /> Refresh</button>
          </div>
          {runs.length === 0 ? <p className="text-sm text-gray-500">No run yet.</p> : (
            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg" data-testid="run-history">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400 text-xs">
                  <tr><th className="text-left px-3 py-2">Run</th><th className="text-left px-3 py-2">Started</th><th className="text-left px-3 py-2">What</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">Products</th><th className="text-right px-3 py-2">Changed</th><th className="text-right px-3 py-2">Waiting</th><th className="text-right px-3 py-2">Cost</th><th className="text-left px-3 py-2">Model / prompt</th><th className="px-3 py-2"></th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {runs.map(r => (
                    <tr key={r.id} data-run={r.id}>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">{r.id}{r.dry_run ? <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">dry</span> : r.filters?.direct ? <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300" title="Every request as a direct call — full price, minutes">direct</span> : null}{r.filters?.nightly ? <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300" title="Started by itself, at the nightly time" data-testid={`run-nightly-${r.id}`}>nightly</span> : null}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{when(r.created_at)}</td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{ENRICHMENT_SCOPES.find(s => s.key === r.scope)?.label || r.scope}{r.vendor_filter ? ` · ${VENDOR_LABELS[r.vendor_filter] || r.vendor_filter}` : ''}{r.filters?.limit ? ` · ≤${r.filters.limit}` : ''}</td>
                      <td className="px-3 py-2 whitespace-nowrap"><span className={`px-2 py-0.5 rounded text-xs ${RUN_STATUS_CLASS[r.status] || 'bg-gray-100 text-gray-700'}`}>{RUN_STATUS_WORDS[r.status] || r.status}</span>{r.error && <span className="block text-xs text-red-600 dark:text-red-400 max-w-xs whitespace-normal" title={r.error}>{r.error.length > 90 ? `${r.error.slice(0, 90)}…` : r.error}</span>}</td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{r.product_count}</td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{r.applied_count ?? '—'}</td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{r.review_count ?? '—'}</td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{r.cost_usd_measured != null ? usd(r.cost_usd_measured) : r.usage?.estimate_usd != null ? `≤ ${usd(r.usage.estimate_usd)}` : '—'}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">{r.model}<br />prompt #{r.prompt_version_id}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button type="button" onClick={() => setOpenRun(r.id)} className="text-sm text-amber-700 dark:text-amber-300 hover:underline">Details</button>
                        {r.status === 'SUBMITTED' && <button type="button" onClick={() => cancelRun(r)} className="ml-3 text-sm text-red-700 dark:text-red-400 hover:underline">Cancel</button>}
                        {r.status === 'RUNNING' && <button data-testid={`run-stop-${r.id}`} type="button" onClick={() => cancelRun(r)} className="ml-3 text-sm text-red-700 dark:text-red-400 hover:underline">Stop</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ---- every night (S12) ---- */}
      <div className={card} data-testid="enrichment-nightly">
        <div className="max-w-prose">
          <h4 className="font-semibold text-gray-900 dark:text-white">Every night</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">New products arrive with every supplier update. Once a day, at the time below, the AI runs <b>Fill what is missing</b> by itself — on the direct lane, in-stock products only, under its own spend cap — so the catalogue never falls behind. Anything a person typed is never touched; a name it is not sure of waits for you below, as after any run.</p>
        </div>
        {status && !status.active && <p className="text-sm text-amber-800 dark:text-amber-200 max-w-prose">The AI is switched off on this server (no API key), so nothing runs at night whatever is set here.</p>}
        {nightlyForm ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                <input data-testid="nightly-enabled" type="checkbox" checked={nightlyForm.enabled} onChange={(e) => editNightly({ enabled: e.target.checked })} className="mt-1 rounded" />
                <span className="text-sm"><b className="text-gray-900 dark:text-white">Run every night</b><span className="block text-gray-600 dark:text-gray-400 max-w-prose">Unticked, nothing runs by itself; you start every run from the form above.</span></span>
              </label>
              <label className={`flex items-start gap-2 cursor-pointer ${nightlyForm.enabled ? '' : 'opacity-50'}`}>
                <input data-testid="nightly-instock" type="checkbox" checked={nightlyForm.inStockOnly} onChange={(e) => editNightly({ inStockOnly: e.target.checked })} className="mt-1 rounded" />
                <span className="text-sm"><b className="text-gray-900 dark:text-white">Only products in stock</b><span className="block text-gray-600 dark:text-gray-400 max-w-prose">The same choice as on the form above: what has a stock count of zero is left for a run you start yourself.</span></span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">At (Madrid time)</label>
                <input data-testid="nightly-time" type="time" value={nightlyForm.time} onChange={(e) => editNightly({ time: e.target.value })} disabled={!nightlyForm.enabled} className={`${input} max-w-[10rem]`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Spend cap per night (USD)</label>
                <input data-testid="nightly-cap" type="number" min="1" max="1000" step="1" value={nightlyForm.spendCapUsd} onChange={(e) => editNightly({ spendCapUsd: e.target.value })} disabled={!nightlyForm.enabled} className={`${input} max-w-[10rem]`} />
              </div>
              <div className="col-span-2 flex flex-wrap items-center gap-2">
                <button data-testid="nightly-save" type="button" onClick={saveNightly} disabled={savingNightly || !nightlyForm.dirty} className={primary}>{savingNightly ? 'Saving…' : 'Save'}</button>
                {nightlyForm.dirty && <span className="text-xs text-gray-500 dark:text-gray-400">Not saved yet.</span>}
              </div>
            </div>
          </div>
        ) : <p className="text-sm text-gray-500">Loading…</p>}
        {status?.nightly && (
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1 max-w-prose" data-testid="nightly-status">
            <p data-testid="nightly-next">
              {!status.nightly.enabled ? <>⏸ <b>Switched off.</b> No run starts by itself.</>
                : status.nightly.next?.due ? <>⏳ <b>Due now</b> — it starts as soon as no other run is in flight.</>
                : <>🕓 <b>Next:</b> {status.nightly.next?.day ? new Date(`${status.nightly.next.day}T12:00:00`).toLocaleDateString() : '—'} at {status.nightly.next?.time} (Madrid time), spending at most {usd(status.nightly.spendCapUsd)}.</>}
            </p>
            <p data-testid="nightly-last" className={status.nightly.last?.outcome === 'refused' || status.nightly.last?.outcome === 'error' || (status.nightly.last?.outcome === 'started' && status.nightly.lastRun?.id === status.nightly.last?.runId && status.nightly.lastRun?.status === 'FAILED') ? 'text-red-700 dark:text-red-400' : ''}>
              <b>Last night:</b> {nightlyLastWords(status.nightly)}
            </p>
          </div>
        )}
      </div>

      {/* ---- waiting ---- */}
      <div className={card} data-testid="enrichment-waiting">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white">Waiting for you{waiting ? ` (${waiting.count})` : ''}</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-prose">Names and details the AI proposed but would not apply on its own. Choose <b>Name waiting</b> and <b>In stock only</b> to see the products a customer sees under the supplier's wording today — the ones that matter most. <b>Take</b> writes the value as the AI's, marked reviewed. <b>Edit</b> lets you write it your way — your wording is saved as yours and the AI's proposal is kept with it, so the next run learns from the correction. <b>Reject</b> drops it; the next "fill what is missing" run tries again.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={waitingVendor} onChange={(e) => { setWaitingVendor(e.target.value); loadWaiting(e.target.value, waitingField, waitingInStock).catch(() => {}); }} className={`${input} w-auto`}>
              <option value="">All suppliers</option>
              {VENDORS.map(v => <option key={v} value={v}>{VENDOR_LABELS[v]}</option>)}
            </select>
            <select data-testid="waiting-field" value={waitingField} onChange={(e) => { setWaitingField(e.target.value); loadWaiting(waitingVendor, e.target.value, waitingInStock).catch(() => {}); }} className={`${input} w-auto`} title="Show only the rows where this detail waits">
              <option value="">Anything waiting</option>
              {(status?.fields || ['name', 'brand', 'platform', 'category', 'languages', 'regions', 'region']).map(f => <option key={f} value={f}>{RUN_FIELD_LABELS[f] || f} waiting</option>)}
            </select>
            <label className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 cursor-pointer" title="Leave out products with a stock count of zero">
              <input data-testid="waiting-instock" type="checkbox" checked={waitingInStock} onChange={(e) => { setWaitingInStock(e.target.checked); loadWaiting(waitingVendor, waitingField, e.target.checked).catch(() => {}); }} className="rounded" /> In stock only
            </label>
            <button type="button" onClick={() => loadWaiting()} className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"><RefreshCw className="w-4 h-4" /> Refresh</button>
          </div>
        </div>
        {waiting && waiting.items.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="inline-flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={ticked.size > 0 && ticked.size === waiting.items.length} onChange={(e) => setTicked(e.target.checked ? new Set(waiting.items.map(i => i.productId)) : new Set())} className="rounded" /> Select all shown ({waiting.items.length})</label>
            <button data-testid="waiting-take-ticked" type="button" disabled={!ticked.size || settling} onClick={() => settle([...ticked], 'take')} className={primary}>Take {ticked.size || ''}</button>
            <button type="button" disabled={!ticked.size || settling} onClick={() => settle([...ticked], 'reject')} className={secondary}>Reject {ticked.size || ''}</button>
          </div>
        )}
        {!waiting ? <p className="text-sm text-gray-500">Loading…</p> : waiting.items.length === 0 ? <p className="text-sm text-gray-500" data-testid="waiting-empty">Nothing is waiting. 🎉</p> : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg" data-testid="waiting-list">
            {waiting.items.map(item => (
              <li key={item.productId} data-sku={item.sku} className="p-3 flex flex-col sm:flex-row sm:items-start gap-3">
                <input type="checkbox" checked={ticked.has(item.productId)} onChange={() => toggleTick(item.productId)} className="mt-1 rounded shrink-0" aria-label={`Select ${item.sku}`} />
                <div className="flex-1 min-w-0 text-sm space-y-1">
                  <div className="text-xs text-gray-500 dark:text-gray-400"><span className="font-mono">{item.sku}</span> · {VENDOR_LABELS[item.vendor] || item.vendor} · run {item.runId}{typeof item.confidence === 'number' ? ` · confidence ${Math.round(item.confidence * 100)}%` : ''}</div>
                  <div className="text-gray-600 dark:text-gray-400 break-words">Supplier's name: {item.vendorName}</div>
                  {item.supplier && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 break-words" data-testid="waiting-supplier">
                      Supplier sends: {['brand', 'category', 'platform', 'region', 'regions', 'languages'].map(k => {
                        const v = item.supplier[k];
                        const text = Array.isArray(v) ? (v.length ? (v.length > 12 ? `${v.length} codes` : v.join(', ')) : 'none') : (v == null || String(v).trim() === '' ? 'none' : String(v));
                        return `${k === 'regions' ? 'countries' : k} ${text}`;
                      }).join(' · ')}
                    </div>
                  )}
                  {item.currentName && <div className="text-gray-600 dark:text-gray-400 break-words">{item.runScope === 'restyle-manual' ? 'Your name now' : 'Our name now'}: {item.currentName}{item.runScope === 'restyle-manual' && <span className="ml-1 text-xs text-amber-700 dark:text-amber-300" title="This run brings the names you typed to the style guide. Taking the proposal keeps your original beside it; rejecting it keeps your name as it is.">(re-style of your name)</span>}</div>}
                  {item.held.map(h => (
                    <div key={h.field} className="flex flex-wrap items-baseline gap-2">
                      <span className="text-gray-500 dark:text-gray-400 capitalize">{h.field === 'region' ? 'region' : h.field}:</span>
                      {editing && editing.productId === item.productId && h.field in editing.values ? (
                        <input
                          type="text"
                          value={editing.values[h.field]}
                          onChange={(e) => setEditing({ ...editing, values: { ...editing.values, [h.field]: e.target.value } })}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveEditHeld(); } else if (e.key === 'Escape') { e.preventDefault(); setEditing(null); } }}
                          aria-label={`Your ${h.field} for ${item.sku}`}
                          data-testid={`waiting-edit-${h.field}`}
                          className={`${input} max-w-xl`}
                          autoFocus={h.field === 'name'}
                        />
                      ) : (
                        <b className="text-gray-900 dark:text-white break-words">{Array.isArray(h.value) ? (h.value.length ? h.value.join(', ') : h.field === 'regions' ? 'Worldwide' : '(none given — nothing to take)') : String(h.value)}</b>
                      )}
                      <span className={`px-1.5 py-0.5 rounded text-xs ${h.takeable ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'}`} title={(HELD_REASON[h.disposition] || {}).help}>{(HELD_REASON[h.disposition] || { label: h.disposition }).label}</span>
                      {h.runId && h.runId !== item.runId && <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="held-run">run {h.runId}</span>}
                    </div>
                  ))}
                  {editing && editing.productId === item.productId && <p className="text-xs text-gray-500 dark:text-gray-400 max-w-prose">Change the text to what it should be and save. A field you leave as proposed is taken as the AI's; a field you change is saved as yours, and what the AI proposed is kept with it. Enter saves, Esc cancels.</p>}
                  {item.grounding && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 break-words" data-testid="waiting-grounding">
                      Game database said: {[['official title', item.grounding.official_title], ['publisher', (item.grounding.publishers || []).join(' / ')], ['kind', item.grounding.kind], ['edition', item.grounding.edition], ['base game', item.grounding.base_game], ['year', item.grounding.first_release_year], ['languages', (item.grounding.languages || []).join(', ')], ['platforms', (item.grounding.platforms || []).join(', ')]]
                        .filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => `${k} ${v}`).join(' · ')}{item.grounding.matched_on ? ' · (facts of the base game)' : ''}
                    </div>
                  )}
                  {item.publisherHint && (
                    <div className="text-xs text-indigo-700 dark:text-indigo-300 break-words" data-testid="waiting-publisher-hint">
                      Game database names the publisher: <b>{item.publisherHint.publishers.join(' / ')}</b>{item.publisherHint.officialTitle ? ` (for "${item.publisherHint.officialTitle}")` : ''} — shown for you only; the supplier's brand stands until you type one.
                    </div>
                  )}
                  {item.notes && <div className="text-xs text-gray-500 dark:text-gray-400 italic break-words">Model's note: {item.notes}</div>}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {editing && editing.productId === item.productId ? (
                    <>
                      <button type="button" data-testid="waiting-edit-save" disabled={settling} onClick={saveEditHeld} className={`${primary} py-1`}>Save as mine</button>
                      <button type="button" disabled={settling} onClick={() => setEditing(null)} className={`${secondary} py-1`}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button type="button" disabled={settling || !item.held.some(h => h.takeable)} onClick={() => settle([item.productId], 'take')} className={`${primary} py-1`}>Take</button>
                      <button type="button" data-testid="waiting-edit" disabled={settling || !item.held.some(h => EDITABLE_HELD.includes(h.field))} onClick={() => startEditHeld(item)} className={`${secondary} py-1`} title="Write the value yourself; the AI's proposal is kept as what it wrote">Edit</button>
                      <button type="button" disabled={settling} onClick={() => settle([item.productId], 'reject')} className={`${secondary} py-1`}>Reject</button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {waiting && waiting.count > waiting.items.length && <p className="text-xs text-gray-500">Showing the first {waiting.items.length} of {waiting.count}. Take or reject these to see more.</p>}
      </div>

      {diff && <PromptDiffModal {...diff} note={note} onNote={setNote} busy={diffBusy} error={diffError} onConfirm={confirmDiff} onClose={() => (diffBusy ? null : setDiff(null))} />}
      {openRun && <RunDetailModal runId={openRun} onClose={() => setOpenRun(null)} />}
    </div>
  );
};

const Configuration = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('terminals');
  const [showEnrichmentHelp, setShowEnrichmentHelp] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Legacy config state (for backward compatibility)
  const [config, setConfig] = useState({
    timeout: '30000',
    rateLimit: '100',
    retryOnFailure: true
  });

  // Bamboo config state
  const [bambooConfig, setBambooConfig] = useState({
    clientId: '', clientSecret: '', baseUrl: 'https://api.bamboocardportal.com',
    accountId: '', sandboxMode: 'true', catalogSyncInterval: '15', orderPollInterval: '10'
  });
  const [bambooAccounts, setBambooAccounts] = useState([]);
  const [bambooCatalogStatus, setBambooCatalogStatus] = useState(null);
  const [testingBamboo, setTestingBamboo] = useState(false);
  const [syncingBamboo, setSyncingBamboo] = useState(false);

  // Ubiqfy config state
  const [ubiqfyConfig, setUbiqfyConfig] = useState({
    username: '', password: '', terminalKey: '',
    baseUrl: 'https://api-sandbox.ubiqfy.com', catalogSyncInterval: '60'
  });
  const [ubiqfyBalances, setUbiqfyBalancesConfig] = useState([]);
  const [ubiqfyCatalogStatus, setUbiqfyCatalogStatus] = useState(null);
  const [testingUbiqfy, setTestingUbiqfy] = useState(false);
  const [syncingUbiqfy, setSyncingUbiqfy] = useState(false);

  // WOCK config state
  const [wockConfig, setWockConfig] = useState({
    clientId: '', clientSecret: '', tenant: 'digitalwarehou.se',
    scope: 'api://wock_stage_external/.default',
    graphqlUrl: 'https://api.stage.wock.digitalwarehou.se/graphql',
    catalogSyncInterval: '60', orderPollInterval: '15'
  });
  const [wockPartner, setWockPartner] = useState(null);
  const [wockCatalogStatus, setWockCatalogStatus] = useState(null);
  const [testingWock, setTestingWock] = useState(false);
  const [syncingWock, setSyncingWock] = useState(false);

  // Default pricing formula state
  const [defaultFormula, setDefaultFormula] = useState('');
  const [defaultFormulaValidation, setDefaultFormulaValidation] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);

  // Alert config state
  const [alertConfig, setAlertConfig] = useState({
    webhookUrl: '', emailAddress: '',
    smtpHost: '', smtpPort: '587', smtpUser: '', smtpPassword: '',
    smtpFromAddress: '', smtpSecure: 'true'
  });
  const [testingAlert, setTestingAlert] = useState(false);
  const [alertTestResults, setAlertTestResults] = useState(null);

  // Users management state
  const [users, setUsers] = useState([]);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    loadTerminals();
    loadConfig();
    loadBambooConfig();
    loadUbiqfyConfig();
    loadWockConfig();
    loadPricingConfig();
    loadAlertConfig();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveUser = async (formData) => {
    try {
      if (editingUser) {
        const payload = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          active: formData.active
        };
        if (formData.password) payload.password = formData.password;
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/users`, {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role
        });
      }
      setShowUserModal(false);
      setEditingUser(null);
      await loadUsers();
      setMessage({ type: 'success', text: editingUser ? 'User updated' : 'User created' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteUser = async (user, hard = false) => {
    const action = hard ? 'permanently delete' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} "${user.email}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/users/${user.id}${hard ? '?hard=true' : ''}`);
      await loadUsers();
      setMessage({ type: 'success', text: hard ? 'User deleted' : 'User deactivated' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      alert('Failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const loadTerminals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/terminals`);
      setTerminals(response.data);
    } catch (error) {
      console.error('Failed to load terminals:', error);
      setMessage({ type: 'error', text: 'Failed to load terminals' });
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config/epay`);
      setConfig(prev => ({
        ...prev,
        timeout: String(response.data.timeout || '30000'),
        rateLimit: String(response.data.rateLimit || '100'),
        retryOnFailure: response.data.retryOnFailure !== false
      }));
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  };

  const loadBambooConfig = async () => {
    try {
      const [configRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/config/bamboo`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/bamboo/catalog/status`).catch(() => ({ data: {} }))
      ]);
      const c = configRes.data;
      setBambooConfig({
        clientId: c.bamboo_client_id || '',
        clientSecret: c.bamboo_client_secret || '',
        baseUrl: c.bamboo_base_url || 'https://api.bamboocardportal.com',
        accountId: c.bamboo_account_id || '',
        sandboxMode: c.bamboo_sandbox_mode || 'true',
        catalogSyncInterval: c.bamboo_catalog_sync_interval_minutes || '15',
        orderPollInterval: c.bamboo_order_poll_interval_seconds || '10'
      });
      setBambooCatalogStatus(statusRes.data);

      // Try to load accounts if configured
      if (c.bamboo_client_id) {
        try {
          const acctRes = await axios.get(`${API_URL}/api/bamboo/accounts`);
          setBambooAccounts(acctRes.data.accounts || []);
        } catch {}
      }
    } catch (error) {
      console.error('Failed to load Bamboo config:', error);
    }
  };

  const handleSaveBambooConfig = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(`${API_URL}/api/config/bamboo`, bambooConfig);
      setMessage({ type: 'success', text: 'Bamboo configuration saved!' });
      await loadBambooConfig();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save Bamboo config' });
    } finally {
      setSaving(false);
    }
  };

  // Ubiqfy config functions
  const loadUbiqfyConfig = async () => {
    try {
      const [configRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/config/ubiqfy`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/ubiqfy/catalog/status`).catch(() => ({ data: {} }))
      ]);
      const c = configRes.data;
      setUbiqfyConfig({
        username: c.ubiqfy_eur_username || '',
        password: c.ubiqfy_eur_password || '',
        terminalKey: c.ubiqfy_eur_terminal_key || '',
        baseUrl: c.ubiqfy_base_url || 'https://api-sandbox.ubiqfy.com',
        catalogSyncInterval: c.ubiqfy_catalog_sync_interval_minutes || '60'
      });
      setUbiqfyCatalogStatus(statusRes.data);
      // Try to load balances
      if (c.ubiqfy_eur_username) {
        try {
          const balRes = await axios.get(`${API_URL}/api/ubiqfy/accounts`);
          setUbiqfyBalancesConfig(balRes.data.balances || []);
        } catch {}
      }
    } catch (error) {
      console.error('Failed to load Ubiqfy config:', error);
    }
  };

  const handleSaveUbiqfyConfig = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Expand single credentials to all 3 currencies for backend
      const payload = {
        eurUsername: ubiqfyConfig.username, eurPassword: ubiqfyConfig.password, eurTerminalKey: ubiqfyConfig.terminalKey,
        usdUsername: ubiqfyConfig.username, usdPassword: ubiqfyConfig.password, usdTerminalKey: ubiqfyConfig.terminalKey,
        brlUsername: ubiqfyConfig.username, brlPassword: ubiqfyConfig.password, brlTerminalKey: ubiqfyConfig.terminalKey,
        baseUrl: ubiqfyConfig.baseUrl, catalogSyncInterval: ubiqfyConfig.catalogSyncInterval
      };
      await axios.put(`${API_URL}/api/config/ubiqfy`, payload);
      setMessage({ type: 'success', text: 'Ubiqfy configuration saved!' });
      await loadUbiqfyConfig();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save Ubiqfy config' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestUbiqfy = async () => {
    setTestingUbiqfy(true);
    setMessage(null);
    try {
      const response = await axios.post(`${API_URL}/api/config/ubiqfy/test`, {
        eurUsername: ubiqfyConfig.username,
        eurPassword: ubiqfyConfig.password,
        usdUsername: ubiqfyConfig.username,
        usdPassword: ubiqfyConfig.password,
        brlUsername: ubiqfyConfig.username,
        brlPassword: ubiqfyConfig.password,
        eurTerminalKey: ubiqfyConfig.terminalKey,
        usdTerminalKey: ubiqfyConfig.terminalKey,
        brlTerminalKey: ubiqfyConfig.terminalKey,
        baseUrl: ubiqfyConfig.baseUrl
      });
      const results = response.data.results || [];
      const successes = results.filter(r => r.success).length;
      const failures = results.filter(r => !r.success);
      let text = `Connection test: ${successes}/${results.length} currencies OK.`;
      if (failures.length > 0) {
        text += ` Failed: ${failures.map(f => `${f.currency} (${f.error})`).join(', ')}`;
      }
      setMessage({ type: successes > 0 ? 'success' : 'error', text });
    } catch (error) {
      setMessage({ type: 'error', text: 'Connection failed: ' + (error.response?.data?.error || error.message) });
    } finally {
      setTestingUbiqfy(false);
    }
  };

  const handleSyncUbiqfyCatalog = async () => {
    setSyncingUbiqfy(true);
    try {
      const response = await axios.post(`${API_URL}/api/ubiqfy/catalog/sync`);
      setMessage({ type: 'success', text: `Catalog synced: ${response.data.synced || 0} products` });
      await loadUbiqfyConfig();
    } catch (error) {
      setMessage({ type: 'error', text: 'Sync failed: ' + (error.response?.data?.error || error.message) });
    } finally {
      setSyncingUbiqfy(false);
    }
  };

  const loadWockConfig = async () => {
    try {
      const [configRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/config/wock`),
        axios.get(`${API_URL}/api/wock/catalog/status`).catch(() => ({ data: null }))
      ]);
      const c = configRes.data;
      setWockConfig({
        clientId: c.wock_client_id || '',
        clientSecret: c.wock_client_secret || '',
        tenant: c.wock_tenant || 'digitalwarehou.se',
        scope: c.wock_scope || 'api://wock_stage_external/.default',
        graphqlUrl: c.wock_graphql_url || 'https://api.stage.wock.digitalwarehou.se/graphql',
        catalogSyncInterval: c.wock_catalog_sync_interval_minutes || '60',
        orderPollInterval: c.wock_order_poll_interval_seconds || '15'
      });
      if (statusRes.data) setWockCatalogStatus(statusRes.data);
      // Load partner info
      try {
        const partnerRes = await axios.get(`${API_URL}/api/wock/accounts`);
        setWockPartner(partnerRes.data.partnerInfo || null);
      } catch {}
    } catch (error) {
      console.error('Failed to load WOCK config:', error);
    }
  };

  const handleSaveWockConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/config/wock`, wockConfig);
      setMessage({ type: 'success', text: 'WOCK configuration saved successfully' });
      await loadWockConfig();
    } catch (error) {
      setMessage({ type: 'error', text: 'Save failed: ' + (error.response?.data?.error || error.message) });
    }
  };

  const handleTestWock = async () => {
    setTestingWock(true);
    try {
      const response = await axios.post(`${API_URL}/api/config/wock/test`, wockConfig);
      if (response.data.success) {
        const pi = response.data.partnerInfo;
        setMessage({ type: 'success', text: `WOCK connection successful! Available: €${(pi.available || 0).toFixed(2)}, Used: €${(pi.used || 0).toFixed(2)}` });
        setWockPartner(pi);
      } else {
        setMessage({ type: 'error', text: 'Connection test failed: ' + (response.data.error || 'Unknown error') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Test failed: ' + (error.response?.data?.error || error.message) });
    } finally {
      setTestingWock(false);
    }
  };

  const handleSyncWockCatalog = async () => {
    setSyncingWock(true);
    try {
      const response = await axios.post(`${API_URL}/api/wock/catalog/sync`);
      setMessage({ type: 'success', text: `WOCK catalog synced: ${response.data.synced || 0} products` });
      await loadWockConfig();
    } catch (error) {
      setMessage({ type: 'error', text: 'Sync failed: ' + (error.response?.data?.error || error.message) });
    } finally {
      setSyncingWock(false);
    }
  };

  const loadPricingConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config/pricing`);
      setDefaultFormula(response.data.defaultFormula || '');
    } catch (error) {
      console.error('Failed to load pricing config:', error);
    }
  };

  const validateDefaultFormula = async (formula) => {
    if (!formula || !formula.trim()) {
      setDefaultFormulaValidation(null);
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/api-keys/validate-formula`, { formula });
      setDefaultFormulaValidation(response.data);
    } catch (error) {
      setDefaultFormulaValidation({ valid: false, error: error.response?.data?.error || 'Validation failed' });
    }
  };

  const handleSavePricingConfig = async () => {
    setSavingPricing(true);
    setMessage(null);
    try {
      await axios.post(`${API_URL}/api/config/pricing`, { defaultFormula: defaultFormula.trim() });
      setMessage({ type: 'success', text: 'Default pricing formula saved!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save pricing formula' });
    } finally {
      setSavingPricing(false);
    }
  };

  const loadAlertConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config/alerts`);
      setAlertConfig({
        webhookUrl: response.data.alert_webhook_url || '',
        emailAddress: response.data.alert_email_address || '',
        smtpHost: response.data.smtp_host || '',
        smtpPort: response.data.smtp_port || '587',
        smtpUser: response.data.smtp_user || '',
        smtpPassword: response.data.smtp_password || '',
        smtpFromAddress: response.data.smtp_from_address || '',
        smtpSecure: response.data.smtp_secure || 'true'
      });
    } catch (error) {
      console.error('Failed to load alert config:', error);
    }
  };

  const handleSaveAlertConfig = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(`${API_URL}/api/config/alerts`, alertConfig);
      setMessage({ type: 'success', text: 'Alert configuration saved!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save alert config' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestAlert = async () => {
    setTestingAlert(true);
    setAlertTestResults(null);
    try {
      const response = await axios.post(`${API_URL}/api/config/alerts/test`, alertConfig);
      setAlertTestResults(response.data.results);
    } catch (error) {
      setAlertTestResults({ error: error.response?.data?.error || 'Test failed' });
    } finally {
      setTestingAlert(false);
    }
  };


  const handleTestBamboo = async () => {
    setTestingBamboo(true);
    setMessage(null);
    try {
      const response = await axios.post(`${API_URL}/api/config/bamboo/test`, {
        clientId: bambooConfig.clientId,
        clientSecret: bambooConfig.clientSecret,
        baseUrl: bambooConfig.baseUrl
      });
      setBambooAccounts(response.data.accounts || []);
      setMessage({ type: 'success', text: `Connection successful! Found ${response.data.accounts?.length || 0} account(s).` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Connection failed: ' + (error.response?.data?.error || error.message) });
    } finally {
      setTestingBamboo(false);
    }
  };

  const handleSyncBambooCatalog = async () => {
    setSyncingBamboo(true);
    try {
      const response = await axios.post(`${API_URL}/api/bamboo/catalog/sync`);
      setMessage({ type: 'success', text: `Catalog synced: ${response.data.synced || 0} products` });
      await loadBambooConfig();
    } catch (error) {
      setMessage({ type: 'error', text: 'Sync failed: ' + (error.response?.data?.error || error.message) });
    } finally {
      setSyncingBamboo(false);
    }
  };

  const handleSaveTerminal = async (terminalData) => {
    setSaving(true);
    setMessage(null);
    try {
      if (terminalData.id) {
        await axios.put(`${API_URL}/api/terminals/${terminalData.id}`, terminalData);
        setMessage({ type: 'success', text: 'Terminal updated successfully!' });
      } else {
        await axios.post(`${API_URL}/api/terminals`, terminalData);
        setMessage({ type: 'success', text: 'Terminal created successfully!' });
      }
      await loadTerminals();
      setEditingTerminal(null);
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to save terminal:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save terminal' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTerminal = async (id) => {
    if (!window.confirm('Are you sure you want to delete this terminal?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/terminals/${id}`);
      setMessage({ type: 'success', text: 'Terminal deleted successfully!' });
      await loadTerminals();
    } catch (error) {
      console.error('Failed to delete terminal:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete terminal' });
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/config`, {
        timeout: config.timeout,
        rateLimit: config.rateLimit,
        retryOnFailure: config.retryOnFailure
      });
      setMessage({ type: 'success', text: 'Settings saved!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffd71f]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">System Configuration</h2>
      
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'}`}>
          {message.text}
        </div>
      )}

      {/* Tabs — ten of them; the bar scrolls sideways on a narrow screen rather than widening the page */}
      <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <nav className="flex gap-4 whitespace-nowrap min-w-max" aria-label="Configuration sections">
          <button
            onClick={() => setActiveTab('terminals')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'terminals'
                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Terminals
          </button>
          <button
            onClick={() => setActiveTab('bamboo')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'bamboo'
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Bamboo Card Portal
          </button>
          <button
            onClick={() => setActiveTab('ubiqfy')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'ubiqfy'
                ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Ubiqfy
          </button>
          <button
            onClick={() => setActiveTab('wock')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'wock'
                ? 'border-pink-500 text-pink-600 dark:text-pink-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            WOCK
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pricing'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Pricing
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            General Settings
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'alerts'
                ? 'border-red-500 text-red-600 dark:text-red-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Alerts
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Users
          </button>
          <button
            data-testid="tab-enrichment"
            onClick={() => setActiveTab('enrichment')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'enrichment'
                ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Enrichment
          </button>
        </nav>
      </div>

      {/* Enrichment Tab (F26 S7) — the AI naming engine: key, model, prompt versions, runs, what waits */}
      {activeTab === 'enrichment' && <EnrichmentTab onHelp={() => setShowEnrichmentHelp(true)} />}
      {showEnrichmentHelp && <CatalogueHelpModal onClose={() => setShowEnrichmentHelp(false)} />}

      {/* Terminals Tab */}
      {activeTab === 'terminals' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manage your ePay terminals. Each queue uses a terminal based on its environment (TEST/LIVE).
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Terminal
            </button>
          </div>

          <div className="grid gap-4">
            {terminals.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No terminals configured. Add your first terminal to get started.</p>
              </div>
            ) : (
              terminals.map(terminal => (
                <div key={terminal.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{terminal.name}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          terminal.environment === 'LIVE'
                            ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
                            : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                        }`}>
                          {terminal.environment}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          terminal.purpose === 'ECOMMERCE'
                            ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}>
                          {terminal.purpose}
                        </span>
                        {terminal.is_reserved && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400">
                            Reserved
                          </span>
                        )}
                        {!terminal.is_active && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        TID: <span className="font-mono">{terminal.terminal_id}</span> • User: {terminal.username}
                      </div>
                      {terminal.notes && (
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{terminal.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingTerminal(terminal)}
                        className="px-3 py-1.5 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTerminal(terminal.id)}
                        className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Terminal info boxes */}
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <h4 className="font-medium text-amber-900 dark:text-amber-300 mb-2">TEST Environment</h4>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                {terminals.filter(t => t.environment === 'TEST' && t.is_active).length} active terminal(s) for test queues.
                Test credentials are used for development and testing without real charges.
              </p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <h4 className="font-medium text-red-900 dark:text-red-300 mb-2">LIVE Environment</h4>
              <p className="text-sm text-red-700 dark:text-red-400">
                {terminals.filter(t => t.environment === 'LIVE' && t.is_active && !t.is_reserved).length} active terminal(s) for live queues.
                Live terminals process real transactions!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bamboo Card Portal Tab */}
      {activeTab === 'bamboo' && (
        <div className="space-y-6">
          {/* Credentials */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Bamboo Card Portal Credentials</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Client ID</label>
                <input type="text" value={bambooConfig.clientId}
                  onChange={(e) => setBambooConfig(prev => ({ ...prev, clientId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Client Secret</label>
                <input type="password" value={bambooConfig.clientSecret}
                  onChange={(e) => setBambooConfig(prev => ({ ...prev, clientSecret: e.target.value }))}
                  placeholder="Enter new secret or leave to keep current"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Base URL</label>
                <input type="text" value={bambooConfig.baseUrl}
                  onChange={(e) => setBambooConfig(prev => ({ ...prev, baseUrl: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account ID</label>
                <input type="text" value={bambooConfig.accountId}
                  onChange={(e) => setBambooConfig(prev => ({ ...prev, accountId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Catalog Sync Interval (min)</label>
                <input type="number" value={bambooConfig.catalogSyncInterval}
                  onChange={(e) => setBambooConfig(prev => ({ ...prev, catalogSyncInterval: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Order Poll Interval (sec)</label>
                <input type="number" value={bambooConfig.orderPollInterval}
                  onChange={(e) => setBambooConfig(prev => ({ ...prev, orderPollInterval: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <button onClick={handleTestBamboo} disabled={testingBamboo || !bambooConfig.clientId}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm flex items-center gap-2">
                {testingBamboo ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                {testingBamboo ? 'Testing...' : 'Test Connection'}
              </button>
              <button onClick={handleSaveBambooConfig} disabled={saving}
                className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg disabled:opacity-50 text-sm">
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>

          {/* Account Balances */}
          {bambooAccounts.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Balances</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {bambooAccounts.map((acct, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm font-mono text-gray-900 dark:text-white">{acct.id}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{acct.currency}</td>
                        <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900 dark:text-white">
                          {typeof acct.balance === 'number' ? acct.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : acct.balance}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${acct.isActive ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' : 'bg-gray-100 text-gray-600'}`}>
                            {acct.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{acct.sandboxMode ? 'Sandbox' : 'Live'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Catalog Sync */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Catalog Sync</h3>
              <button onClick={handleSyncBambooCatalog} disabled={syncingBamboo}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm">
                <RefreshCw className={`w-4 h-4 ${syncingBamboo ? 'animate-spin' : ''}`} />
                {syncingBamboo ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Last Sync</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {bambooCatalogStatus?.lastSyncAt ? new Date(bambooCatalogStatus.lastSyncAt).toLocaleString() : 'Never'}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Bamboo Products</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {bambooCatalogStatus?.productCount ?? '—'}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Syncer Status</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {bambooCatalogStatus?.isRunning ? 'Running' : 'Stopped'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ubiqfy Tab */}
      {activeTab === 'ubiqfy' && (
        <div className="space-y-6">
          {/* Credentials */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ubiqfy (Pagaqui Partners) Credentials</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Single credential set used for all currency wallets (EUR, USD, BRL).</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
                <input type="text" value={ubiqfyConfig.username}
                  onChange={(e) => setUbiqfyConfig(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                <input type="password" value={ubiqfyConfig.password}
                  onChange={(e) => setUbiqfyConfig(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter new or leave to keep"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Terminal Key</label>
                <input type="text" value={ubiqfyConfig.terminalKey}
                  onChange={(e) => setUbiqfyConfig(prev => ({ ...prev, terminalKey: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Base URL</label>
                <input type="text" value={ubiqfyConfig.baseUrl}
                  onChange={(e) => setUbiqfyConfig(prev => ({ ...prev, baseUrl: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Catalog Sync Interval (min)</label>
                <input type="number" value={ubiqfyConfig.catalogSyncInterval}
                  onChange={(e) => setUbiqfyConfig(prev => ({ ...prev, catalogSyncInterval: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <button onClick={handleTestUbiqfy} disabled={testingUbiqfy || !ubiqfyConfig.username}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm flex items-center gap-2">
                {testingUbiqfy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                {testingUbiqfy ? 'Testing...' : 'Test Connection'}
              </button>
              <button onClick={handleSaveUbiqfyConfig} disabled={saving}
                className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg disabled:opacity-50 text-sm">
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>

          {/* Account Balances */}
          {ubiqfyBalances.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Balances</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {ubiqfyBalances.map((b, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm font-mono text-gray-900 dark:text-white">{b.username}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{b.currency}</td>
                        <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900 dark:text-white">
                          {b.balance !== null ? (typeof b.balance === 'number' ? b.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : b.balance) : <span className="text-red-500">{b.error || 'N/A'}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Catalog Sync */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Catalog Sync</h3>
              <button onClick={handleSyncUbiqfyCatalog} disabled={syncingUbiqfy}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm">
                <RefreshCw className={`w-4 h-4 ${syncingUbiqfy ? 'animate-spin' : ''}`} />
                {syncingUbiqfy ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Last Sync</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {ubiqfyCatalogStatus?.lastSyncAt ? new Date(ubiqfyCatalogStatus.lastSyncAt).toLocaleString() : 'Never'}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Ubiqfy Products</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {ubiqfyCatalogStatus?.productCount ?? '—'}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Syncer Status</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {ubiqfyCatalogStatus?.isRunning ? 'Running' : 'Stopped'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WOCK Tab */}
      {activeTab === 'wock' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">WOCK Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Client ID</label>
                <input type="text" value={wockConfig.clientId} onChange={(e) => setWockConfig({ ...wockConfig, clientId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Client Secret</label>
                <input type="password" value={wockConfig.clientSecret} onChange={(e) => setWockConfig({ ...wockConfig, clientSecret: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tenant</label>
                <input type="text" value={wockConfig.tenant} onChange={(e) => setWockConfig({ ...wockConfig, tenant: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Scope</label>
                <input type="text" value={wockConfig.scope} onChange={(e) => setWockConfig({ ...wockConfig, scope: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">GraphQL URL</label>
                <input type="text" value={wockConfig.graphqlUrl} onChange={(e) => setWockConfig({ ...wockConfig, graphqlUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Catalog Sync Interval (minutes)</label>
                <input type="number" value={wockConfig.catalogSyncInterval} onChange={(e) => setWockConfig({ ...wockConfig, catalogSyncInterval: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Order Poll Interval (seconds)</label>
                <input type="number" value={wockConfig.orderPollInterval} onChange={(e) => setWockConfig({ ...wockConfig, orderPollInterval: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={handleTestWock} disabled={testingWock}
                className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 text-sm">
                {testingWock ? 'Testing...' : 'Test Connection'}
              </button>
              <button onClick={handleSaveWockConfig}
                className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 text-sm">
                Save Configuration
              </button>
            </div>
          </div>

          {wockPartner && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Partner Info</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-pink-600 dark:text-pink-400">€{(wockPartner.available || 0).toFixed(2)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Available</div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">€{(wockPartner.used || 0).toFixed(2)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Used</div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">{wockPartner.stockLimit || 'N/A'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Stock Limit</div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Catalog Sync</h3>
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {wockCatalogStatus ? (
                  <>
                    <p>Last synced: {wockCatalogStatus.lastSyncAt ? new Date(wockCatalogStatus.lastSyncAt).toLocaleString() : 'Never'}</p>
                    <p>Products: {wockCatalogStatus.productCount || 0}</p>
                    <p>Syncer: {wockCatalogStatus.isRunning ? '✅ Active' : '⚠️ Inactive'}</p>
                  </>
                ) : (
                  <p>Not configured</p>
                )}
              </div>
              <button onClick={handleSyncWockCatalog} disabled={syncingWock}
                className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 text-sm">
                {syncingWock ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Tab */}
      {activeTab === 'pricing' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Default Pricing Formula</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Applied to all B2B API keys that don't have a per-key pricing formula. Leave empty to charge face value by default.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Formula</label>
            <input
              type="text"
              value={defaultFormula}
              onChange={(e) => { setDefaultFormula(e.target.value); setDefaultFormulaValidation(null); }}
              onBlur={(e) => validateDefaultFormula(e.target.value)}
              className="w-full max-w-lg px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
              placeholder="[cost] * 1.15"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Variables: [cost], [face_value], [quantity], [order_face_value], [order_cost_value]
            </p>
            {defaultFormulaValidation && (
              <p className={`text-xs mt-1 ${defaultFormulaValidation.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {defaultFormulaValidation.valid
                  ? `Valid — sample: cost=10.00, face=12.00 → ${defaultFormulaValidation.sampleDisplay || (defaultFormulaValidation.sampleResult / 100).toFixed(2)}`
                  : `Error: ${defaultFormulaValidation.error}`}
              </p>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <button
              onClick={handleSavePricingConfig}
              disabled={savingPricing}
              className="px-4 py-2 bg-purple-600 dark:bg-purple-500 text-white rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 disabled:opacity-50 text-sm"
            >
              {savingPricing ? 'Saving...' : 'Save Default Formula'}
            </button>
          </div>
        </div>
      )}

      {/* General Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">General Settings</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Request Timeout (ms)</label>
              <input 
                type="number" 
                value={config.timeout}
                onChange={(e) => setConfig(prev => ({ ...prev, timeout: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Global Rate Limit (calls/min)</label>
              <input 
                type="number" 
                value={config.rateLimit}
                onChange={(e) => setConfig(prev => ({ ...prev, rateLimit: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Retry on failure</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">When enabled, failed code generations will be retried until successful. When disabled, failed attempts count as completed.</p>
            </div>
            <button
              onClick={() => setConfig(prev => ({ ...prev, retryOnFailure: !prev.retryOnFailure }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.retryOnFailure ? 'bg-[#ffd71f]' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.retryOnFailure ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <div className="flex justify-end pt-4">
            <button
              onClick={handleSaveConfig}
              disabled={saving}
              className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Alert Notifications</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Get notified when API key guardrails are triggered (spend caps, quantity limits, low balance).</p>

          {/* Webhook */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Webhook URL</label>
            <input type="url" value={alertConfig.webhookUrl}
              onChange={(e) => setAlertConfig(prev => ({ ...prev, webhookUrl: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="https://hooks.slack.com/services/..." />
            <p className="text-xs text-gray-500 mt-1">Receives JSON POST for each alert event</p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Alert Email Address</label>
            <input type="email" value={alertConfig.emailAddress}
              onChange={(e) => setAlertConfig(prev => ({ ...prev, emailAddress: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="alerts@company.com" />
          </div>

          {/* SMTP Settings */}
          {alertConfig.emailAddress && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">SMTP Settings</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">SMTP Host</label>
                  <input type="text" value={alertConfig.smtpHost}
                    onChange={(e) => setAlertConfig(prev => ({ ...prev, smtpHost: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="smtp.gmail.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Port</label>
                  <input type="number" value={alertConfig.smtpPort}
                    onChange={(e) => setAlertConfig(prev => ({ ...prev, smtpPort: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="587" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Username</label>
                  <input type="text" value={alertConfig.smtpUser}
                    onChange={(e) => setAlertConfig(prev => ({ ...prev, smtpUser: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="user@gmail.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Password</label>
                  <input type="password" value={alertConfig.smtpPassword}
                    onChange={(e) => setAlertConfig(prev => ({ ...prev, smtpPassword: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="app password" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">From Address</label>
                  <input type="email" value={alertConfig.smtpFromAddress}
                    onChange={(e) => setAlertConfig(prev => ({ ...prev, smtpFromAddress: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="noreply@company.com" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={alertConfig.smtpSecure === 'true' || alertConfig.smtpSecure === true}
                      onChange={(e) => setAlertConfig(prev => ({ ...prev, smtpSecure: e.target.checked ? 'true' : 'false' }))}
                      className="w-4 h-4 text-red-600 rounded focus:ring-red-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">TLS/SSL</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Test + Save buttons */}
          <div className="flex items-center gap-3 pt-2">
            <button onClick={handleTestAlert} disabled={testingAlert || (!alertConfig.webhookUrl && !alertConfig.emailAddress)}
              className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50 text-sm">
              {testingAlert ? 'Testing...' : 'Test Alerts'}
            </button>
            <button onClick={handleSaveAlertConfig} disabled={saving}
              className="px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 text-sm">
              {saving ? 'Saving...' : 'Save Alert Config'}
            </button>
          </div>

          {/* Test Results */}
          {alertTestResults && (
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30 text-sm space-y-1">
              <p className="font-medium text-gray-900 dark:text-white">Test Results:</p>
              {alertTestResults.error ? (
                <p className="text-red-600 dark:text-red-400">{alertTestResults.error}</p>
              ) : (
                <>
                  {alertTestResults.webhook && <p className={alertTestResults.webhook === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>Webhook: {alertTestResults.webhook}</p>}
                  {alertTestResults.email && <p className={alertTestResults.email === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>Email: {alertTestResults.email}</p>}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manage admin and operator user accounts. Admins can create, edit, and remove users.
            </p>
            <button
              onClick={() => { setEditingUser(null); setShowUserModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 text-sm"
            >
              <Plus className="w-4 h-4" /> Add User
            </button>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={showInactiveUsers}
                onChange={(e) => setShowInactiveUsers(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              Show inactive
            </label>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Name</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Email</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Role</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Created</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loadingUsers ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                  ) : users.filter(u => showInactiveUsers || u.active).length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No users found</td></tr>
                  ) : users.filter(u => showInactiveUsers || u.active).map(u => {
                    const isSelf = currentUser && u.id === currentUser.id;
                    return (
                      <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3 text-gray-900 dark:text-white">
                          {u.first_name} {u.last_name}
                          {isSelf && <span className="ml-2 text-xs text-gray-500">(you)</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            u.role === 'admin'
                              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            u.active
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {u.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => { setEditingUser(u); setShowUserModal(true); }}
                              className="p-1.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded"
                              title="Edit user"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                            {!isSelf && (
                              <button
                                onClick={() => handleDeleteUser(u, false)}
                                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                                title={u.active ? 'Deactivate user' : 'Already inactive'}
                                disabled={!u.active}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Terminal Modal */}
      {(showAddModal || editingTerminal) && (
        <TerminalModal
          terminal={editingTerminal}
          onSave={handleSaveTerminal}
          onClose={() => { setShowAddModal(false); setEditingTerminal(null); }}
          saving={saving}
        />
      )}

      {/* Add/Edit User Modal */}
      {showUserModal && (
        <UserModal
          user={editingUser}
          currentUser={currentUser}
          onSave={handleSaveUser}
          onClose={() => { setShowUserModal(false); setEditingUser(null); }}
        />
      )}
    </div>
  );
};

// Terminal Add/Edit Modal
const TerminalModal = ({ terminal, onSave, onClose, saving }) => {
  const [formData, setFormData] = useState({
    name: terminal?.name || '',
    terminal_id: terminal?.terminal_id || '',
    username: terminal?.username || '',
    password: '',
    environment: terminal?.environment || 'TEST',
    purpose: terminal?.purpose || 'QUEUE',
    primary_host: terminal?.primary_host || 'https://precision.epayworldwide.com/up-interface',
    secondary_host: terminal?.secondary_host || 'https://backup.precision.epayworldwide.com/up-interface',
    is_active: terminal?.is_active !== false,
    is_reserved: terminal?.is_reserved || false,
    notes: terminal?.notes || ''
  });

  const handleSubmit = () => {
    const data = { ...formData };
    if (terminal?.id) data.id = terminal.id;
    // Only include password if it was changed (not empty and not masked)
    if (!data.password || data.password === '********') {
      delete data.password;
    }
    onSave(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {terminal ? 'Edit Terminal' : 'Add New Terminal'}
          </h3>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Live Terminal 1"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Terminal ID (TID) *</label>
              <input
                type="text"
                value={formData.terminal_id}
                onChange={(e) => setFormData(prev => ({ ...prev, terminal_id: e.target.value }))}
                placeholder="8 characters"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username *</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password {terminal ? '(leave blank to keep current)' : '*'}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder={terminal ? '••••••••' : ''}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Environment *</label>
              <select
                value={formData.environment}
                onChange={(e) => setFormData(prev => ({ ...prev, environment: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="TEST">TEST</option>
                <option value="LIVE">LIVE</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Purpose *</label>
              <select
                value={formData.purpose}
                onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="QUEUE">Queue Processing</option>
                <option value="ECOMMERCE">E-commerce (Kabrir)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Host</label>
            <input
              type="text"
              value={formData.primary_host}
              onChange={(e) => setFormData(prev => ({ ...prev, primary_host: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Secondary Host</label>
            <input
              type="text"
              value={formData.secondary_host}
              onChange={(e) => setFormData(prev => ({ ...prev, secondary_host: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_reserved}
                onChange={(e) => setFormData(prev => ({ ...prev, is_reserved: e.target.checked }))}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Reserved (not used for regular queues)</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Optional notes about this terminal"
            />
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.name || !formData.terminal_id || !formData.username}
            className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg disabled:opacity-50"
          >
            {saving ? 'Saving...' : (terminal ? 'Update Terminal' : 'Add Terminal')}
          </button>
        </div>
      </div>
    </div>
  );
};

// User Add/Edit Modal
const UserModal = ({ user, currentUser, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    role: user?.role || 'operator',
    password: '',
    active: user ? user.active : true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isSelf = !!user && !!currentUser && user.id === currentUser.id;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    if (!formData.email || !formData.firstName || !formData.lastName) {
      setError('Email, first name, and last name are required');
      return;
    }
    if (!user && !formData.password) {
      setError('Password is required when creating a new user');
      return;
    }
    if (formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {user ? 'Edit User' : 'Add New User'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password {user && <span className="text-xs text-gray-500">(leave blank to keep current)</span>}
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder={user ? '••••••••' : 'Min 6 characters'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              disabled={isSelf}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm disabled:opacity-60"
            >
              <option value="operator">Operator</option>
              <option value="admin">Admin</option>
            </select>
            {isSelf && (
              <p className="text-xs text-gray-500 mt-1">You cannot change your own role.</p>
            )}
          </div>

          {user && (
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Active</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Inactive users cannot log in.</p>
              </div>
              <button
                type="button"
                onClick={() => !isSelf && setFormData({ ...formData, active: !formData.active })}
                disabled={isSelf}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${formData.active ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.active ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          )}
        </form>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : (user ? 'Update User' : 'Create User')}
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterableProductSelect = ({ products = [], value, onChange, placeholder = 'All Products' }) => {
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const vendorGroups = { epay: [], bamboo: [], ubiqfy: [], wock: [] };
  products.forEach(p => { if (vendorGroups[p.vendor]) vendorGroups[p.vendor].push(p); });

  const selectedProduct = value && value !== 'all' ? products.find(p => (p.sku || p.vendorSku) === value) : null;

  const formatProduct = (p) => {
    const displaySku = p.sku || p.vendorSku || 'No SKU';
    const denomination = p.isVariableAmount ? '(Variable)' : p.faceValue ? `(${currencySymbol(p.currencyCode)}${(p.faceValue / 100).toFixed(2)})` : '';
    return `${displaySku} - ${p.name} ${denomination}`;
  };

  const filterList = (list) => {
    if (!searchText) return list;
    const q = searchText.toLowerCase();
    return list.filter(p => (p.sku || '').toLowerCase().includes(q) || (p.vendorSku || '').toLowerCase().includes(q) || p.name.toLowerCase().includes(q));
  };

  const filteredGroups = {
    epay: filterList(vendorGroups.epay),
    bamboo: filterList(vendorGroups.bamboo),
    ubiqfy: filterList(vendorGroups.ubiqfy),
    wock: filterList(vendorGroups.wock)
  };
  const flatFiltered = [null, ...filteredGroups.epay, ...filteredGroups.bamboo, ...filteredGroups.ubiqfy, ...filteredGroups.wock]; // null = "All Products"

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) { setIsOpen(false); setSearchText(''); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setHighlightIndex(0); }, [searchText]);

  const handleSelect = (product) => {
    onChange(product ? (product.sku || product.vendorSku) : 'all');
    setIsOpen(false);
    setSearchText('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) { if (e.key === 'ArrowDown' || e.key === 'Enter') { setIsOpen(true); e.preventDefault(); } return; }
    if (e.key === 'ArrowDown') { setHighlightIndex(i => Math.min(i + 1, flatFiltered.length - 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { setHighlightIndex(i => Math.max(i - 1, 0)); e.preventDefault(); }
    else if (e.key === 'Enter') { handleSelect(flatFiltered[highlightIndex]); e.preventDefault(); }
    else if (e.key === 'Escape') { setIsOpen(false); setSearchText(''); }
  };

  useEffect(() => {
    if (isOpen && listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${highlightIndex}"]`);
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, isOpen]);

  const vendorHeader = (label, colorClasses) => (
    <div className={`px-3 py-1.5 text-xs font-semibold ${colorClasses} sticky top-0`}>{label}</div>
  );

  const renderItems = (items, startIdx) => items.map((p, i) => {
    const idx = startIdx + i;
    return (
      <div key={p.id} data-idx={idx} onMouseDown={() => handleSelect(p)} onMouseEnter={() => setHighlightIndex(idx)}
        className={`px-3 py-1.5 text-sm cursor-pointer truncate ${idx === highlightIndex ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200' : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
        {formatProduct(p)}
      </div>
    );
  });

  let runningIdx = 1; // 0 is "All Products"

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={isOpen ? searchText : (selectedProduct ? formatProduct(selectedProduct) : '')}
        onChange={(e) => { setSearchText(e.target.value); if (!isOpen) setIsOpen(true); }}
        onFocus={() => { setIsOpen(true); setSearchText(''); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border ${value && value !== 'all' ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
      />
      <ChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      {isOpen && (
        <div ref={listRef} className="absolute z-50 mt-1 w-full min-w-[320px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          <div data-idx={0} onMouseDown={() => handleSelect(null)} onMouseEnter={() => setHighlightIndex(0)}
            className={`px-3 py-2 text-sm cursor-pointer font-medium ${0 === highlightIndex ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200' : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
            All Products
          </div>
          {flatFiltered.length <= 1 && searchText && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No products found</div>
          )}
          {filteredGroups.epay.length > 0 && (<>{vendorHeader('ePay Products', 'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50')}{renderItems(filteredGroups.epay, (runningIdx = 1, runningIdx))}</>)}
          {filteredGroups.bamboo.length > 0 && (<>{vendorHeader('Bamboo Products', 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20')}{renderItems(filteredGroups.bamboo, (runningIdx = 1 + filteredGroups.epay.length, runningIdx))}</>)}
          {filteredGroups.ubiqfy.length > 0 && (<>{vendorHeader('Ubiqfy Products', 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20')}{renderItems(filteredGroups.ubiqfy, (runningIdx = 1 + filteredGroups.epay.length + filteredGroups.bamboo.length, runningIdx))}</>)}
          {filteredGroups.wock.length > 0 && (<>{vendorHeader('WOCK Products', 'text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-900/20')}{renderItems(filteredGroups.wock, (runningIdx = 1 + filteredGroups.epay.length + filteredGroups.bamboo.length + filteredGroups.ubiqfy.length, runningIdx))}</>)}
        </div>
      )}
    </div>
  );
};

const ProductSearchSelect = ({ epayProducts = [], bambooProducts = [], ubiqfyProducts = [], wockProducts = [], value, onChange }) => {
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const MAX_VISIBLE = 50;

  const allProducts = useMemo(() => [...epayProducts, ...bambooProducts, ...ubiqfyProducts, ...wockProducts], [epayProducts, bambooProducts, ubiqfyProducts, wockProducts]);
  const selectedProduct = value ? allProducts.find(p => p.id === parseInt(value)) : null;

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const formatProduct = (p) => {
    const displaySku = p.sku || p.vendorSku || 'No SKU';
    if (p.vendor === 'bamboo' || p.vendor === 'ubiqfy' || p.vendor === 'wock') {
      return `${displaySku} - ${p.name} ${p.isVariableAmount ? '(Variable)' : p.faceValue ? `(${currencySymbol(p.currencyCode)}${(p.faceValue / 100).toFixed(2)})` : ''}`;
    }
    return `${displaySku} - ${p.name} ${p.isVariableAmount ? '(Variable)' : `(${currencySymbol(p.currencyCode)}${(p.faceValue / 100).toFixed(2)})`}`;
  };

  const filterProducts = (list) => {
    if (!debouncedSearchText) return list;
    const q = debouncedSearchText.toLowerCase();
    return list.filter(p => (p.sku || '').toLowerCase().includes(q) || (p.vendorSku || '').toLowerCase().includes(q) || p.name.toLowerCase().includes(q));
  };

  const filteredEpay = useMemo(() => filterProducts(epayProducts), [epayProducts, debouncedSearchText]);
  const filteredBamboo = useMemo(() => filterProducts(bambooProducts), [bambooProducts, debouncedSearchText]);
  const filteredUbiqfy = useMemo(() => filterProducts(ubiqfyProducts), [ubiqfyProducts, debouncedSearchText]);
  const filteredWock = useMemo(() => filterProducts(wockProducts), [wockProducts, debouncedSearchText]);
  const flatFiltered = useMemo(() => [...filteredEpay, ...filteredBamboo, ...filteredUbiqfy, ...filteredWock], [filteredEpay, filteredBamboo, filteredUbiqfy, filteredWock]);

  // Build a Map for O(1) index lookups instead of indexOf
  const indexMap = useMemo(() => {
    const m = new Map();
    flatFiltered.forEach((p, i) => m.set(p.id, i));
    return m;
  }, [flatFiltered]);

  const displayList = useMemo(() => flatFiltered.slice(0, MAX_VISIBLE), [flatFiltered]);
  const isTruncated = flatFiltered.length > MAX_VISIBLE;

  // Split displayList back into vendor groups for rendering
  const displayGroups = useMemo(() => {
    const epay = [], bamboo = [], ubiqfy = [], wock = [];
    for (const p of displayList) {
      if (p.vendor === 'bamboo') bamboo.push(p);
      else if (p.vendor === 'ubiqfy') ubiqfy.push(p);
      else if (p.vendor === 'wock') wock.push(p);
      else epay.push(p);
    }
    return { epay, bamboo, ubiqfy, wock };
  }, [displayList]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setHighlightIndex(0); }, [debouncedSearchText]);

  const handleSelect = (productId) => {
    onChange(productId);
    setIsOpen(false);
    setSearchText('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) { if (e.key === 'ArrowDown' || e.key === 'Enter') { setIsOpen(true); e.preventDefault(); } return; }
    if (e.key === 'ArrowDown') { setHighlightIndex(i => Math.min(i + 1, displayList.length - 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { setHighlightIndex(i => Math.max(i - 1, 0)); e.preventDefault(); }
    else if (e.key === 'Enter' && displayList[highlightIndex]) { handleSelect(displayList[highlightIndex].id); e.preventDefault(); }
    else if (e.key === 'Escape') { setIsOpen(false); setSearchText(''); }
  };

  const renderVendorGroup = (items, label, headerClasses) => {
    if (items.length === 0) return null;
    return (
      <>
        <div className={`px-3 py-1.5 text-xs font-semibold ${headerClasses} sticky top-0`}>{label}</div>
        {items.map(p => {
          const idx = indexMap.get(p.id);
          return (
            <div key={p.id} onMouseDown={() => handleSelect(p.id)} onMouseEnter={() => setHighlightIndex(idx)}
              className={`px-3 py-2 text-sm cursor-pointer ${idx === highlightIndex ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200' : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
              {formatProduct(p)}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={isOpen ? searchText : (selectedProduct ? formatProduct(selectedProduct) : '')}
        onChange={(e) => { setSearchText(e.target.value); if (!isOpen) setIsOpen(true); }}
        onFocus={() => { setIsOpen(true); setSearchText(''); }}
        onKeyDown={handleKeyDown}
        placeholder="Search SKU or product name..."
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
      />
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {displayList.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No products found</div>
          ) : (
            <>
              {isTruncated && (
                <div className="px-3 py-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
                  Showing {MAX_VISIBLE} of {flatFiltered.length} results — type to narrow
                </div>
              )}
              {renderVendorGroup(displayGroups.epay, 'ePay Products', 'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50')}
              {renderVendorGroup(displayGroups.bamboo, 'Bamboo Products', 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20')}
              {renderVendorGroup(displayGroups.ubiqfy, 'Ubiqfy Products', 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20')}
              {renderVendorGroup(displayGroups.wock, 'WOCK Products', 'text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-900/20')}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CreateQueueModal = ({ onClose, onSubmit, products = [] }) => {
  const [poNumber, setPoNumber] = useState('');
  const [piNumber, setPiNumber] = useState('');
  const [environment, setEnvironment] = useState('TEST');
  const [items, setItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter only active products
  const activeProducts = products.filter(p => p.active);

  // Group products by vendor
  const epayProducts = activeProducts.filter(p => p.vendor !== 'bamboo' && p.vendor !== 'ubiqfy' && p.vendor !== 'wock');
  const bambooProducts = activeProducts.filter(p => p.vendor === 'bamboo');
  const ubiqfyProducts = activeProducts.filter(p => p.vendor === 'ubiqfy');
  const wockProducts = activeProducts.filter(p => p.vendor === 'wock');

  const addItem = () => {
    if (items.length >= 10) {
      alert('Maximum 10 SKUs per queue');
      return;
    }
    setItems([...items, { productId: null, ratePerMinute: 1, targetQuantity: 10, customAmountMinor: null, vendor: 'epay', vendorFilter: 'all', wockAutoDeliver: true }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    setItems(items.map((item, i) => {
      if (i !== index) return item;

      const updatedItem = { ...item, [field]: value };

      // When product changes, set vendor and reset custom amount
      if (field === 'productId') {
        const selectedProduct = activeProducts.find(p => p.id === parseInt(value));
        updatedItem.vendor = selectedProduct?.vendor || 'epay';
        if (selectedProduct?.isVariableAmount) {
          updatedItem.customAmountMinor = selectedProduct.minAmountMinor;
        } else {
          updatedItem.customAmountMinor = null;
        }
        // Bamboo/WOCK products don't need rate per minute (batch ordering)
        if (selectedProduct?.vendor === 'bamboo' || selectedProduct?.vendor === 'wock') {
          updatedItem.ratePerMinute = 1;
        }
      }

      return updatedItem;
    }));
  };

  const getProductById = (id) => activeProducts.find(p => p.id === parseInt(id));

  const handleSubmit = async () => {
    if (!poNumber || items.length === 0 || items.some(i => !i.productId || !i.targetQuantity)) {
      alert('Please fill all required fields including target quantity for each SKU');
      return;
    }

    // Validate variable amount items
    for (const item of items) {
      const product = getProductById(item.productId);
      if (product?.isVariableAmount) {
        if (!item.customAmountMinor) {
          alert(`Please specify an amount for ${product.name}`);
          return;
        }
        if (item.customAmountMinor < product.minAmountMinor) {
          alert(`Amount for ${product.name} must be at least ${currencySymbol(product.currencyCode)}${(product.minAmountMinor / 100).toFixed(2)}`);
          return;
        }
        if (item.customAmountMinor > product.maxAmountMinor) {
          alert(`Amount for ${product.name} cannot exceed ${currencySymbol(product.currencyCode)}${(product.maxAmountMinor / 100).toFixed(2)}`);
          return;
        }
      }
    }

    setIsSubmitting(true);

    try {
      // Save queue to database via API
      const response = await axios.post(`${API_URL}/api/queues`, {
        poNumber,
        piNumber: piNumber || undefined,
        environment,
        items: items.map(item => ({
          productId: item.productId,
          ratePerMinute: item.ratePerMinute,
          targetQuantity: item.targetQuantity,
          customAmountMinor: item.customAmountMinor || null,
          vendor: item.vendor || 'epay',
          wockAutoDeliver: item.vendor === 'wock' ? item.wockAutoDeliver : undefined
        }))
      });

      const savedQueue = response.data;
      
      // Fetch the full queue details to get real item IDs
      const detailResponse = await axios.get(`${API_URL}/api/queues/${savedQueue.id}`);
      const queueWithItems = detailResponse.data;
      
      // Format the response to match frontend structure
      const newQueue = {
        id: queueWithItems.id,
        poNumber: queueWithItems.po_number,
        piNumber: queueWithItems.pi_number,
        environment: queueWithItems.environment,
        status: queueWithItems.status,
        createdAt: queueWithItems.created_at,
        items: queueWithItems.items ? queueWithItems.items.map(item => ({
          id: item.id,
          productId: item.productId,
          sku: item.product?.sku,
          ratePerMinute: item.ratePerMinute,
          targetQuantity: item.targetQuantity,
          generatedCount: item.generatedCount || 0,
          successCount: item.successCount || 0,
          failedCount: item.failedCount || 0,
          customAmountMinor: item.customAmountMinor,
          vendor: item.vendor,
          wockAutoDeliver: item.wockAutoDeliver
        })) : [],
        wockOrders: (queueWithItems.wock_orders || []).map(wo => ({
          id: wo.id,
          wockOrderId: wo.wock_order_id,
          queueItemId: wo.queue_item_id,
          quantity: wo.quantity,
          status: wo.status,
          autoDeliver: wo.auto_deliver,
          keysReceived: wo.keys_received,
          keysFailed: wo.keys_failed,
          errorMessage: wo.error_message,
          createdAt: wo.created_at,
          completedAt: wo.completed_at
        }))
      };

      onSubmit(newQueue);
    } catch (error) {
      console.error('Failed to create queue:', error);
      alert('Failed to create queue: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Create New Queue</h3>
        </div>
        
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
              PO Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder="e.g., PO-2025-001"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
              PI Number
            </label>
            <input
              type="text"
              value={piNumber}
              onChange={(e) => setPiNumber(e.target.value)}
              placeholder="e.g., PI-2025-001"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">Environment</label>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="TEST">Test</option>
              <option value="LIVE">Live</option>
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                SKUs (max 5) <span className="text-red-500">*</span>
              </label>
              <button
                onClick={addItem}
                disabled={items.length >= 5}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                <span>Add SKU</span>
              </button>
            </div>

            {items.length === 0 ? (
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
                No SKUs added yet. Click Add SKU to start.
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item, index) => {
                  const selectedProduct = getProductById(item.productId);
                  const isVariable = selectedProduct?.isVariableAmount;
                  const vf = item.vendorFilter || 'all';

                  return (
                    <div key={index} className={`p-3 sm:p-4 rounded-lg border space-y-3 ${
                      selectedProduct?.vendor === 'bamboo'
                        ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20'
                        : selectedProduct?.vendor === 'ubiqfy'
                          ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20'
                          : selectedProduct?.vendor === 'wock'
                            ? 'border-pink-300 dark:border-pink-700 bg-pink-50 dark:bg-pink-900/20'
                            : isVariable
                              ? 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20'
                              : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
                    }`}>
                      {/* Row 1: Vendor filter + delete button */}
                      <div className="flex items-center justify-between">
                        <select
                          value={vf}
                          onChange={(e) => updateItem(index, 'vendorFilter', e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="all">All Vendors</option>
                          <option value="epay">ePay</option>
                          <option value="bamboo">Bamboo</option>
                          <option value="ubiqfy">Ubiqfy</option>
                          <option value="wock">WOCK</option>
                        </select>
                        <button
                          onClick={() => removeItem(index)}
                          className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Row 2: Product search — full width */}
                      <div>
                        <ProductSearchSelect
                          epayProducts={vf === 'all' || vf === 'epay' ? epayProducts : []}
                          bambooProducts={vf === 'all' || vf === 'bamboo' ? bambooProducts : []}
                          ubiqfyProducts={vf === 'all' || vf === 'ubiqfy' ? ubiqfyProducts : []}
                          wockProducts={vf === 'all' || vf === 'wock' ? wockProducts : []}
                          value={item.productId}
                          onChange={(id) => updateItem(index, 'productId', id)}
                        />
                      </div>

                      {/* Row 3: Target Qty + Rate/Mode/Delivery/Amount side by side */}
                      <div className={`grid gap-3 ${isVariable ? 'grid-cols-3' : 'grid-cols-2'}`}>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Target Qty <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={item.targetQuantity || ''}
                            onChange={(e) => updateItem(index, 'targetQuantity', parseInt(e.target.value))}
                            placeholder="e.g., 100"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                        {isVariable && (
                          <div>
                            <label className="block text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">Amount ({currencySymbol(selectedProduct.currencyCode)}) *</label>
                            <input
                              type="number"
                              step="0.01"
                              min={(selectedProduct.minAmountMinor / 100).toFixed(2)}
                              max={(selectedProduct.maxAmountMinor / 100).toFixed(2)}
                              value={item.customAmountMinor ? (item.customAmountMinor / 100).toFixed(2) : ''}
                              onChange={(e) => updateItem(index, 'customAmountMinor', Math.round(parseFloat(e.target.value) * 100))}
                              placeholder={`${(selectedProduct.minAmountMinor / 100).toFixed(0)}-${(selectedProduct.maxAmountMinor / 100).toFixed(0)}`}
                              className="w-full px-3 py-2 border border-purple-300 dark:border-purple-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                            />
                          </div>
                        )}
                        <div>
                          {isVariable ? (
                            <>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Rate/min</label>
                              <input
                                type="number"
                                min="1"
                                value={item.ratePerMinute}
                                onChange={(e) => updateItem(index, 'ratePerMinute', parseInt(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                              />
                            </>
                          ) : selectedProduct?.vendor === 'bamboo' ? (
                            <>
                              <label className="block text-xs font-medium mb-1 text-emerald-700 dark:text-emerald-300">Mode</label>
                              <div className="px-3 py-2 border rounded-lg text-sm border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300">
                                Batch order
                              </div>
                            </>
                          ) : selectedProduct?.vendor === 'wock' ? (
                            <>
                              <label className="block text-xs font-medium mb-1 text-pink-700 dark:text-pink-300">Delivery</label>
                              <button
                                type="button"
                                onClick={() => updateItem(index, 'wockAutoDeliver', !item.wockAutoDeliver)}
                                className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between ${
                                  item.wockAutoDeliver !== false
                                    ? 'border-pink-300 dark:border-pink-700 bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300'
                                    : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                                }`}
                              >
                                <span>{item.wockAutoDeliver !== false ? 'Auto' : 'Manual'}</span>
                                <div className={`w-8 h-4 rounded-full relative transition-colors ${item.wockAutoDeliver !== false ? 'bg-pink-400' : 'bg-amber-400'}`}>
                                  <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${item.wockAutoDeliver !== false ? 'right-0.5' : 'left-0.5'}`} />
                                </div>
                              </button>
                            </>
                          ) : (
                            <>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Rate/min</label>
                              <input
                                type="number"
                                min="1"
                                value={item.ratePerMinute}
                                onChange={(e) => updateItem(index, 'ratePerMinute', parseInt(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-[#ffd71f] hover:bg-amber-400 text-[#080300] rounded-lg disabled:opacity-50 flex items-center space-x-2"
          >
            {isSubmitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            <span>{isSubmitting ? 'Creating...' : 'Create Queue'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400',
    purple: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400',
    green: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400',
    red: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400',
    orange: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
      </div>
      <div className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-1">{value}</div>
      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{title}</div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    RUNNING: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-400', label: 'Running' },
    PAUSED: { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-700 dark:text-yellow-400', label: 'Paused' },
    STOPPED: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', label: 'Stopped' },
    COMPLETED: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-400', label: 'Completed' },
    // A queue the vendor could not fully supply. It is stopped rather than left
    // to re-order the shortfall by itself when stock returns — PLATFORM-FINDINGS
    // #101. Without this entry it would fall through to DRAFT and read "Draft".
    PARTIAL: { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-400', label: 'Partial' },
    DRAFT: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', label: 'Draft' },
    SUCCESS: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-400', label: 'Success' },
    FAILED: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-400', label: 'Failed' },
    CANCELLED: { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-400', label: 'Cancelled' },
    ERROR: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-400', label: 'Error' }
  };

  const config = statusConfig[status] || statusConfig.DRAFT;

  return (
    <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

export default App;